import type {
  CordIdbRelayRequestMessage,
  CordIntrospectionEnvelope,
  CordIntrospectionIdbRequest,
} from "@cord/shared"
import type { QueryClient } from "@tanstack/react-query"
import {
  buildResolveMergeConflictClientMessageId,
  NEW_SYNC_WEBSOCKET_PAGE_BYTE_CAP,
  NEW_SYNC_WEBSOCKET_PAGE_ROW_CAP,
  RUNTIME_CHECKPOINT_MAX_BYTE_LENGTH,
  RUNTIME_CHECKPOINT_PROJECTION_VERSION_V1,
  RUNTIME_CHECKPOINT_VERSION_V1,
} from "@cord/shared"
import { executeBrowserStorageIntrospectionRequest } from "@/lib/browser-storage-introspection"
import {
  resolvePersistedValidatedResumeSource,
  type ThreadSyncRenderSource,
  type ThreadSyncResumeSource,
} from "@/lib/thread-sync-activation-contract"
import {
  applyControlPlaneSnapshotToActiveRuntime,
  advanceActiveRuntimeCursor,
  patchActiveRuntimeSnapshot,
} from "@/lib/thread-sync-active-runtime"
import {
  emptyControlPlaneState as buildEmptyControlPlaneState,
  normalizeActiveEditOperation,
  type ThreadControlPlaneState,
} from "@/lib/thread-sync-control-plane"
import {
  buildHandshakeFrame,
  markResumeConnectionClosed,
  resetResumeForColdReplay,
  prepareResumeForConnectionAttempt,
  resetResumeForDisconnect,
  restoreResumeFromPersistedValidatedSnapshot,
  type ThreadSyncResumeState,
} from "@/lib/thread-sync-resume-state"
import {
  type ThreadRuntimeMaterialization,
} from "@/lib/thread-sync-runtime-materialization"
import {
  normalizeQueuedSessionCommandNotice,
  normalizeQueuedMessageDraft,
  normalizeRuntimeActionOutcome,
  normalizeSessionActionNotice,
} from "@/lib/thread-sync-message-normalizers"
import {
  isDisplayOnlyRuntimeMaterialization,
  isSyncDerivedRuntimeMaterialization,
} from "@/lib/thread-sync-runtime-materialization"
import {
  applyBaselinePageToRuntime,
  applySyncEventsToRuntime,
  applySyncEventsToRuntimeInChunks,
  THREAD_SYNC_BULK_APPLY_CHUNK_EVENT_COUNT,
  type ThreadSyncRuntimeApplyResult,
} from "@/lib/thread-sync-runtime-apply"
import type {
  AgentRuntimeComputationState,
} from "@/lib/agent-runtime-sync-batch-computation"
import {
  evaluateThreadSyncReplayWorkerEligibility,
  evaluateThreadSyncDurableReplicaCoveragePolicy,
} from "@/lib/thread-sync-replay-worker-safety"
import {
  ACTIVE_THREAD_HTTP_PREFILL_MAX_PAGES,
  ACTIVE_THREAD_HTTP_PREFILL_MAX_TOTAL_BYTES,
  ACTIVE_THREAD_HTTP_PREFILL_MAX_WAIT_MS,
  EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_PAGES,
  EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_TOTAL_BYTES,
  EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_WAIT_MS,
  RUNTIME_CHECKPOINT_BOOTSTRAP_MAX_WAIT_MS,
  fetchThreadSyncBaselinePage,
  fetchThreadSyncBootstrapArtifact,
  fetchThreadSyncBootstrapArtifactBlob,
  fetchThreadSyncCatchupPage,
  type ThreadSyncHttpBaselinePageResponse,
  type ThreadSyncBootstrapArtifactResponse,
  type ThreadSyncHttpCatchupResponse,
  type ThreadSyncHttpPrefillMode,
  type ThreadSyncRuntimeCheckpointBlob,
} from "@/lib/thread-sync-http-prefill"
import {
  applyOptimisticRuntimeSeed as applyOptimisticRuntimeSeedToActiveStore,
  captureActiveRuntimeState as captureRuntimeCacheActiveState,
  clearRuntimeState as clearThreadSyncRuntimeState,
  createThreadSyncRuntimeState,
  invalidatePersistedReplicaState as invalidateRuntimeReplicaState,
  invalidatePersistedReplicaBySessionId,
  replaceDisplayOnlyRuntimeWithControlPlaneOnly as replaceDisplayOnlyRuntimeWithControlPlaneOnlyFromCache,
  resetRuntimeStateAndInvalidatePersistedReplica,
  resetRuntimeStore,
  restoreRuntimeState as restoreThreadSyncRuntimeState,
  syncActiveRuntimeStateToCache,
  type ThreadSyncRuntimeState,
} from "@/lib/thread-sync-runtime-cache"
import {
  advanceManagedRuntimeStateCursor,
} from "@/lib/thread-sync-inactive-replica-computation"
import {
  buildClaudeSnapshot,
  isCurrentClaudeDerivedProjection,
} from "@/lib/claude-raw-runtime"
import { SESSION_RUNTIME_ADAPTERS } from "@/lib/session-runtime-adapters"
import type { ManagedRuntimeState } from "@/lib/thread-sync-replica-runtime"
import {
  compareThreadSyncReplicaSummaries,
  buildThreadSyncReplicaSummary,
  type ThreadSyncReplicaSummary,
} from "@/lib/thread-sync-replica-arbitration"
import {
  buildThreadSyncReplicaProvenance,
} from "@/lib/thread-sync-replica-provenance"
import {
  readAndClearBrowserRuntimeReloadBreadcrumb,
} from "@/lib/browser-runtime-reload-breadcrumb"
import type {
  ThreadSyncBackgroundWarmSummary,
} from "@/lib/thread-sync-background-warm-protocol"
import {
  clearThreadSyncPersistedReplicaSummaryPreservingInvalidation,
  clearAllThreadSyncPersistedReplicaSummaries,
  getThreadSyncPersistedReplicaInvalidation,
  getThreadSyncPersistedReplicaSummary,
  upsertThreadSyncPersistedReplicaSummary,
} from "@/lib/thread-sync-replica-summary-store"
import {
  appendThreadReplicaDelta,
  clearThreadReplicaSession,
} from "@/lib/thread-replica-append"
import {
  importThreadReplicaRuntimeCheckpoint,
  saveThreadReplicaCheckpoint,
} from "@/lib/thread-replica-checkpoint-store"
import { hydrateThreadReplica } from "@/lib/thread-replica-hydrate"
import { advanceThreadReplicaRuntimeCursorMeta } from "@/lib/thread-replica-meta-store"
import { compactThreadReplicaRuntimeTailWindow } from "@/lib/thread-replica-runtime-tail-window-compaction"
import {
  THREAD_REPLICA_DEFAULT_SCOPE_KEY,
  EMPTY_THREAD_REPLICA_CURSOR,
  THREAD_REPLICA_EVENT_CONTRACT_VERSION,
  type ThreadReplicaHydrateResult,
  type ThreadReplicaMetaRecord,
  type ThreadReplicaBaselineEventInput,
  type ThreadReplicaRuntimeEventInput,
  type ThreadReplicaScope,
} from "@/lib/thread-replica-types"
import { getForegroundOwnershipTabId } from "@/lib/session-foreground-ownership"
import {
  appendSandboxProgressEntries,
  applyFileChangesSideEffects,
  applyFileTreeSideEffects,
  applyGitResultSideEffects,
  applyStartNewWorkGitCompletedSideEffects,
  applyMergePrResultSideEffects,
  applyPendingMessageCountSideEffects,
  applyPrFlowSnapshotSideEffects,
  applyPrStatusSideEffects,
  applyQuickPrCompletionSideEffects,
  clearOptimisticWorkRequestPendingSideEffects,
  applyResolveConflictSideChannelMessage,
  applySessionBranchUpdatedSideEffects,
  applyYoloFlowSideEffects,
  applyRejectedMessageToQueueState,
  computeYoloFlowUpdate,
  getLatestPendingQueueTimestamp,
  normalizeCodeServerStatusSideChannel,
  normalizePendingQueueRecords,
  normalizeSandboxProgressMessage,
  reconcileLocalOutboxWithServerPending,
  removeRenderedMessageFromQueueState,
} from "@/lib/thread-sync-side-channel-effects"
import { applyStatusPayloadToThreadState } from "@/lib/thread-sync-status-application"
import {
  applyAuthoritativeSessionTitleUpdate,
  normalizeSessionTitleUpdate,
  normalizeThreadActivityUpdate,
} from "@/lib/multiplex-thread-activity-support"
import {
  applyAuthoritativeSessionArchiveStateUpdate,
} from "@/lib/session-membership-cache"
import {
  applyTransportOptions,
  beginTransportConnectAttempt,
  beginTransportDisconnect,
  clearConnectingWatchdogTimer,
  clearKeepaliveTimer,
  clearReconnectTimer,
  clearTransportTimers,
  closeTransportSocket,
  computeReconnectDelayMs,
  createThreadSyncTransportState,
  hasOpenOrConnectingTransportSocket,
  incrementReconnectAttempts,
  isCurrentTransportConnection,
  recordTransportClose,
  recordTransportError,
  setTransportStatus,
  type ThreadSyncTransportState,
} from "@/lib/thread-sync-transport-lifecycle"
import {
  type LocalOutboxQueueRecord,
  type PendingQueueRecord,
} from "@/lib/session-queue-ui-state"
import { buildSessionStatusUrl } from "@/lib/session-status-url"
import {
  deriveRuntimeLifecycleAvailabilityObservedAt,
  normalizeConnectionFlowRuntimeLifecycleDebugSnapshot,
} from "@/lib/session-connection-flow"
import {
  buildThreadSyncViewState,
  type ThreadTranscriptFreshness,
  type ThreadSyncViewState,
} from "@/lib/thread-sync-view-state"
import {
  applyBaselinePageMetrics,
  planCatchupCompleteHandling,
  planCatchupPageHandling,
  planLiveSyncEventHandling,
  planSyncErrorHandling,
  planSyncHeartbeat,
  planSyncReadyHandling,
  shouldCaptureAuthoritativeRuntime,
} from "@/lib/thread-sync-sync-orchestration"
import { useAgentRuntime } from "@/stores/agent-runtime"
import {
  useThreadRuntime,
} from "@/stores/thread-runtime"
import { useRuntimeConversationActivity } from "@/stores/runtime-conversation-activity"
import { debugInfo, debugWarn } from "@/stores/debug-logs"
import {
  clearSessionSyncHealthEntry,
  upsertSessionSyncHealthEntry,
} from "@/stores/session-sync-health"
import {
  useSessionPrFlow,
} from "@/stores/session-pr-flow"
import type { PendingForkSnapshot } from "@/stores/pending-fork-snapshot"
import type {
  ChatGptOauthDebug,
  ChatSendResult,
  CodeServerStatus,
  DevServerStatus,
  MessageRejectionEvent,
  QueuedSessionCommandOutcomeNotice,
  PreviewEndpoint,
  RemoteWsDiagnostics,
  RuntimeProcessSnapshot,
  SandboxProgress,
  SandboxStatus,
  SessionActionOutcomeNotice,
  SessionAgentIdentity,
  WsStatus,
  YoloFlowStep,
} from "@/hooks/thread-sync-client-types"

export type { PrAdoptionControlPlaneSnapshot } from "@/lib/thread-sync-control-plane"

type SyncServerFrame =
  | {
      type: "sync.baseline.page"
      agentType: "claude_code"
      events: Array<{
        contractVersion?: number
        replayEventId?: string
        baselineSeq: number
        type: string
        payload: unknown
        timestamp: number
        originSessionId?: string
        originEventId?: number
      }>
      nextBaselineSeq: number
      hasMore: boolean
      count: number
      approxBytes: number
    }
  | {
      type: "sync.ready"
      acceptedCursor: number
      tailId: number
      historyContinuity: "contiguous" | "broken"
      replicationContinuity: "contiguous" | "broken"
      historyToken: string | null
    }
  | {
      type: "sync.catchup.page"
      events: Array<{
        contractVersion?: number
        replayEventId?: string
        id: number
        type: string
        payload: unknown
        timestamp: number
        conversationId?: string
        providerSessionId?: string
        childSessionId?: string
      }>
      effectiveAfter: number
      nextCursor: number
      hasMore: boolean
      count: number
      approxBytes: number
    }
  | {
      type: "sync.catchup.complete"
      finalCursor: number
    }
  | {
      type: "sync.event"
      event: {
        contractVersion?: number
        replayEventId?: string
        id: number
        type: string
        payload: unknown
        timestamp: number
        conversationId?: string
        providerSessionId?: string
        childSessionId?: string
      }
    }
  | {
      type: "sync.heartbeat"
      timestamp: number
    }
  | {
      type: "sync.error"
      code: string
      message: string
    }

type SyncCatchupPageFrame = Extract<SyncServerFrame, { type: "sync.catchup.page" }>

function buildThreadReplicaScope(sessionId: string): ThreadReplicaScope {
  return {
    scopeKey: THREAD_REPLICA_DEFAULT_SCOPE_KEY,
    sessionId,
  }
}

function buildThreadReplicaRuntimeReplayEventId(args: { id: number; replayEventId?: string }): string {
  return args.replayEventId?.trim() || `runtime:${Math.max(0, Math.floor(args.id))}`
}

function buildThreadReplicaBaselineReplayEventId(args: {
  baselineSeq: number
  replayEventId?: string
}): string {
  return args.replayEventId?.trim() || `baseline:${Math.max(0, Math.floor(args.baselineSeq))}`
}

function buildThreadReplicaRuntimeEventsFromTransportEvents(args: {
  events: SyncRuntimeEvent[]
}): ThreadReplicaRuntimeEventInput[] {
  // The durable event lane is shared across all runtime conversations on this
  // parent session; preserving the transport's `conversationId` on every row is
  // what lets hydration rebuild a per-conversation projection later without
  // rewriting the whole IDB layout as a second lane per tab.
  return args.events.map((event) => ({
    contractVersion:
      typeof event.contractVersion === "number" && Number.isFinite(event.contractVersion)
        ? Math.max(1, Math.floor(event.contractVersion))
        : THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    replayEventId: buildThreadReplicaRuntimeReplayEventId({
      id: event.id,
      replayEventId: event.replayEventId,
    }),
    id: Math.max(0, Math.floor(event.id)),
    type: event.type,
    payload: event.payload,
    timestamp: Math.max(0, Math.floor(event.timestamp)),
    childSessionId: event.childSessionId ?? null,
    conversationId: event.conversationId ?? null,
  }))
}

function buildThreadReplicaBaselineEventsFromTransportEvents(args: {
  events: Array<{
    contractVersion?: number
    replayEventId?: string
    baselineSeq: number
    type: string
    payload: unknown
    timestamp: number
    originSessionId?: string
    originEventId?: number
  }>
}): ThreadReplicaBaselineEventInput[] {
  return args.events.map((event) => ({
    contractVersion:
      typeof event.contractVersion === "number" && Number.isFinite(event.contractVersion)
        ? Math.max(1, Math.floor(event.contractVersion))
        : THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    replayEventId: buildThreadReplicaBaselineReplayEventId({
      baselineSeq: event.baselineSeq,
      replayEventId: event.replayEventId,
    }),
    baselineSeq: Math.max(0, Math.floor(event.baselineSeq)),
    type: event.type,
    payload: event.payload,
    timestamp: Math.max(0, Math.floor(event.timestamp)),
    originSessionId: event.originSessionId ?? null,
    originEventId:
      typeof event.originEventId === "number" && Number.isFinite(event.originEventId)
        ? Math.max(0, Math.floor(event.originEventId))
        : null,
  }))
}

type ThreadSyncManagerConnectOptions = {
  connectEnabled?: boolean
  initialBranchSwitchInProgress?: boolean
  viewerActivityEnabled?: boolean
  readOnlyTransport?: boolean
  activeConversationId?: string | null
}

export type ThreadSyncManagerSwitchOptions = {
  optimisticRuntimeSeed?: PendingForkSnapshot | null
  activeConversationId?: string | null
}

type ThreadSyncQueueState = {
  serverPendingMessages: PendingQueueRecord[]
  serverPendingSessionActions: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  deferredPreHandshakeMessages: Array<{ type: string; [key: string]: unknown }>
  pendingLocalDispatches: Map<
    string,
    { message: { type: string; [key: string]: unknown }; lastAttemptedConnectionVersion: number | null }
  >
  serverOrchestratedLocalOutboxClientMessageIds: Map<string, number>
  lastMessageRejection: MessageRejectionEvent | null
  cancelledClientMessageIds: Map<string, number>
  recentlyServerObservedClientMessageIds: Map<string, number>
  recentlyRenderedClientMessageIds: Map<string, number>
  pendingMessageClientMessageIdsByMessageId: Map<number, { clientMessageId: string; expiresAt: number }>
  renderedLocalOutboxCleanupTimer: ReturnType<typeof setTimeout> | null
}

type ThreadSyncEffectsState = {
  sessionActionNotice: SessionActionOutcomeNotice | null
  queuedSessionCommandNotice: QueuedSessionCommandOutcomeNotice | null
  codeServerStatus: CodeServerStatus
  codeServerError: string | null
}

type ThreadSyncCatchupBufferState = {
  events: SyncCatchupPageFrame["events"]
  effectiveAfter: number | null
  nextCursor: number | null
  hasMore: boolean
  count: number
  approxBytes: number
  flushTimer: ReturnType<typeof setTimeout> | null
  flushPromise: Promise<"applied" | "noop" | "error"> | null
  deferredCallbacks: Array<() => void>
}

type ThreadSyncLiveEventBufferState = {
  events: SyncRuntimeEvent[]
  flushAnimationFrameId: number | null
  flushTimer: ReturnType<typeof setTimeout> | null
}

type ThreadSyncHttpPrefillState = {
  mode: ThreadSyncHttpPrefillMode | null
  phase: "runtime_checkpoint" | "catchup_prefill" | null
  connectionVersion: number | null
  status: "idle" | "inflight" | "applied" | "skipped" | "failed" | "timed_out"
  maxPages: number
  maxTotalBytes: number
  maxWaitMs: number
  targetCursor: number | null
  pageCount: number
  totalBytes: number
  lastError: string | null
  abortController: AbortController | null
  releaseTimer: ReturnType<typeof setTimeout> | null
}

type ThreadSyncEntryConnectionRole =
  | "inactive"
  | "foreground_active"
  | "handoff_previous"

type ThreadSyncBackgroundWarmPolicyEntry = {
  isHot: boolean
  lastActivatedAt: number | null
  connectionRole: ThreadSyncEntryConnectionRole
  hasOpenOrConnectingTransport: boolean
}

type ThreadSyncBackgroundWarmControlSnapshot = {
  suspended: boolean
  activeSessionId: string | null
  suspensionReason:
    | "recent_active_session_switch"
    | "foreground_stress"
    | "active_http_prefill_inflight"
    | "active_runtime_too_large"
    | "active_catchup_page_too_large"
    | null
  isCatchingUp: boolean
  httpPrefillStatus: ThreadSyncHttpPrefillState["status"]
  httpPrefillPageCount: number
  httpPrefillTotalBytes: number
  lastCatchupPageCount: number
  lastCatchupPageBytes: number
  snapshotTimelineCount: number
  openCodeMessageCount: number
  claudeRawEventCount: number
}

type ThreadSyncReplayWorkerEligibilityCacheEntry = {
  sessionId: string
  stateVersion: number
  eligibility: ReturnType<typeof evaluateThreadSyncReplayWorkerEligibility>
}

type ThreadSyncDurableReplicaHydrationState = {
  generation: number
  inFlight: boolean
  shouldDeferColdHandshake: boolean
}

type ThreadSyncCheckpointSaveSourceKind = "foreground_ws" | "foreground_http_prefill"

type ThreadSyncCheckpointSaveFingerprint = {
  expectedEpoch: number
  cursor: number
  conversationId: string
  lastBaselineSequence: number | null
  eventContractVersion: number | null
  projectorVersion: number | null
}

type ThreadSyncCheckpointSaveRequest = ThreadSyncCheckpointSaveFingerprint & {
  entry: ThreadSyncEntry
  sourceKind: ThreadSyncCheckpointSaveSourceKind
  historyToken: string | null
  runtimeState: NonNullable<ThreadSyncRuntimeState["cachedState"]>
  createdAtMs: number
}

type ThreadSyncCheckpointSaveState = {
  inFlight: boolean
  pending: ThreadSyncCheckpointSaveRequest | null
  lastSuccessfulFingerprint: string | null
}

type ThreadSyncDurableReplicaWriteQueueState = {
  tail: Promise<void> | null
}

type ThreadSyncViewStateInvalidationScope =
  | {
      type: "all"
    }
  | {
      type: "sessions"
      sessionIds: Set<string>
      clearEmptyViewState: boolean
    }

type ThreadSyncEntry = {
  sessionId: string
  lastActivatedAt: number
  backgroundReplicaInvalidatedAtMs: number | null
  durableReplicaEpoch: number | null
  sessionBranch: string | null
  baseBranch: string | null
  connectionRole: ThreadSyncEntryConnectionRole
  transport: ThreadSyncTransportState
  durableReplicaHydration: ThreadSyncDurableReplicaHydrationState
  resume: ThreadSyncResumeState
  runtime: ThreadSyncRuntimeState
  activeConversationId: string
  // Bumped whenever the visible runtime conversation changes so asynchronous
  // default-checkpoint hydration and chunked catchup cannot write a stale
  // conversation's projection into the active global runtime store.
  activeConversationRevision: number
  runtimeEventsByConversationId: Map<string, SyncRuntimeEvent[]>
  controlPlane: ThreadControlPlaneState
  queue: ThreadSyncQueueState
  effects: ThreadSyncEffectsState
  catchupBuffer: ThreadSyncCatchupBufferState
  liveEventBuffer: ThreadSyncLiveEventBufferState
  httpPrefill: ThreadSyncHttpPrefillState
  checkpointSave: ThreadSyncCheckpointSaveState
  durableReplicaWriteQueue: ThreadSyncDurableReplicaWriteQueueState
  lastHttpPrefillCompletedAt: number | null
  lastSessionSurfaceRefreshAt: number | null
  sessionSurfaceRefreshCooldownTimer: ReturnType<typeof setTimeout> | null
}

const EMPTY_PENDING_MESSAGES: PendingQueueRecord[] = []
const EMPTY_PENDING_SESSION_ACTIONS: PendingQueueRecord[] = []
const EMPTY_LOCAL_OUTBOX: LocalOutboxQueueRecord[] = []
const DEFAULT_RUNTIME_CONVERSATION_ID = "default"

type SyncRuntimeEvent = {
  contractVersion?: number
  replayEventId?: string
  id: number
  type: string
  payload: unknown
  timestamp: number
  conversationId?: string
  providerSessionId?: string
  childSessionId?: string
}

const FRAME_BATCHABLE_RUNTIME_EVENT_TYPES = new Set([
  "message.updated",
  "message.part.updated",
  "message.part.delta",
  "sdk_message",
  "session_update",
  "todo_update",
  "task_update",
  "image_generated",
])

const URGENT_RUNTIME_EVENT_TYPES = new Set([
  "aborted",
  "permission.asked",
  "permission.replied",
  "permission_request",
  "permission_reply",
  "plan_request",
  "plan_reply",
  "question.asked",
  "question.replied",
  "question.rejected",
  "question_request",
  "question_reply",
  "question_reject",
  "run_completed",
  "run_error",
  "run_started",
  "session.error",
  "session.status",
  "session_action",
])

function isFrameBatchableRuntimeEvent(event: SyncRuntimeEvent): boolean {
  return FRAME_BATCHABLE_RUNTIME_EVENT_TYPES.has(event.type) && !URGENT_RUNTIME_EVENT_TYPES.has(event.type)
}

function normalizeRuntimeConversationId(value: unknown): string {
  if (typeof value !== "string") return DEFAULT_RUNTIME_CONVERSATION_ID
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : DEFAULT_RUNTIME_CONVERSATION_ID
}

function getStatusPayloadConversationId(payload: unknown): string | null {
  const row = asRecord(payload)
  const scopedConversationId = asString(row?.statusConversationId)
  return scopedConversationId ? normalizeRuntimeConversationId(scopedConversationId) : null
}
// Keep cancel tombstones for 30s so stale in-flight status frames cannot
// resurrect a just-cancelled queued message while the server catches up. If
// backend reconciliation regularly exceeds this window we should increase it
// with measurements rather than keeping tombstones indefinitely.
const CANCELLED_MESSAGE_TOMBSTONE_TTL_MS = 30_000
// Keep recently server-observed client message ids for 15s so transient stale
// pending snapshots do not momentarily drop optimistic queue rows that were
// just acknowledged by the server. This matches the rendered-handoff window
// below: runtime user-message echoes can lag backend queue finalization by more
// than one paint when the sandbox is resuming or the sync stream is catching up.
// If final runtime echoes routinely exceed this, add telemetry before increasing
// it because stale queued rows would remain visible for longer on missed events.
const RECENTLY_SERVER_OBSERVED_CLIENT_MESSAGE_ID_TTL_MS = 15_000
// Keep a rendered local outbox mirror around for 15s after `message_rendered` so
// React has a stable row while the pending queue deletion and runtime timeline
// echo arrive in separate websocket/control-plane frames. The timeline linkage
// suppresses the mirror as soon as the canonical user bubble is present; this
// timer is only a safety valve for missed or severely delayed runtime echoes.
const RENDERED_LOCAL_OUTBOX_HANDOFF_TTL_MS = 15_000
// Keep local-only echoes for server-orchestrated sends while the HTTP action
// performs authorization and the DO emits a durable pending/rendered message.
// Five minutes intentionally covers a slow claim-confirmation prompt without
// letting a missed server response leave stale optimistic bubbles indefinitely.
const SERVER_ORCHESTRATED_LOCAL_OUTBOX_TTL_MS = 5 * 60_000
const KEEPALIVE_INTERVAL_MS = 20_000
const RECONNECT_MAX_DELAY_MS = 30_000
/**
 * Browsers should either open or close a websocket quickly, but we have seen
 * foreground session sockets remain in CONNECTING long enough that the UI is
 * stuck on "Reconnecting..." until a thread switch tears the socket down. 15s
 * is longer than the 10s fresh-thread notice delay and shorter than the 30s
 * max reconnect backoff, so healthy slow handshakes get a grace window while a
 * wedged browser transport is reset before the user has to switch threads.
 */
export const THREAD_SYNC_CONNECTING_WATCHDOG_TIMEOUT_MS = 15_000
/**
 * Keep roughly a few screens of recently-touched threads in memory so users who
 * bounce among 6-7 active threads still retain a broader same-tab hot set plus
 * a nearby backlog of recently visited threads. If low-memory devices show
 * pressure, reduce this with measurements instead of tightening eviction by
 * instinct and regressing thread-switch locality.
 */
const THREAD_SYNC_ENTRY_LRU_MAX = 32
/**
 * Keep a small recent inactive hot set eligible for full artifact handoff from
 * the background worker. Eight threads comfortably covers typical rapid
 * switchback usage without structured-cloning large snapshots for the entire
 * 32-entry inactive cache.
 */
const THREAD_SYNC_BACKGROUND_WARM_HOT_SESSION_LRU_MAX = 8
/**
 * Allow up to 5 seconds of overlap between the previous and next thread so the
 * new foreground socket can establish transport without paying a strict
 * close-then-open gap. If overlap still leaks too long in practice, shorten
 * this bound before adding more handoff states or server-side complexity.
 */
const HANDOFF_OVERLAP_MAX_MS = 5_000
/**
 * Coalesce backlog pages for roughly one frame so adjacent websocket catchup
 * bursts become one runtime/render transaction without adding a human-noticeable
 * delay. If this still feels jittery we should tune batching thresholds before
 * increasing the delay further.
 */
const CATCHUP_BUFFER_FLUSH_DELAY_MS = 16
/**
 * Flush once roughly four websocket pages worth of backlog rows have been
 * buffered. This reduces repeated full-runtime replay work while keeping a
 * single browser apply bounded enough that large completed threads do not feel
 * like one giant all-or-nothing stall.
 */
const CATCHUP_BUFFER_EVENT_ROW_SOFT_CAP = NEW_SYNC_WEBSOCKET_PAGE_ROW_CAP * 4
/**
 * Mirror the row soft-cap with a byte soft-cap derived from the websocket page
 * budget. This prevents a few unusually large pages from sitting in the buffer
 * for too long or flushing as an overly large single main-thread burst.
 */
const CATCHUP_BUFFER_APPROX_BYTES_SOFT_CAP = NEW_SYNC_WEBSOCKET_PAGE_BYTE_CAP * 4
/**
 * Session-surface queries are already invalidated by websocket side-channel and
 * file/git bridge events. Suppressing repeated "prefill complete" nudges for a
 * short window avoids re-fetch storms during rapid thread switches while still
 * allowing a later revisit to refresh if no live updates arrived.
 */
const PREFILL_SESSION_SURFACE_FETCH_COOLDOWN_MS = 15_000
/**
 * Once foreground websocket catchup is already processing at least one full page
 * worth of backlog, suspend optional background warming so the browser focuses
 * its memory and GC budget on the visible thread instead of hidden inactive
 * replicas.
 */
const BACKGROUND_WARM_SUSPEND_ACTIVE_CATCHUP_PAGE_ROW_THRESHOLD =
  NEW_SYNC_WEBSOCKET_PAGE_ROW_CAP
/**
 * Mirror the row threshold with the websocket page byte budget. If the active
 * thread is already ingesting a page this large, pausing background warming is a
 * safer trade than letting hidden transcript work compete for the same tab.
 */
const BACKGROUND_WARM_SUSPEND_ACTIVE_CATCHUP_PAGE_BYTE_THRESHOLD =
  NEW_SYNC_WEBSOCKET_PAGE_BYTE_CAP

/**
 * Fresh thread switches are the exact window where the foreground route mounts,
 * scroll restoration, durable hydration, and preview teardown all compete for
 * the same renderer memory budget. Keep optional background warming fully
 * paused for two seconds after each active-session change; this covers the
 * trace-observed interaction burst without making recently viewed inactive
 * threads cold for long. If users commonly switch again before two seconds,
 * each switch extends the pause and avoids stacking hidden IDB work on top of
 * the visible thread.
 */
const BACKGROUND_WARM_ACTIVE_SWITCH_SUSPEND_MS = 2_000

/**
 * Foreground catchup/prefill work can stay expensive after the initial route
 * switch cooldown expires, especially for empty-prefix large-thread startup.
 * Four seconds covers the current 2.5s empty-prefix HTTP prefill window plus a
 * short websocket/catchup apply tail without keeping background warming paused
 * long enough to make ordinary inactive switchbacks cold.
 */
const BACKGROUND_WARM_FOREGROUND_STRESS_SUSPEND_MS = 4_000

/**
 * Durable replica hydration is an optional fast-path before the cold websocket
 * handshake. It must never be allowed to hold the visible thread on the loading
 * state indefinitely: Chrome can occasionally leave IndexedDB requests stuck
 * until the browser process restarts. Three seconds is longer than the 2.5s
 * empty-prefix HTTP bootstrap window, so healthy local restores still win, but
 * a wedged browser store falls back to the authoritative socket path quickly.
 */
const DURABLE_REPLICA_HYDRATION_TIMEOUT_MS = 3_000

/**
 * Visible live-sync updates should ideally notify at most once per paint so a
 * burst of adjacent websocket frames does not rebuild every thread-sync view on
 * each individual event. When requestAnimationFrame is unavailable (tests, some
 * non-window contexts, or hidden tabs), fall back to a zero-delay timer so the
 * external store still flushes promptly without waiting for a paused frame loop.
 */
const THREAD_SYNC_DEFERRED_EMIT_FALLBACK_DELAY_MS = 0
/**
 * If we discover that a newer trusted persisted replica cannot actually restore
 * as authoritative, repair writes must sort strictly after that offending
 * record. One millisecond is enough because the underlying write clock already
 * guarantees per-session monotonic ordering beyond the requested timestamp.
 */
const TRUSTED_REPLICA_REPAIR_MIN_CLOCK_STEP_MS = 1

function isImmediatelyReusableCachedRuntimeMaterialization(
  materialization: ThreadRuntimeMaterialization,
): boolean {
  // Only fully-authoritative cached runtimes are safe to surface before a fresh
  // durable hydrate or sync.ready validation arrives. Partial catchup restores
  // can have a correct cursor while still missing transcript sections.
  return materialization === "authoritative"
}

function shouldShowForegroundActivationLoading(args: {
  connectionRole: ThreadSyncEntryConnectionRole
  connectEnabled: boolean
  syncReadyReceived: boolean
  transportLoading: boolean
}): boolean {
  return (
    args.connectionRole === "foreground_active" &&
    args.connectEnabled &&
    !args.syncReadyReceived &&
    !args.transportLoading
  )
}

function canResumeFromValidatedPrefixMaterialization(
  materialization: ThreadRuntimeMaterialization,
): boolean {
  return materialization === "authoritative"
}

function canResumeHandshakeFromRuntimePrefix(args: {
  entry: ThreadSyncEntry
  connectionVersion: number
}): boolean {
  const { entry } = args
  if (entry.resume.cursor <= 0) {
    return false
  }

  if (entry.runtime.resumeSource === "runtime_checkpoint") {
    return entry.runtime.materialization === "authoritative"
  }

  if (entry.resume.historyToken === null) return false

  if (canResumeFromValidatedPrefixMaterialization(entry.runtime.materialization)) {
    return true
  }

  return (
    entry.runtime.materialization === "authoritative_partial" &&
    entry.runtime.connectionVersion === args.connectionVersion &&
    entry.runtime.replicaProvenance?.sourceKind === "foreground_http_prefill"
  )
}

function withDurableReplicaHydrationTimeout(args: {
  promise: Promise<ThreadReplicaHydrateResult>
  sessionId: string
  managerInstanceId: number
  generation: number
}): Promise<ThreadReplicaHydrateResult> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const timeoutPromise = new Promise<ThreadReplicaHydrateResult>((resolve) => {
    timeoutId = setTimeout(() => {
      debugWarn("sync.new", "Durable thread replica hydration timed out; falling back to cold replay", {
        sessionId: args.sessionId,
        managerInstanceId: args.managerInstanceId,
        generation: args.generation,
        timeoutMs: DURABLE_REPLICA_HYDRATION_TIMEOUT_MS,
      })
      resolve({ status: "missing", reason: "storage_unavailable" })
    }, DURABLE_REPLICA_HYDRATION_TIMEOUT_MS)
  })

  return Promise.race([args.promise, timeoutPromise]).finally(() => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
  })
}

function shouldPreserveRuntimeDuringColdReplayCursorRegression(args: {
  acceptedCursor: number
  requestedAfter: number
  previousHistoryToken: string | null
  hasCursorRegression: boolean
  hasHistoryContinuityBroken: boolean
  hasHistoryTokenMismatch: boolean
  replicationContinuity: "contiguous" | "broken"
}): boolean {
  return (
    args.hasCursorRegression &&
    !args.hasHistoryContinuityBroken &&
    !args.hasHistoryTokenMismatch &&
    args.replicationContinuity === "contiguous" &&
    args.acceptedCursor <= EMPTY_THREAD_REPLICA_CURSOR &&
    args.requestedAfter <= EMPTY_THREAD_REPLICA_CURSOR &&
    args.previousHistoryToken === null
  )
}

function shouldRuntimeCheckpointReplaceCurrentReplica(args: {
  current: ThreadSyncReplicaSummary | null
  incoming: ThreadSyncReplicaSummary
}): boolean {
  const { current, incoming } = args
  if (current === null) return true

  const currentCursor = current.cursor ?? -1
  const incomingCursor = incoming.cursor ?? -1
  if (
    current.quality === "continuity_validated" &&
    currentCursor >= incomingCursor
  ) {
    return false
  }

  if (
    current.quality !== "continuity_validated" &&
    incoming.quality === "continuity_validated" &&
    incomingCursor >= currentCursor
  ) {
    return true
  }

  return compareThreadSyncReplicaSummaries({ current, incoming }) === "incoming"
}

function createThreadSyncCatchupBufferState(): ThreadSyncCatchupBufferState {
  return {
    events: [],
    effectiveAfter: null,
    nextCursor: null,
    hasMore: false,
    count: 0,
    approxBytes: 0,
    flushTimer: null,
    flushPromise: null,
    deferredCallbacks: [],
  }
}

function createThreadSyncLiveEventBufferState(): ThreadSyncLiveEventBufferState {
  return {
    events: [],
    flushAnimationFrameId: null,
    flushTimer: null,
  }
}

function createThreadSyncHttpPrefillState(): ThreadSyncHttpPrefillState {
  return {
    mode: null,
    phase: null,
    connectionVersion: null,
    status: "idle",
    maxPages: 0,
    maxTotalBytes: 0,
    maxWaitMs: 0,
    targetCursor: null,
    pageCount: 0,
    totalBytes: 0,
    lastError: null,
    abortController: null,
    releaseTimer: null,
  }
}

function createThreadSyncDurableReplicaHydrationState(): ThreadSyncDurableReplicaHydrationState {
  return {
    generation: 0,
    inFlight: false,
    shouldDeferColdHandshake: false,
  }
}

function createThreadSyncDurableReplicaWriteQueueState(): ThreadSyncDurableReplicaWriteQueueState {
  return {
    tail: null,
  }
}

function hasValidatedDurableReplicaSummary(summary: ThreadSyncBackgroundWarmSummary | null): boolean {
  // Only delay a cold transport handshake when the cheap mirrored metadata says
  // a validated durable prefix exists. Hydration itself is asynchronous; using
  // this small summary avoids broadly delaying ordinary cold loads, while still
  // protecting long-thread switchbacks that have a known resumable local cursor.
  return (
    summary?.storageBackend === "durable_replica" &&
    typeof summary.cursor === "number" &&
    Number.isFinite(summary.cursor) &&
    summary.cursor > 0 &&
    typeof summary.historyToken === "string" &&
    summary.historyToken.trim().length > 0 &&
    summary.quality === "continuity_validated"
  )
}

function clearSessionSurfaceRefreshCooldownTimer(entry: ThreadSyncEntry): void {
  if (entry.sessionSurfaceRefreshCooldownTimer === null) {
    return
  }

  clearTimeout(entry.sessionSurfaceRefreshCooldownTimer)
  entry.sessionSurfaceRefreshCooldownTimer = null
}

function shouldRefreshSessionSurfaceAfterPrefill(args: {
  lastHttpPrefillCompletedAt: number | null
  lastSessionSurfaceRefreshAt: number | null
  nowMs: number
}): boolean {
  if (args.lastHttpPrefillCompletedAt === null) {
    return false
  }

  if (args.lastSessionSurfaceRefreshAt === null) {
    return true
  }

  if (args.lastHttpPrefillCompletedAt <= args.lastSessionSurfaceRefreshAt) {
    return false
  }

  return args.nowMs - args.lastSessionSurfaceRefreshAt >= PREFILL_SESSION_SURFACE_FETCH_COOLDOWN_MS
}

function buildCheckpointSaveFingerprintKey(
  fingerprint: ThreadSyncCheckpointSaveFingerprint,
): string {
  return [
    fingerprint.expectedEpoch,
    fingerprint.cursor,
    normalizeRuntimeConversationId(fingerprint.conversationId),
    fingerprint.lastBaselineSequence ?? "baseline_unknown",
    fingerprint.eventContractVersion ?? "contract_unknown",
    fingerprint.projectorVersion ?? "projector_unknown",
  ].join(":")
}

function now(): number {
  return Date.now()
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  return Math.floor(value)
}

function normalizeSyncContinuity(
  value: unknown,
  fallback: "contiguous" | "broken" = "contiguous",
): "contiguous" | "broken" {
  return value === "broken" || value === "contiguous" ? value : fallback
}

function normalizeWsUrl(url: string): string {
  return url.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://")
}

function buildRuntimeStateDebugSummary(args: {
  runtime: ThreadSyncRuntimeState
}): Record<string, unknown> {
  const snapshot = args.runtime.cachedState?.snapshot ?? null

  return {
    runtimeMaterialization: args.runtime.materialization,
    renderSource: args.runtime.renderSource,
    resumeSource: args.runtime.resumeSource,
    runtimeConnectionVersion: args.runtime.connectionVersion,
    continuityValidatedConnectionVersion: args.runtime.continuityValidatedConnectionVersion,
    cachedLastEventId: args.runtime.cachedState?.lastEventId ?? null,
    hasSnapshot: snapshot !== null,
    timelineCount: snapshot?.timeline.length ?? 0,
    pendingInteractionCount: snapshot?.pendingInteractions.length ?? 0,
    pendingMessageCount: snapshot?.pendingMessages.length ?? 0,
    conversationEpoch: snapshot?.conversationEpoch ?? null,
    providerEventId: snapshot?.cursorState.providerEventId ?? null,
  }
}

function buildActiveRuntimeComputationState(args: {
  store: Pick<
    ReturnType<typeof useAgentRuntime.getState>,
    | "agentType"
    | "snapshot"
    | "lastAppliedTimestamp"
    | "lastEventId"
    | "rawOpenCodeProjection"
    | "rawClaudeProjection"
    | "rawCursorProjection"
  >
}): AgentRuntimeComputationState {
  const { store } = args
  return {
    agentType: store.agentType,
    snapshot: store.snapshot,
    lastAppliedTimestamp: store.lastAppliedTimestamp,
    lastEventId: store.lastEventId,
    rawOpenCodeProjection: store.rawOpenCodeProjection,
    rawClaudeProjection: store.rawClaudeProjection,
    rawCursorProjection: store.rawCursorProjection,
  }
}

function buildDefaultBackgroundWarmControlSnapshot(args: {
  activeSessionId: string | null
}): ThreadSyncBackgroundWarmControlSnapshot {
  return {
    suspended: false,
    activeSessionId: args.activeSessionId,
    suspensionReason: null,
    isCatchingUp: false,
    httpPrefillStatus: "idle",
    httpPrefillPageCount: 0,
    httpPrefillTotalBytes: 0,
    lastCatchupPageCount: 0,
    lastCatchupPageBytes: 0,
    snapshotTimelineCount: 0,
    openCodeMessageCount: 0,
    claudeRawEventCount: 0,
  }
}

function buildSessionWsUrl(sessionId: string): string {
  const trimmedBaseUrl = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
  const baseUrl = trimmedBaseUrl || window.location.origin
  return `${baseUrl}/api/sessions/${encodeURIComponent(sessionId)}/ws`
}

function createEntry(
  sessionId: string,
  options?: ThreadSyncManagerConnectOptions,
  optimisticRuntimeSeed?: PendingForkSnapshot | null,
): ThreadSyncEntry {
  return {
    sessionId,
    lastActivatedAt: now(),
    backgroundReplicaInvalidatedAtMs: null,
    durableReplicaEpoch: null,
    connectionRole: "inactive",
    transport: createThreadSyncTransportState({
      connectEnabled: options?.connectEnabled,
      viewerActivityEnabled: options?.viewerActivityEnabled,
      readOnlyTransport: options?.readOnlyTransport,
      nowMs: now(),
    }),
    durableReplicaHydration: createThreadSyncDurableReplicaHydrationState(),
    resume: {
      cursor: 0,
      tailCursor: 0,
      requestedAfter: 0,
      historyToken: null,
      isCatchingUp: false,
      syncReadyReceived: false,
      lastPageCount: 0,
      lastPageBytes: 0,
    },
    sessionBranch: null,
    baseBranch: null,
    runtime: createThreadSyncRuntimeState(optimisticRuntimeSeed ?? null),
    activeConversationId: DEFAULT_RUNTIME_CONVERSATION_ID,
    activeConversationRevision: 0,
    runtimeEventsByConversationId: new Map(),
    controlPlane: buildEmptyControlPlaneState(options?.initialBranchSwitchInProgress === true),
    queue: {
      serverPendingMessages: EMPTY_PENDING_MESSAGES,
      serverPendingSessionActions: EMPTY_PENDING_SESSION_ACTIONS,
      localOutbox: EMPTY_LOCAL_OUTBOX,
      deferredPreHandshakeMessages: [],
      pendingLocalDispatches: new Map(),
      serverOrchestratedLocalOutboxClientMessageIds: new Map(),
      lastMessageRejection: null,
      cancelledClientMessageIds: new Map(),
      recentlyServerObservedClientMessageIds: new Map(),
      recentlyRenderedClientMessageIds: new Map(),
      pendingMessageClientMessageIdsByMessageId: new Map(),
      renderedLocalOutboxCleanupTimer: null,
    },
    effects: {
      sessionActionNotice: null,
      queuedSessionCommandNotice: null,
      codeServerStatus: "idle",
      codeServerError: null,
    },
    catchupBuffer: createThreadSyncCatchupBufferState(),
    liveEventBuffer: createThreadSyncLiveEventBufferState(),
    httpPrefill: createThreadSyncHttpPrefillState(),
    checkpointSave: {
      inFlight: false,
      pending: null,
      lastSuccessfulFingerprint: null,
    },
    durableReplicaWriteQueue: createThreadSyncDurableReplicaWriteQueueState(),
    lastHttpPrefillCompletedAt: null,
    lastSessionSurfaceRefreshAt: null,
    sessionSurfaceRefreshCooldownTimer: null,
  }
}

export class ThreadSyncManager {
  private readonly instanceId: number
  private readonly entries = new Map<string, ThreadSyncEntry>()
  private readonly listeners = new Set<() => void>()
  private readonly viewStateCache = new Map<string, ThreadSyncViewState>()
  private readonly clearingSessionIds = new Set<string>()
  private readonly sessionStateClearPromises = new Map<string, Promise<void>>()
  private emptyViewState: ThreadSyncViewState | null = null
  private destroyed = false
  private activeThreadId: string | null = null
  private handoffPreviousThreadId: string | null = null
  private handoffCloseTimer: ReturnType<typeof setTimeout> | null = null
  private backgroundWarmActiveSwitchSuspendedUntilMs = 0
  private backgroundWarmActiveSwitchResumeTimer: ReturnType<typeof setTimeout> | null = null
  private backgroundWarmForegroundStressSuspendedUntilMs = 0
  private backgroundWarmForegroundStressResumeTimer: ReturnType<typeof setTimeout> | null = null
  private deferredEmitAnimationFrameId: number | null = null
  private deferredEmitTimer: ReturnType<typeof setTimeout> | null = null
  private deferredEmitInvalidationScope: ThreadSyncViewStateInvalidationScope | null = null
  private activeReplayWorkerEligibilityCache: ThreadSyncReplayWorkerEligibilityCacheEntry | null = null
  private queryClient: QueryClient | null = null

  constructor(instanceId?: number) {
    this.instanceId = instanceId ?? 0
  }

  setQueryClient(queryClient: QueryClient | null): void {
    this.queryClient = queryClient
  }

  private enqueueDurableReplicaWrite<T>(entry: ThreadSyncEntry, operation: () => Promise<T>): Promise<T> {
    const runOperation = () => {
      try {
        return operation()
      } catch (error) {
        return Promise.reject(error)
      }
    }
    const previousWrite = entry.durableReplicaWriteQueue.tail
    const queuedWrite = previousWrite
      ? previousWrite.catch(() => undefined).then(runOperation)
      : runOperation()
    const queueTail = queuedWrite.then(
      () => undefined,
      (error) => {
        debugWarn("sync.new", "Durable thread replica queued write failed", {
          sessionId: entry.sessionId,
          managerInstanceId: this.instanceId,
          error: error instanceof Error ? error.message : String(error),
        })
      },
    )
    entry.durableReplicaWriteQueue.tail = queueTail
    queueTail.then(() => {
      if (entry.durableReplicaWriteQueue.tail === queueTail) {
        entry.durableReplicaWriteQueue.tail = null
      }
    })
    return queuedWrite
  }

  destroy(): void {
    this.destroyed = true
    this.activeReplayWorkerEligibilityCache = null
    this.clearDeferredEmitSchedule()
    const activeThreadId = this.activeThreadId
    this.activeThreadId = null
    this.clearHandoffCloseTimer()
    this.clearBackgroundWarmActiveSwitchResumeTimer()
    this.clearBackgroundWarmForegroundStressResumeTimer()
    this.backgroundWarmActiveSwitchSuspendedUntilMs = 0
    this.backgroundWarmForegroundStressSuspendedUntilMs = 0
    this.handoffPreviousThreadId = null
    for (const [sessionId, entry] of this.entries) {
      this.resetHttpPrefillState(entry)
      this.resetCatchupBuffer(entry)
      this.resetLiveEventBuffer(entry)
      this.clearRenderedLocalOutboxCleanupTimer(entry)
      clearSessionSurfaceRefreshCooldownTimer(entry)
      clearTransportTimers(entry.transport)
      closeTransportSocket(entry.transport, "reconnect:destroy")
      clearSessionSyncHealthEntry(sessionId)
      useSessionPrFlow.getState().clearSnapshot(sessionId)
      this.clearEntryRuntimeState(entry)
    }
    this.entries.clear()
    this.clearingSessionIds.clear()
    this.sessionStateClearPromises.clear()
    clearAllThreadSyncPersistedReplicaSummaries()
    this.listeners.clear()
    if (activeThreadId) {
      resetRuntimeStore()
    }
    this.emit()
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getActiveThreadId(): string | null {
    return this.activeThreadId
  }

  switchTo(sessionId: string | null, options?: ThreadSyncManagerSwitchOptions): void {
    if (!sessionId) {
      const previousActiveId = this.activeThreadId
      this.closePendingHandoffThread({ closeReason: "reconnect:handoff_abandoned" })
      if (previousActiveId) {
        this.disconnect(previousActiveId, { clearRuntime: true, removeEntry: false, shouldReconnect: false })
      } else {
        this.activeThreadId = null
        resetRuntimeStore()
      }
      this.emit()
      return
    }

    const previousActiveId = this.activeThreadId
    const hadExistingEntry = this.entries.has(sessionId)
    this.closePendingHandoffThread({
      retainSessionId: sessionId,
      closeReason: "reconnect:handoff_replaced",
    })
    if (previousActiveId && previousActiveId !== sessionId) {
      const previousEntry = this.entries.get(previousActiveId)
      if (previousEntry) {
        this.captureActiveRuntimeState(previousEntry)
        if (hasOpenOrConnectingTransportSocket(previousEntry.transport)) {
          this.prepareEntryForHandoff(previousEntry)
          this.startPendingHandoff(previousActiveId)
        } else {
          previousEntry.connectionRole = "inactive"
          this.disconnect(previousActiveId, {
            clearRuntime: false,
            removeEntry: false,
            shouldReconnect: false,
            closeReason: "reconnect:switch_thread",
          })
        }
      }
    }

    const hasOptimisticRuntimeSeedOption = Boolean(
      options && Object.prototype.hasOwnProperty.call(options, "optimisticRuntimeSeed"),
    )
    const entry = this.getOrCreateEntry(sessionId, undefined, options?.optimisticRuntimeSeed ?? null)
    if (hasOptimisticRuntimeSeedOption) {
      entry.runtime.optimisticSeed = options?.optimisticRuntimeSeed ?? null
    }
    const nextConversationId = normalizeRuntimeConversationId(options?.activeConversationId)
    if (previousActiveId === sessionId) {
      this.applyActiveConversationId(entry, nextConversationId, {
        replayForegroundRuntime: true,
        notify: false,
      })
    } else {
      this.applyActiveConversationId(entry, nextConversationId, {
        replayForegroundRuntime: false,
        notify: false,
      })
    }
    if (previousActiveId !== sessionId) {
      this.applyDeferredBackgroundReplicaInvalidationBeforeReuse(entry)
      if (this.shouldInvalidateEntryFromPersistedReplicaMetadata(entry)) {
        this.applyBackgroundReplicaInvalidation(entry)
      }
    }
    entry.lastActivatedAt = now()
    entry.connectionRole = "foreground_active"
    this.activeThreadId = sessionId
    const startedPersistedHydration = previousActiveId !== sessionId
    if (startedPersistedHydration) {
      this.startDurableReplicaHydration({
        entry,
        allowReplaceExistingRuntime: true,
      })
    }
    const shouldLogSessionActivation =
      previousActiveId !== sessionId ||
      hadExistingEntry === false

    if (shouldLogSessionActivation) {
      debugInfo("sync.new", "Switched foreground session", {
        fromSessionId: previousActiveId,
        toSessionId: sessionId,
        hadExistingEntry,
        durableHydrationRequested: startedPersistedHydration,
        ...buildRuntimeStateDebugSummary({ runtime: entry.runtime }),
      })
    }

    if (previousActiveId !== sessionId) {
      this.suspendBackgroundWarmAfterActiveSwitch()
      if (entry.runtime.optimisticSeed) {
        this.applyActivationContract(entry, {
          renderSource: "optimistic_seed",
          resumeSource: "none",
        })
        if (shouldLogSessionActivation) {
          debugInfo("sync.new", "Using optimistic seed because no local runtime restore was available", {
            sessionId,
            managerInstanceId: this.instanceId,
          })
        }
        this.applyOptimisticRuntimeSeed(entry)
      } else {
        const canAttemptCachedRuntimeRestore =
          entry.runtime.cachedState !== null &&
          isImmediatelyReusableCachedRuntimeMaterialization(
            entry.runtime.materialization,
          )
        const didRestoreCachedRuntime =
          canAttemptCachedRuntimeRestore &&
          this.restoreRuntimeState(entry)
        if (didRestoreCachedRuntime) {
          this.applyActivationContract(entry, {
            renderSource: "durable_replica",
            resumeSource: canResumeFromValidatedPrefixMaterialization(
              entry.runtime.materialization,
            )
              ? resolvePersistedValidatedResumeSource({
                  cursor: entry.resume.cursor,
                  historyToken: entry.resume.historyToken,
                })
              : "none",
          })
          if (shouldLogSessionActivation) {
            debugInfo("sync.new", "Reused cached runtime while durable hydration is pending", {
              sessionId,
              managerInstanceId: this.instanceId,
              ...buildRuntimeStateDebugSummary({ runtime: entry.runtime }),
            })
          }
        } else {
          this.applyActivationContract(entry, {
            renderSource: "empty",
            resumeSource: "none",
          })
          if (shouldLogSessionActivation) {
            debugInfo("sync.new", "Waiting for durable local replica hydrate or cold replay", {
              sessionId,
              managerInstanceId: this.instanceId,
              ...buildRuntimeStateDebugSummary({ runtime: entry.runtime }),
            })
          }
          if (this.getStoredRuntimeEventsForConversation(entry, entry.activeConversationId).length > 0) {
            this.replayStoredRuntimeEventsForActiveConversation(entry, entry.activeConversationId)
          } else {
            resetRuntimeStore()
          }
        }
      }
    } else if (entry.runtime.optimisticSeed) {
      this.applyActivationContract(entry, {
        renderSource: "optimistic_seed",
        resumeSource: "none",
      })
      this.applyOptimisticRuntimeSeed(entry)
    }

    this.pruneInactiveEntries()
    this.emit()
  }

  connect(sessionId: string, options?: ThreadSyncManagerConnectOptions): void {
    const entry = this.getOrCreateEntry(sessionId, options)
    this.applyOptions(entry, options)

    if (!entry.transport.connectEnabled) {
      this.closePendingHandoffThread({
        closeReason: "reconnect:handoff_abandoned",
      })
      if (this.hasStartedTransportWork(entry)) {
        this.disconnect(sessionId, { clearRuntime: false, removeEntry: false, shouldReconnect: false })
      } else {
        this.setConnectionStatus(entry, "disconnected", {
          suppressDegradedTracking: true,
        })
        this.emit()
      }
      return
    }

    if (hasOpenOrConnectingTransportSocket(entry.transport)) {
      if (
        entry.transport.ws?.readyState === WebSocket.OPEN &&
        entry.transport.helloSent === false &&
        entry.transport.deferInitialHandshake === false
      ) {
        this.sendInitialHandshake({
          entry,
          ws: entry.transport.ws,
          connectionVersion: entry.transport.connectionVersion,
          reason: "connect_reuse_existing_socket",
        })
      }
      if (entry.transport.helloSent) {
        this.restartKeepaliveTimer(entry)
      }
      this.completePendingHandoffIfReady(entry)
      this.emit()
      return
    }

    this.openSocket(entry)
  }

  forceReconnect(args: {
    sessionId: string
    closeReason: string
    resetReconnectAttempts?: boolean
  }): boolean {
    const entry = this.entries.get(args.sessionId)
    if (!entry) {
      return false
    }
    if (this.activeThreadId !== args.sessionId || entry.transport.connectEnabled !== true) {
      return false
    }

    const resetReconnectAttempts = args.resetReconnectAttempts !== false

    this.disconnect(args.sessionId, {
      clearRuntime: false,
      removeEntry: false,
      shouldReconnect: !resetReconnectAttempts,
      closeReason: args.closeReason,
    })

    const reconnectEntry = this.entries.get(args.sessionId)
    if (
      !reconnectEntry ||
      this.activeThreadId !== args.sessionId ||
      reconnectEntry.transport.connectEnabled !== true
    ) {
      return false
    }

    if (!resetReconnectAttempts) {
      return true
    }

    reconnectEntry.transport.reconnectAttempts = 0
    this.openSocket(reconnectEntry)
    this.emit()
    return true
  }

  disconnect(
    sessionId: string,
    options?: {
      clearRuntime?: boolean
      removeEntry?: boolean
      shouldReconnect?: boolean
      closeReason?: string
    },
  ): void {
    const entry = this.entries.get(sessionId)
    if (!entry) {
      if (options?.clearRuntime === true && this.activeThreadId === sessionId) {
        resetRuntimeStore()
      }
      return
    }

    const shouldReconnect = options?.shouldReconnect === true
    this.resetHttpPrefillState(entry, {
      preserveCompletedAt: options?.removeEntry !== true && options?.clearRuntime !== true,
    })
    this.resetCatchupBuffer(entry)
    this.resetLiveEventBuffer(entry)
    entry.queue.deferredPreHandshakeMessages = []
    if (options?.removeEntry === true) {
      this.clearRenderedLocalOutboxCleanupTimer(entry)
    }
    clearSessionSurfaceRefreshCooldownTimer(entry)
    entry.resume = resetResumeForDisconnect(entry.resume)
    beginTransportDisconnect(entry.transport, {
      closeReason:
        options?.closeReason ??
        (options?.removeEntry === true ? "reconnect:switch_thread" : "reconnect:manual"),
    })

    if (!shouldReconnect) {
      this.setConnectionStatus(entry, "disconnected", {
        suppressDegradedTracking: true,
      })
    }

    if (this.handoffPreviousThreadId === sessionId) {
      this.clearHandoffCloseTimer()
      this.handoffPreviousThreadId = null
    }
    if (options?.removeEntry === true || options?.clearRuntime === true) {
      entry.connectionRole = "inactive"
    }

    if (options?.clearRuntime === true && this.activeThreadId === sessionId) {
      this.closePendingHandoffThread({ closeReason: "reconnect:handoff_abandoned" })
    }

    if (options?.clearRuntime === true && this.activeThreadId === sessionId) {
      if (options?.removeEntry !== true) {
            this.captureActiveRuntimeState(entry)
      }
      resetRuntimeStore()
      if (options?.removeEntry === true) {
        this.clearEntryRuntimeState(entry)
      }
      this.activeThreadId = null
    }

    if (options?.removeEntry === true) {
      clearSessionSyncHealthEntry(sessionId)
      useSessionPrFlow.getState().clearSnapshot(sessionId)
      if (this.activeThreadId === sessionId) {
        this.activeThreadId = null
      }
      this.entries.delete(sessionId)
    } else {
      this.applyDeferredBackgroundReplicaInvalidationIfSafe(entry)
      if (shouldReconnect) {
        this.scheduleReconnect(entry)
      }
      this.syncHealth(entry)
    }

    this.emit()
  }

  async clearSessionState(sessionId: string): Promise<void> {
    const inFlightClear = this.sessionStateClearPromises.get(sessionId)
    if (inFlightClear) {
      return inFlightClear
    }

    const clearPromise = (async () => {
      this.clearingSessionIds.add(sessionId)
      try {
        // Mark persisted replica metadata invalid before removing the entry so a
        // stale same-tab cache cannot immediately win a later switchback.
        invalidatePersistedReplicaBySessionId(sessionId)
        this.disconnect(sessionId, { clearRuntime: true, removeEntry: true, shouldReconnect: false })
        clearThreadSyncPersistedReplicaSummaryPreservingInvalidation(sessionId)
      } finally {
        this.clearingSessionIds.delete(sessionId)
      }
    })()

    this.sessionStateClearPromises.set(sessionId, clearPromise)
    try {
      await clearPromise
    } finally {
      if (this.sessionStateClearPromises.get(sessionId) === clearPromise) {
        this.sessionStateClearPromises.delete(sessionId)
      }
    }
  }

  send(sessionId: string, message: { type: string; [key: string]: unknown }): boolean {
    const entry = this.entries.get(sessionId)
    if (!entry || entry.transport.readOnlyTransport) return false
    let ws = entry.transport.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    if (entry.transport.helloSent !== true) {
      if (this.shouldDeferPreHandshakeMessage({ entry, message })) {
        this.enqueueDeferredPreHandshakeMessage({ entry, message })
        this.emit()
        return true
      }
      const interactiveSendReady = this.ensureInteractiveSendReady({ entry })
      if (!interactiveSendReady) {
        return false
      }
      ws = entry.transport.ws
      const helloReadyAfterInteractiveSend = Boolean(entry.transport.helloSent)
      if (!ws || ws.readyState !== WebSocket.OPEN || !helloReadyAfterInteractiveSend) {
        return false
      }
    }

    try {
      ws.send(JSON.stringify(message))
      return true
    } catch (error) {
      recordTransportError({
        transport: entry.transport,
        message: error instanceof Error ? error.message : "Failed to send websocket message",
        nowMs: now(),
      })
      this.setConnectionStatus(entry, "error")
      this.emit()
      return false
    }
  }

  sendChatMessage(sessionId: string, message: { type: string; [key: string]: unknown }): ChatSendResult {
    const entry = this.entries.get(sessionId)
    if (!entry || entry.transport.readOnlyTransport) {
      return { accepted: false, reason: "transport_unavailable" }
    }

    const clientMessageId =
      asString(message.clientMessageId) ??
      (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `client-${now()}`)
    const outboundMessage = {
      ...message,
      conversationId: normalizeRuntimeConversationId(
        message.conversationId ?? entry.activeConversationId,
      ),
      clientMessageId,
    }

    this.upsertLocalOutboxEntry({
      entry,
      clientMessageId,
      message: outboundMessage,
      nowMs: now(),
    })

    const accepted = this.send(sessionId, outboundMessage)
    if (accepted) {
      entry.queue.pendingLocalDispatches.set(clientMessageId, {
        message: outboundMessage,
        lastAttemptedConnectionVersion: entry.transport.connectionVersion,
      })
      this.applyQueuePendingWorkSideEffects(entry)
      this.emit()
      return { accepted: true, reason: null }
    }

    entry.queue.pendingLocalDispatches.set(clientMessageId, {
      message: outboundMessage,
      lastAttemptedConnectionVersion: null,
    })
    this.maybeExpeditePendingLocalDispatches({ entry })
    this.applyQueuePendingWorkSideEffects(entry)
    this.emit()
    return { accepted: true, reason: "queued_local" }
  }

  stageServerOrchestratedChatMessage(
    sessionId: string,
    message: { type: string; [key: string]: unknown },
  ): ChatSendResult {
    const entry = this.entries.get(sessionId)
    if (!entry || entry.transport.readOnlyTransport) {
      return { accepted: false, reason: "transport_unavailable" }
    }

    const clientMessageId = asString(message.clientMessageId)
    if (!clientMessageId) {
      return { accepted: false, reason: "missing_client_message_id" }
    }

    const nowMs = now()
    const outboundMessage = {
      ...message,
      conversationId: normalizeRuntimeConversationId(message.conversationId ?? entry.activeConversationId),
      clientMessageId,
    }

    this.upsertLocalOutboxEntry({
      entry,
      clientMessageId,
      message: outboundMessage,
      nowMs,
    })
    entry.queue.serverOrchestratedLocalOutboxClientMessageIds.set(
      clientMessageId,
      nowMs + SERVER_ORCHESTRATED_LOCAL_OUTBOX_TTL_MS,
    )
    this.scheduleRenderedLocalOutboxCleanup(entry)
    this.applyQueuePendingWorkSideEffects(entry)
    this.emit()
    return { accepted: true, reason: null }
  }

  setActiveConversationId(sessionId: string, conversationId: string): void {
    const entry = this.entries.get(sessionId)
    if (!entry) return
    this.applyActiveConversationId(entry, conversationId, {
      replayForegroundRuntime: true,
      notify: true,
    })
  }

  /**
   * Cancel a single queued message by its `clientMessageId`.
   *
   * This performs two actions:
   * 1. Optimistically removes the message from the local outbox (for
   *    immediate UI feedback regardless of whether the server has seen it).
   * 2. Sends a `cancel_queued_message` command to the server so the
   *    server-side pending message row is also deleted (if it exists).
   *
   * Returns `true` if the cancel was dispatched (WebSocket send succeeded or
   * the message was local-only), `false` if transport is unavailable AND the
   * message was not in the local outbox.
   */
  cancelQueuedMessage(sessionId: string, clientMessageId: string): boolean {
    const entry = this.entries.get(sessionId)
    if (!entry) return false

    const pendingLocalDispatchOnly = entry.queue.pendingLocalDispatches.has(clientMessageId)

    // Also attempt to tell the server to cancel (best-effort — if the
    // WebSocket is down the server-side row will remain until reconnect,
    // but the user can re-send abort then).
    const sentToServer = this.send(sessionId, {
      type: "cancel_queued_message",
      clientMessageId,
    })

    if (!sentToServer && !pendingLocalDispatchOnly) {
      return false
    }

    const previousLocalLength = entry.queue.localOutbox.length
    entry.queue.localOutbox = entry.queue.localOutbox.filter(
      (item) => item.clientMessageId !== clientMessageId,
    )
    const removedLocally = entry.queue.localOutbox.length < previousLocalLength
    entry.queue.pendingLocalDispatches.delete(clientMessageId)
    entry.queue.serverOrchestratedLocalOutboxClientMessageIds.delete(clientMessageId)
    entry.queue.recentlyServerObservedClientMessageIds.delete(clientMessageId)

    let removedFromServerQueue = false
    if (sentToServer) {
      const previousServerLength = entry.queue.serverPendingMessages.length
      entry.queue.serverPendingMessages = entry.queue.serverPendingMessages.filter(
        (item) => item.clientMessageId !== clientMessageId,
      )
      removedFromServerQueue = entry.queue.serverPendingMessages.length < previousServerLength
    }

    if (removedLocally || removedFromServerQueue || sentToServer) {
      this.rememberCancelledClientMessageId(entry, clientMessageId)
      this.applyQueuePendingWorkSideEffects(entry)
      this.emit()
      return true
    }
    return false
  }

  private rememberCancelledClientMessageId(entry: ThreadSyncEntry, clientMessageId: string): void {
    this.pruneCancelledClientMessageIds(entry, now())
    entry.queue.cancelledClientMessageIds.set(
      clientMessageId,
      now() + CANCELLED_MESSAGE_TOMBSTONE_TTL_MS,
    )
  }

  private pruneCancelledClientMessageIds(entry: ThreadSyncEntry, nowMs: number): void {
    for (const [clientMessageId, expiresAt] of entry.queue.cancelledClientMessageIds.entries()) {
      if (expiresAt <= nowMs) {
        entry.queue.cancelledClientMessageIds.delete(clientMessageId)
      }
    }
  }

  private pruneRecentlyRenderedClientMessageIds(entry: ThreadSyncEntry, nowMs: number): void {
    for (const [clientMessageId, expiresAt] of entry.queue.recentlyRenderedClientMessageIds.entries()) {
      if (expiresAt <= nowMs) {
        entry.queue.recentlyRenderedClientMessageIds.delete(clientMessageId)
      }
    }
  }

  private pruneServerOrchestratedLocalOutboxClientMessageIds(
    entry: ThreadSyncEntry,
    nowMs: number,
  ): void {
    for (const [
      clientMessageId,
      expiresAt,
    ] of entry.queue.serverOrchestratedLocalOutboxClientMessageIds.entries()) {
      if (expiresAt <= nowMs) {
        entry.queue.serverOrchestratedLocalOutboxClientMessageIds.delete(clientMessageId)
      }
    }
  }

  private prunePendingMessageClientMessageIdsByMessageId(entry: ThreadSyncEntry, nowMs: number): void {
    for (const [messageId, mapping] of entry.queue.pendingMessageClientMessageIdsByMessageId.entries()) {
      if (mapping.expiresAt <= nowMs) {
        entry.queue.pendingMessageClientMessageIdsByMessageId.delete(messageId)
      }
    }
  }

  private pruneRecentlyServerObservedClientMessageIds(entry: ThreadSyncEntry, nowMs: number): void {
    for (const [clientMessageId, expiresAt] of entry.queue.recentlyServerObservedClientMessageIds.entries()) {
      if (expiresAt <= nowMs) {
        entry.queue.recentlyServerObservedClientMessageIds.delete(clientMessageId)
      }
    }
  }

  private rememberPendingMessageClientMessageIdsByMessageId(args: {
    entry: ThreadSyncEntry
    messages: PendingQueueRecord[]
    nowMs: number
  }): void {
    this.prunePendingMessageClientMessageIdsByMessageId(args.entry, args.nowMs)
    for (const message of args.messages) {
      if (typeof message.id !== "number") continue
      if (typeof message.clientMessageId !== "string" || message.clientMessageId.length === 0) continue
      args.entry.queue.pendingMessageClientMessageIdsByMessageId.set(message.id, {
        clientMessageId: message.clientMessageId,
        expiresAt: args.nowMs + RENDERED_LOCAL_OUTBOX_HANDOFF_TTL_MS,
      })
    }
  }

  private resolveRenderedClientMessageId(args: {
    entry: ThreadSyncEntry
    messageId: unknown
    clientMessageId: unknown
    nowMs: number
  }): string | null {
    const explicitClientMessageId = asString(args.clientMessageId)
    if (explicitClientMessageId) return explicitClientMessageId

    const messageId = asNumber(args.messageId)
    if (messageId === null) return null

    this.prunePendingMessageClientMessageIdsByMessageId(args.entry, args.nowMs)
    return args.entry.queue.pendingMessageClientMessageIdsByMessageId.get(messageId)?.clientMessageId ?? null
  }

  private filterRenderedPendingMessages(entry: ThreadSyncEntry, nowMs: number): void {
    this.pruneRecentlyRenderedClientMessageIds(entry, nowMs)
    if (entry.queue.recentlyRenderedClientMessageIds.size === 0) return
    entry.queue.serverPendingMessages = entry.queue.serverPendingMessages.filter((message) => {
      if (!message.clientMessageId) return true
      return !entry.queue.recentlyRenderedClientMessageIds.has(message.clientMessageId)
    })
    entry.queue.serverPendingSessionActions = entry.queue.serverPendingSessionActions.filter((message) => {
      if (!message.clientMessageId) return true
      return !entry.queue.recentlyRenderedClientMessageIds.has(message.clientMessageId)
    })
  }

  private clearRenderedLocalOutboxCleanupTimer(entry: ThreadSyncEntry): void {
    if (entry.queue.renderedLocalOutboxCleanupTimer === null) return
    clearTimeout(entry.queue.renderedLocalOutboxCleanupTimer)
    entry.queue.renderedLocalOutboxCleanupTimer = null
  }

  private pruneExpiredRenderedLocalOutboxHandoffs(entry: ThreadSyncEntry, nowMs: number): boolean {
    const previousLength = entry.queue.localOutbox.length
    const expiredClientMessageIds: string[] = []
    entry.queue.localOutbox = entry.queue.localOutbox.filter((message) => {
      if (typeof message.renderedHandoffExpiresAt !== "number") return true
      const shouldKeep = message.renderedHandoffExpiresAt > nowMs
      if (!shouldKeep) {
        expiredClientMessageIds.push(message.clientMessageId)
      }
      return shouldKeep
    })
    for (const clientMessageId of expiredClientMessageIds) {
      entry.queue.recentlyRenderedClientMessageIds.delete(clientMessageId)
      entry.queue.recentlyServerObservedClientMessageIds.delete(clientMessageId)
    }
    return entry.queue.localOutbox.length !== previousLength
  }

  private scheduleRenderedLocalOutboxCleanup(entry: ThreadSyncEntry): void {
    this.clearRenderedLocalOutboxCleanupTimer(entry)

    let nextExpiryAt: number | null = null
    for (const message of entry.queue.localOutbox) {
      if (typeof message.renderedHandoffExpiresAt !== "number") continue
      if (!Number.isFinite(message.renderedHandoffExpiresAt)) continue
      nextExpiryAt = nextExpiryAt === null
        ? message.renderedHandoffExpiresAt
        : Math.min(nextExpiryAt, message.renderedHandoffExpiresAt)
    }
    for (const expiresAt of entry.queue.recentlyRenderedClientMessageIds.values()) {
      if (!Number.isFinite(expiresAt)) continue
      nextExpiryAt = nextExpiryAt === null ? expiresAt : Math.min(nextExpiryAt, expiresAt)
    }
    for (const expiresAt of entry.queue.serverOrchestratedLocalOutboxClientMessageIds.values()) {
      if (!Number.isFinite(expiresAt)) continue
      nextExpiryAt = nextExpiryAt === null ? expiresAt : Math.min(nextExpiryAt, expiresAt)
    }
    for (const mapping of entry.queue.pendingMessageClientMessageIdsByMessageId.values()) {
      if (!Number.isFinite(mapping.expiresAt)) continue
      nextExpiryAt = nextExpiryAt === null ? mapping.expiresAt : Math.min(nextExpiryAt, mapping.expiresAt)
    }

    if (nextExpiryAt === null) return

    entry.queue.renderedLocalOutboxCleanupTimer = setTimeout(() => {
      entry.queue.renderedLocalOutboxCleanupTimer = null
      const currentEntry = this.entries.get(entry.sessionId)
      if (this.destroyed || currentEntry !== entry) return
      const cleanupObservedAt = now()
      const pruned = this.pruneExpiredRenderedLocalOutboxHandoffs(entry, cleanupObservedAt)
      const localOutboxLengthBeforeReconcile = entry.queue.localOutbox.length
      this.pruneRecentlyRenderedClientMessageIds(entry, cleanupObservedAt)
      this.pruneServerOrchestratedLocalOutboxClientMessageIds(entry, cleanupObservedAt)
      this.prunePendingMessageClientMessageIdsByMessageId(entry, cleanupObservedAt)
      entry.queue.localOutbox = reconcileLocalOutboxWithServerPending({
        localOutbox: entry.queue.localOutbox,
        serverPendingMessages: [
          ...entry.queue.serverPendingMessages,
          ...entry.queue.serverPendingSessionActions,
        ],
        preserveClientMessageIds: this.buildPreservedClientMessageIds({
          entry,
          nowMs: cleanupObservedAt,
        }),
      })
      this.scheduleRenderedLocalOutboxCleanup(entry)
      this.prunePendingLocalDispatches(entry)
      this.applyQueuePendingWorkSideEffects(entry)
      if (
        pruned ||
        entry.queue.localOutbox.length !== localOutboxLengthBeforeReconcile
      ) {
        this.emit()
      }
    }, Math.max(0, nextExpiryAt - now()))
  }

  private rememberRecentlyServerObservedClientMessageIds(args: {
    entry: ThreadSyncEntry
    messages: PendingQueueRecord[]
    nowMs: number
  }): void {
    this.pruneRecentlyServerObservedClientMessageIds(args.entry, args.nowMs)
    for (const message of args.messages) {
      if (!message.clientMessageId) continue
      args.entry.queue.serverOrchestratedLocalOutboxClientMessageIds.delete(
        message.clientMessageId,
      )
      args.entry.queue.recentlyServerObservedClientMessageIds.set(
        message.clientMessageId,
        args.nowMs + RECENTLY_SERVER_OBSERVED_CLIENT_MESSAGE_ID_TTL_MS,
      )
    }
  }

  private buildPendingWorkSideEffectSnapshot(entry: ThreadSyncEntry): {
    pendingMessageCount: number
    latestPendingWorkTimestamp: number | null
  } {
    const serverPendingRecords = [
      ...entry.queue.serverPendingMessages,
      ...entry.queue.serverPendingSessionActions,
    ]
    const serverPendingClientMessageIds = new Set(
      serverPendingRecords
        .map((message) => message.clientMessageId)
        .filter((clientMessageId): clientMessageId is string => typeof clientMessageId === "string"),
    )
    const unresolvedLocalOutboxCount = entry.queue.localOutbox.filter((message) => {
      if (serverPendingClientMessageIds.has(message.clientMessageId)) return false
      // Rendered handoff rows are only temporary UI mirrors while the canonical
      // runtime user message catches up. Counting them as requested work would
      // keep the sidebar spinner alive after the prompt has already landed.
      if (typeof message.renderedHandoffExpiresAt === "number") return false
      return true
    }).length

    return {
      pendingMessageCount: serverPendingRecords.length + unresolvedLocalOutboxCount,
      // Local outbox timestamps are browser-clock values, while server pending
      // rows and terminal thread finishes are SessionDO-clock values. When local
      // unresolved work exists, pass a null timestamp so derived sidebar state
      // treats it as fresh pending work without comparing mixed clocks.
      latestPendingWorkTimestamp: unresolvedLocalOutboxCount > 0
        ? null
        : getLatestPendingQueueTimestamp({ pendingQueueRecords: serverPendingRecords }),
    }
  }

  private applyQueuePendingWorkSideEffects(entry: ThreadSyncEntry): void {
    const snapshot = this.buildPendingWorkSideEffectSnapshot(entry)
    applyPendingMessageCountSideEffects({
      sessionId: entry.sessionId,
      pendingMessageCount: snapshot.pendingMessageCount,
      latestPendingWorkTimestamp: snapshot.latestPendingWorkTimestamp,
    })
    if (snapshot.pendingMessageCount === 0) {
      clearOptimisticWorkRequestPendingSideEffects({
        sessionId: entry.sessionId,
      })
    }
  }

  private buildPreservedClientMessageIds(args: {
    entry: ThreadSyncEntry
    nowMs: number
  }): Set<string> {
    this.pruneRecentlyServerObservedClientMessageIds(args.entry, args.nowMs)
    this.pruneRecentlyRenderedClientMessageIds(args.entry, args.nowMs)
    this.pruneServerOrchestratedLocalOutboxClientMessageIds(args.entry, args.nowMs)
    const preservedClientMessageIds = new Set<string>(args.entry.queue.pendingLocalDispatches.keys())
    for (const clientMessageId of args.entry.queue.serverOrchestratedLocalOutboxClientMessageIds.keys()) {
      preservedClientMessageIds.add(clientMessageId)
    }
    for (const clientMessageId of args.entry.queue.recentlyServerObservedClientMessageIds.keys()) {
      preservedClientMessageIds.add(clientMessageId)
    }
    for (const clientMessageId of args.entry.queue.recentlyRenderedClientMessageIds.keys()) {
      preservedClientMessageIds.add(clientMessageId)
    }
    for (const message of args.entry.queue.localOutbox) {
      if (typeof message.renderedHandoffExpiresAt !== "number") continue
      if (message.renderedHandoffExpiresAt <= args.nowMs) continue
      preservedClientMessageIds.add(message.clientMessageId)
    }
    return preservedClientMessageIds
  }

  private filterCancelledPendingMessages(entry: ThreadSyncEntry, nowMs: number): void {
    this.pruneCancelledClientMessageIds(entry, nowMs)
    if (entry.queue.cancelledClientMessageIds.size === 0) return
    entry.queue.serverPendingMessages = entry.queue.serverPendingMessages.filter((message) => {
      if (!message.clientMessageId) return true
      return !entry.queue.cancelledClientMessageIds.has(message.clientMessageId)
    })
  }

  private upsertLocalOutboxEntry(args: {
    entry: ThreadSyncEntry
    clientMessageId: string
    message: { type: string; [key: string]: unknown }
    nowMs: number
  }): void {
    const existingIndex = args.entry.queue.localOutbox.findIndex(
      (item) => item.clientMessageId === args.clientMessageId,
    )
    const nextDraft = normalizeQueuedMessageDraft({
      clientMessageId: args.clientMessageId,
      message: args.message,
      nowMs: args.nowMs,
    })

    if (existingIndex === -1) {
      args.entry.queue.localOutbox = [...args.entry.queue.localOutbox, nextDraft]
      return
    }

    args.entry.queue.localOutbox = args.entry.queue.localOutbox.map((item, index) => {
      if (index !== existingIndex) return item
      return {
        ...item,
        ...nextDraft,
        observedServerStatus: item.observedServerStatus,
        timestamp: item.timestamp,
      }
    })
  }

  private buildPendingLocalDispatchOrder(entry: ThreadSyncEntry): string[] {
    const orderedClientMessageIds: string[] = []
    const seenClientMessageIds = new Set<string>()

    for (const item of entry.queue.localOutbox) {
      if (!entry.queue.pendingLocalDispatches.has(item.clientMessageId)) continue
      if (seenClientMessageIds.has(item.clientMessageId)) continue
      seenClientMessageIds.add(item.clientMessageId)
      orderedClientMessageIds.push(item.clientMessageId)
    }

    for (const clientMessageId of entry.queue.pendingLocalDispatches.keys()) {
      if (seenClientMessageIds.has(clientMessageId)) continue
      seenClientMessageIds.add(clientMessageId)
      orderedClientMessageIds.push(clientMessageId)
    }

    return orderedClientMessageIds
  }

  private flushPendingLocalDispatches(args: {
    entry: ThreadSyncEntry
  }): boolean {
    const { entry } = args
    const isForegroundDispatch = entry.connectionRole === "foreground_active"
    const isHandoffDrainDispatch = entry.connectionRole === "handoff_previous"
    if (!isForegroundDispatch && !isHandoffDrainDispatch) return false
    if (entry.transport.readOnlyTransport && !isHandoffDrainDispatch) return false
    if (entry.queue.pendingLocalDispatches.size === 0) return false

    let sentAny = false
    for (const clientMessageId of this.buildPendingLocalDispatchOrder(entry)) {
      const pendingDispatch = entry.queue.pendingLocalDispatches.get(clientMessageId)
      if (!pendingDispatch) continue
      if (pendingDispatch.lastAttemptedConnectionVersion === entry.transport.connectionVersion) {
        continue
      }
      const sent = isHandoffDrainDispatch
        ? this.sendPendingLocalDispatchDuringHandoff({
            entry,
            message: pendingDispatch.message,
          })
        : this.send(entry.sessionId, pendingDispatch.message)
      if (!sent) {
        return sentAny
      }
      entry.queue.pendingLocalDispatches.set(clientMessageId, {
        ...pendingDispatch,
        lastAttemptedConnectionVersion: entry.transport.connectionVersion,
      })
      sentAny = true
    }

    if (
      isHandoffDrainDispatch &&
      !this.hasUnattemptedPendingLocalDispatchesForCurrentConnection(entry)
    ) {
      this.closePendingHandoffThread({
        closeReason: "reconnect:handoff_drain_complete",
      })
    }

    return sentAny
  }

  private sendPendingLocalDispatchDuringHandoff(args: {
    entry: ThreadSyncEntry
    message: { type: string; [key: string]: unknown }
  }): boolean {
    const { entry } = args
    const ws = entry.transport.ws
    if (!ws || ws.readyState !== WebSocket.OPEN || entry.transport.helloSent !== true) {
      return false
    }

    try {
      ws.send(JSON.stringify(args.message))
      return true
    } catch (error) {
      recordTransportError({
        transport: entry.transport,
        message: error instanceof Error ? error.message : "Failed to send handoff queued message",
        nowMs: now(),
      })
      this.setConnectionStatus(entry, "error")
      this.emit()
      return false
    }
  }

  private shouldDeferPreHandshakeMessage(args: {
    entry: ThreadSyncEntry
    message: { type: string; [key: string]: unknown }
  }): boolean {
    if (
      args.entry.transport.deferInitialHandshake !== true &&
      args.entry.httpPrefill.status !== "inflight"
    ) {
      return false
    }

    // `pty_connected` is emitted automatically by the PTY bridge. It should not
    // interrupt the initial HTTP/R2 bootstrap path; otherwise a large fresh
    // browser load can be forced back to a cursor-0 websocket replay.
    return args.message.type === "pty_connected"
  }

  private enqueueDeferredPreHandshakeMessage(args: {
    entry: ThreadSyncEntry
    message: { type: string; [key: string]: unknown }
  }): void {
    const existingIndex = args.entry.queue.deferredPreHandshakeMessages.findIndex(
      (message) => message.type === args.message.type,
    )
    if (existingIndex === -1) {
      args.entry.queue.deferredPreHandshakeMessages = [
        ...args.entry.queue.deferredPreHandshakeMessages,
        args.message,
      ]
      return
    }

    args.entry.queue.deferredPreHandshakeMessages = args.entry.queue.deferredPreHandshakeMessages.map(
      (message, index) => index === existingIndex ? args.message : message,
    )
  }

  private flushDeferredPreHandshakeMessages(args: {
    entry: ThreadSyncEntry
    ws: WebSocket
  }): boolean {
    if (
      args.entry.transport.helloSent !== true ||
      args.ws.readyState !== WebSocket.OPEN ||
      args.entry.queue.deferredPreHandshakeMessages.length === 0
    ) {
      return false
    }

    const messages = args.entry.queue.deferredPreHandshakeMessages
    args.entry.queue.deferredPreHandshakeMessages = []
    let sentAny = false
    for (const message of messages) {
      try {
        args.ws.send(JSON.stringify(message))
        sentAny = true
      } catch (error) {
        recordTransportError({
          transport: args.entry.transport,
          message: error instanceof Error ? error.message : "Failed to send deferred websocket message",
          nowMs: now(),
        })
        this.setConnectionStatus(args.entry, "error")
        return sentAny
      }
    }
    return sentAny
  }

  private maybeExpeditePendingLocalDispatches(args: {
    entry: ThreadSyncEntry
  }): void {
    const { entry } = args
    if (entry.queue.pendingLocalDispatches.size === 0) return
    if (entry.connectionRole !== "foreground_active") return
    if (entry.transport.connectEnabled !== true) return
    if (hasOpenOrConnectingTransportSocket(entry.transport)) return

    clearReconnectTimer(entry.transport)
    this.openSocket(entry)
  }

  private prunePendingLocalDispatches(entry: ThreadSyncEntry): void {
    if (entry.queue.pendingLocalDispatches.size === 0) return

    const visibleLocalOutboxClientMessageIds = new Set(
      entry.queue.localOutbox.map((item) => item.clientMessageId),
    )
    const serverObservedClientMessageIds = new Set(
      [...entry.queue.serverPendingMessages, ...entry.queue.serverPendingSessionActions]
        .map((message) => message.clientMessageId)
        .filter((clientMessageId): clientMessageId is string => typeof clientMessageId === "string"),
    )

    for (const clientMessageId of entry.queue.pendingLocalDispatches.keys()) {
      if (!visibleLocalOutboxClientMessageIds.has(clientMessageId)) {
        entry.queue.pendingLocalDispatches.delete(clientMessageId)
        continue
      }
      if (serverObservedClientMessageIds.has(clientMessageId)) {
        entry.queue.pendingLocalDispatches.delete(clientMessageId)
        entry.queue.serverOrchestratedLocalOutboxClientMessageIds.delete(clientMessageId)
      }
    }
  }

  private removeLocalOutboxEntryForClientMessageId(
    entry: ThreadSyncEntry,
    clientMessageId: string,
  ): boolean {
    const previousLocalLength = entry.queue.localOutbox.length
    entry.queue.localOutbox = entry.queue.localOutbox.filter(
      (item) => item.clientMessageId !== clientMessageId,
    )
    entry.queue.pendingLocalDispatches.delete(clientMessageId)
    entry.queue.serverOrchestratedLocalOutboxClientMessageIds.delete(clientMessageId)
    entry.queue.recentlyServerObservedClientMessageIds.delete(clientMessageId)
    entry.queue.recentlyRenderedClientMessageIds.delete(clientMessageId)
    return entry.queue.localOutbox.length !== previousLocalLength
  }

  async refreshStatusFromProbe(
    sessionId: string,
    _options?: { reason?: string },
  ): Promise<boolean> {
    const entry = this.entries.get(sessionId) ?? this.getOrCreateEntry(sessionId)
    entry.transport.wsTransportProbeAt = now()
    this.emit()

    try {
      const response = await fetch(buildSessionStatusUrl({
        baseUrl: import.meta.env.VITE_API_URL || "",
        sessionId,
        conversationId: entry.activeConversationId,
      }), {
        credentials: "include",
      })
      if (!response.ok) {
        entry.transport.wsTransportProbeState = "session_unreachable"
        this.emit()
        return false
      }

      const payload = await response.json().catch(() => null)
      entry.transport.wsTransportProbeState = "session_reachable"
      this.applyStatusPayload(entry, payload)
      this.emit()
      return true
    } catch {
      entry.transport.wsTransportProbeState = "session_unreachable"
      this.emit()
      return false
    }
  }

  getViewState(sessionId: string | null): ThreadSyncViewState {
    if (!sessionId) {
      if (!this.emptyViewState) {
        this.emptyViewState = this.buildViewState(createEntry("", { connectEnabled: false }))
      }
      return this.emptyViewState
    }

    const entry = this.entries.get(sessionId)
    if (!entry) {
      const cachedMissingEntry = this.viewStateCache.get(sessionId)
      if (cachedMissingEntry) return cachedMissingEntry
      const nextViewState = this.buildViewState(createEntry(sessionId))
      this.viewStateCache.set(sessionId, nextViewState)
      return nextViewState
    }

    const cachedViewState = this.viewStateCache.get(sessionId)
    if (cachedViewState) return cachedViewState

    const nextViewState = this.buildViewState(entry)
    this.viewStateCache.set(sessionId, nextViewState)
    return nextViewState
  }

  private scheduleSessionSurfaceRefreshReevaluation(entry: ThreadSyncEntry): void {
    clearSessionSurfaceRefreshCooldownTimer(entry)

    if (
      entry.lastHttpPrefillCompletedAt === null ||
      entry.lastSessionSurfaceRefreshAt === null ||
      entry.lastHttpPrefillCompletedAt <= entry.lastSessionSurfaceRefreshAt
    ) {
      return
    }

    const remainingCooldownMs =
      entry.lastSessionSurfaceRefreshAt + PREFILL_SESSION_SURFACE_FETCH_COOLDOWN_MS - now()
    if (remainingCooldownMs <= 0) {
      this.emit()
      return
    }

    entry.sessionSurfaceRefreshCooldownTimer = setTimeout(() => {
      entry.sessionSurfaceRefreshCooldownTimer = null
      this.emit()
    }, remainingCooldownMs)
  }

  recordSessionSurfaceRefresh(sessionId: string): void {
    const entry = this.entries.get(sessionId)
    if (!entry) {
      return
    }

    entry.lastSessionSurfaceRefreshAt = now()
    this.scheduleSessionSurfaceRefreshReevaluation(entry)
    this.emit()
  }

  private captureActiveRuntimeState(
    entry: ThreadSyncEntry,
    options?: {
      materialization?: Exclude<ThreadRuntimeMaterialization, "empty">
    },
  ): void {
    if (this.clearingSessionIds.has(entry.sessionId)) {
      return
    }

    if (entry.backgroundReplicaInvalidatedAtMs !== null) {
      const nextMaterialization = options?.materialization ?? entry.runtime.materialization
      if (nextMaterialization === "authoritative") {
        entry.backgroundReplicaInvalidatedAtMs = null
      } else {
        return
      }
    }

    captureRuntimeCacheActiveState({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      connectionVersion: entry.transport.connectionVersion,
      resume: entry.resume,
      materialization: options?.materialization,
      nowMs: now(),
    })
  }

  private restoreRuntimeState(entry: ThreadSyncEntry): boolean {
    return restoreThreadSyncRuntimeState(entry.runtime)
  }

  private clearEntryRuntimeState(entry: ThreadSyncEntry): void {
    this.resetLiveEventBuffer(entry)
    clearThreadSyncRuntimeState(entry.runtime)
  }

  private applyActivationContract(entry: ThreadSyncEntry, args: {
    renderSource: ThreadSyncRenderSource
    resumeSource: ThreadSyncResumeSource
  }): void {
    entry.runtime.renderSource = args.renderSource
    entry.runtime.resumeSource = args.resumeSource
  }

  private applyBackgroundReplicaInvalidation(entry: ThreadSyncEntry): void {
    this.resetHttpPrefillState(entry)
    this.resetCatchupBuffer(entry)
    this.clearEntryRuntimeState(entry)
    entry.resume = resetResumeForColdReplay()
    entry.backgroundReplicaInvalidatedAtMs = null
    // Persisted invalidation metadata is expected to be marked by the caller
    // that detected the invalidation source (for example metadata store
    // subscribers). Re-marking from this local apply path causes recursive
    // invalidation notifications and can overflow the stack.
    this.clearDurableReplica(entry, "background_replica_invalidated", {
      skipPersistedReplicaInvalidation: true,
    })
  }

  private applyDeferredBackgroundReplicaInvalidationIfSafe(entry: ThreadSyncEntry): boolean {
    if (entry.backgroundReplicaInvalidatedAtMs === null) {
      return false
    }

    if (this.activeThreadId === entry.sessionId) {
      return false
    }

    if (entry.connectionRole !== "inactive") {
      return false
    }

    if (hasOpenOrConnectingTransportSocket(entry.transport)) {
      return false
    }

    this.applyBackgroundReplicaInvalidation(entry)
    return true
  }

  private applyDeferredBackgroundReplicaInvalidationBeforeReuse(entry: ThreadSyncEntry): boolean {
    if (entry.backgroundReplicaInvalidatedAtMs === null) {
      return false
    }

    this.resetHttpPrefillState(entry)
    this.resetCatchupBuffer(entry)
    clearTransportTimers(entry.transport)
    if (this.handoffPreviousThreadId === entry.sessionId) {
      this.clearHandoffCloseTimer()
      this.handoffPreviousThreadId = null
    }
    if (entry.transport.ws !== null) {
      beginTransportDisconnect(entry.transport, {
        closeReason: "reconnect:background_replica_invalidated",
      })
      this.setConnectionStatus(entry, "disconnected", {
        suppressDegradedTracking: true,
      })
    }
    entry.connectionRole = "inactive"
    this.applyBackgroundReplicaInvalidation(entry)
    return true
  }

  private invalidatePersistedReplicaState(
    entry: ThreadSyncEntry,
    requestedNowMs?: number,
  ): number {
    return invalidateRuntimeReplicaState({
      sessionId: entry.sessionId,
      requestedNowMs,
    })
  }

  private clearDurableReplicaPersistedSummary(sessionId: string): void {
    const persistedSummary = getThreadSyncPersistedReplicaSummary(sessionId)
    if (persistedSummary?.storageBackend !== "durable_replica") {
      return
    }

    clearThreadSyncPersistedReplicaSummaryPreservingInvalidation(sessionId)
  }

  private buildDurableReplicaPersistedSummary(args: {
    entry: ThreadSyncEntry
    meta: ThreadReplicaMetaRecord
    agentType: ThreadSyncBackgroundWarmSummary["agentType"]
    sourceKind: "foreground_ws" | "foreground_http_prefill" | "hydrated"
  }): ThreadSyncBackgroundWarmSummary {
    const hasReplicaData =
      args.meta.localCursor > 0 || args.meta.lastBaselineSequence > 0

    return {
      sessionId: args.entry.sessionId,
      agentType: args.agentType,
      historyToken: args.meta.historyToken,
      storageBackend: "durable_replica",
      ...buildThreadSyncReplicaSummary({
        resumeCursor: hasReplicaData ? args.meta.localCursor : null,
        quality:
          args.meta.historyToken !== null
            ? "continuity_validated"
            : hasReplicaData
              ? "best_effort"
              : null,
        savedAtMs: args.meta.lastUpdatedAtMs,
        provenance: buildThreadSyncReplicaProvenance(
          args.sourceKind === "hydrated"
            ? { sourceKind: "hydrated" }
            : {
                sourceKind: args.sourceKind,
                sourceTabId: getForegroundOwnershipTabId(),
              },
        ),
      }),
    }
  }

  private upsertDurableReplicaPersistedSummary(args: {
    entry: ThreadSyncEntry
    meta: ThreadReplicaMetaRecord
    agentType?: ThreadSyncBackgroundWarmSummary["agentType"]
    sourceKind: "foreground_ws" | "foreground_http_prefill" | "hydrated"
  }): void {
    upsertThreadSyncPersistedReplicaSummary(
      this.buildDurableReplicaPersistedSummary({
        entry: args.entry,
        meta: args.meta,
        agentType:
          args.agentType ??
          args.entry.runtime.cachedState?.agentType ??
          null,
        sourceKind: args.sourceKind,
      }),
    )
  }

  private clearDurableReplica(
    entry: ThreadSyncEntry,
    reason: string,
    options?: {
      skipPersistedReplicaInvalidation?: boolean
      invalidatedAtMs?: number
    },
  ): void {
    if (options?.skipPersistedReplicaInvalidation !== true) {
      this.invalidatePersistedReplicaState(entry, options?.invalidatedAtMs)
    }
    const expectedEpoch = entry.durableReplicaEpoch ?? undefined
    void this.enqueueDurableReplicaWrite(entry, () => clearThreadReplicaSession({
      ...buildThreadReplicaScope(entry.sessionId),
      expectedEpoch,
      reason,
      nowMs: now(),
    })).then((result) => {
      if (result.status !== "cleared") {
        return
      }

      entry.durableReplicaEpoch = result.meta.replicaEpoch
      this.clearDurableReplicaPersistedSummary(entry.sessionId)

      debugInfo("sync.new", "Cleared durable thread replica", {
        sessionId: entry.sessionId,
        managerInstanceId: this.instanceId,
        reason,
        replicaEpoch: result.meta.replicaEpoch,
      })
    })
  }

  private maybeSaveDurableReplicaCheckpoint(args: {
    entry: ThreadSyncEntry
    expectedEpoch: number
    sourceKind: ThreadSyncCheckpointSaveSourceKind
    meta?: ThreadReplicaMetaRecord
  }): void {
    const checkpointConversationId = normalizeRuntimeConversationId(args.entry.activeConversationId)
    // Checkpoints serialize a *projection*, not raw events. Every projection is
    // conversation-scoped, so persist the conversation id captured with the
    // projection instead of reading mutable active-tab state later in the async
    // save path. This lets side tabs restore from local checkpoints after they
    // have once been fully materialized.
    // Checkpoints become hydrate anchors and can survive tail-window compaction,
    // so only persist them once the foreground transcript is fully authoritative.
    if (
      this.activeThreadId !== args.entry.sessionId ||
      args.entry.runtime.cachedState === null ||
      args.entry.runtime.cachedState.snapshot === null ||
      args.entry.runtime.materialization !== "authoritative"
    ) {
      return
    }

    // `cachedState` is only refreshed by `captureActiveRuntimeState`, which runs
    // at milestones (sync.ready, sync.catchup.complete, control-plane snapshot
    // apply, switch-away, etc.). It is NOT refreshed on every `sync.event`.
    // Meanwhile `entry.resume.cursor` advances on every event. When the caller
    // is a post-append `.then` fired mid-stream, `cachedState.lastEventId` lags
    // behind `entry.resume.cursor`, and its `rawOpenCodeProjection` is missing
    // the effects of events in that gap (specifically new messages, because
    // `cloneProjectionForMutation` shallow-copies `messagesById` so only the
    // cloned projection receives new keys).
    //
    // Saving in that state would write a checkpoint stamped at the new cursor
    // but built from the stale projection, because `saveThreadReplicaCheckpoint`
    // calls `normalizeManagedRuntimeState` which forcibly overwrites
    // `lastEventId` and `providerEventId` to match the supplied cursor. On
    // hydrate, the replay path starts at `checkpoint.cursor + 1` and never
    // replays the events the projection is missing, so the poisoned snapshot
    // wins permanently. This was Bug 2 in browser-cache-terminal-state-signaling.
    //
    // Skipping here is safe: the runtime lane already contains every applied
    // event, and the next milestone capture will produce a fresh cachedState
    // whose following save will anchor at the up-to-date cursor. Any earlier
    // events between the last clean checkpoint and that save are replayed on
    // hydrate from the runtime lane.
    const cachedLastEventId = args.entry.runtime.cachedState.lastEventId
    if (
      typeof cachedLastEventId !== "number" ||
      !Number.isFinite(cachedLastEventId) ||
      cachedLastEventId !== args.entry.resume.cursor
    ) {
      return
    }

    this.enqueueDurableReplicaCheckpointSave({
      entry: args.entry,
      expectedEpoch: args.expectedEpoch,
      conversationId: checkpointConversationId,
      sourceKind: args.sourceKind,
      historyToken: args.entry.resume.historyToken,
      cursor: args.entry.resume.cursor,
      runtimeState: args.entry.runtime.cachedState,
      lastBaselineSequence: args.meta?.lastBaselineSequence ?? null,
      eventContractVersion: args.meta?.eventContractVersion ?? null,
      projectorVersion: args.meta?.projectorVersion ?? null,
      createdAtMs: now(),
    })
  }

  private enqueueDurableReplicaCheckpointSave(request: ThreadSyncCheckpointSaveRequest): void {
    const fingerprintKey = buildCheckpointSaveFingerprintKey(request)
    if (request.entry.checkpointSave.lastSuccessfulFingerprint === fingerprintKey) {
      return
    }

    request.entry.checkpointSave.pending = request
    this.flushDurableReplicaCheckpointSave(request.entry)
  }

  private flushDurableReplicaCheckpointSave(entry: ThreadSyncEntry): void {
    if (entry.checkpointSave.inFlight) {
      return
    }

    const request = entry.checkpointSave.pending
    if (!request) {
      return
    }

    const fingerprintKey = buildCheckpointSaveFingerprintKey(request)
    entry.checkpointSave.pending = null
    if (entry.checkpointSave.lastSuccessfulFingerprint === fingerprintKey) {
      this.flushDurableReplicaCheckpointSave(entry)
      return
    }

    entry.checkpointSave.inFlight = true
    void this.enqueueDurableReplicaWrite(entry, () => saveThreadReplicaCheckpoint({
      ...buildThreadReplicaScope(entry.sessionId),
      expectedEpoch: request.expectedEpoch,
      cursor: request.cursor,
      runtimeState: request.runtimeState,
      projectorVersion: request.projectorVersion ?? undefined,
      createdAtMs: request.createdAtMs,
      // Tag the checkpoint with the conversation we actually projected into
      // `useAgentRuntime` when we captured this state. Hydration later
      // compares this against the target conversation to avoid restoring a
      // side-chat projection into the default tab (or vice versa).
      conversationId: request.conversationId,
    })).then((result) => {
      if (result.status !== "saved") {
        return
      }
      if (this.destroyed || this.entries.get(entry.sessionId) !== entry) {
        return
      }

      entry.checkpointSave.lastSuccessfulFingerprint = fingerprintKey
      this.promoteDurableReplicaValidationFromCheckpoint({
        entry,
        expectedEpoch: request.expectedEpoch,
        sourceKind: request.sourceKind,
        cursor: request.cursor,
        historyToken: request.historyToken,
      })

      this.maybeCompactDurableReplicaTailWindow({
        entry,
        expectedEpoch: request.expectedEpoch,
        runtimeState: request.runtimeState,
        sourceKind: request.sourceKind,
      })
    }).finally(() => {
      if (this.destroyed || this.entries.get(entry.sessionId) !== entry) {
        return
      }
      entry.checkpointSave.inFlight = false
      this.flushDurableReplicaCheckpointSave(entry)
    })
  }

  private promoteDurableReplicaValidationFromCheckpoint(args: {
    entry: ThreadSyncEntry
    expectedEpoch: number
    sourceKind: ThreadSyncCheckpointSaveSourceKind
    cursor: number
    historyToken: string | null
  }): void {
    if (
      typeof args.historyToken !== "string" ||
      args.historyToken.trim().length === 0
    ) {
      return
    }

    void this.enqueueDurableReplicaWrite(args.entry, () => advanceThreadReplicaRuntimeCursorMeta({
      ...buildThreadReplicaScope(args.entry.sessionId),
      nextCursor: args.cursor,
      historyToken: args.historyToken,
      expectedEpoch: args.expectedEpoch,
      nowMs: now(),
    })).then((result) => {
      if (this.destroyed || this.entries.get(args.entry.sessionId) !== args.entry) {
        return
      }
      if (result.status !== "advanced") {
        return
      }

      args.entry.durableReplicaEpoch = result.meta.replicaEpoch
      this.upsertDurableReplicaPersistedSummary({
        entry: args.entry,
        meta: result.meta,
        sourceKind: args.sourceKind,
      })
    })
  }

  private maybeCompactDurableReplicaTailWindow(args: {
    entry: ThreadSyncEntry
    expectedEpoch: number
    runtimeState?: NonNullable<ThreadSyncRuntimeState["cachedState"]>
    sourceKind?: ThreadSyncCheckpointSaveSourceKind
  }): void {
    const runtimeState = args.runtimeState ?? args.entry.runtime.cachedState
    if (runtimeState === null) {
      return
    }

    const coveragePolicy = evaluateThreadSyncDurableReplicaCoveragePolicy({
      currentState: runtimeState,
    })
    if (coveragePolicy.mode !== "compact_to_tail_window") {
      return
    }

    void this.enqueueDurableReplicaWrite(args.entry, () => compactThreadReplicaRuntimeTailWindow({
      ...buildThreadReplicaScope(args.entry.sessionId),
      retainRuntimeEventCount: coveragePolicy.retainRuntimeEventCount,
      expectedEpoch: args.expectedEpoch,
      nowMs: now(),
    })).then((result) => {
      if (this.destroyed || this.entries.get(args.entry.sessionId) !== args.entry) {
        return
      }
      if (result.status !== "compacted") {
        return
      }

      args.entry.durableReplicaEpoch = result.meta.replicaEpoch
      this.upsertDurableReplicaPersistedSummary({
        entry: args.entry,
        meta: result.meta,
        sourceKind: args.sourceKind ?? "foreground_ws",
      })
      debugInfo("sync.new", "Compacted durable thread replica to tail window", {
        sessionId: args.entry.sessionId,
        managerInstanceId: this.instanceId,
        retainRuntimeEventCount: coveragePolicy.retainRuntimeEventCount,
        deletedRuntimeEventCount: result.deletedRuntimeEventCount,
        checkpointCursor: result.checkpointCursor,
        replicaEpoch: result.meta.replicaEpoch,
        ...coveragePolicy.summary,
      })
    })
  }

  private advanceDurableReplicaCursor(args: {
    entry: ThreadSyncEntry
    nextCursor: number
    historyToken: string | null
  }): void {
    const expectedEpoch = args.entry.durableReplicaEpoch ?? undefined
    void this.enqueueDurableReplicaWrite(args.entry, () => advanceThreadReplicaRuntimeCursorMeta({
      ...buildThreadReplicaScope(args.entry.sessionId),
      nextCursor: args.nextCursor,
      historyToken: args.historyToken,
      expectedEpoch,
      nowMs: now(),
    })).then((result) => {
      if (!this.isCurrentEntry(args.entry)) {
        return
      }
      if (result.status === "advanced" || result.status === "noop") {
        args.entry.durableReplicaEpoch = result.meta.replicaEpoch
        this.upsertDurableReplicaPersistedSummary({
          entry: args.entry,
          meta: result.meta,
          sourceKind: "foreground_http_prefill",
        })
        this.maybeSaveDurableReplicaCheckpoint({
          entry: args.entry,
          expectedEpoch: result.meta.replicaEpoch,
          sourceKind: "foreground_http_prefill",
          meta: result.meta,
        })
      }
    })
  }

  private appendRuntimeEventsToDurableReplica(args: {
    entry: ThreadSyncEntry
    expectedAfter: number
    events: Array<{
      contractVersion?: number
      replayEventId?: string
      id: number
      type: string
      payload: unknown
      timestamp: number
      childSessionId?: string
      conversationId?: string
    }>
    nextCursor: number
    historyToken: string | null
    sourceKind: "foreground_ws" | "foreground_http_prefill"
    saveCheckpoint?: boolean
  }): void {
    // The durable event lane stores every transport row regardless of which
    // conversation sent it. This used to be default-only, but that caused two
    // serious bugs once side chats existed:
    //
    // 1. Appending only the active conversation's events produced a sparse
    //    sequence (e.g. 10, 12, 14 when 11 and 13 were side-chat rows). The
    //    lane enforces `sequence = previous + 1`, so the very next append
    //    would return `gap/non_contiguous_input`, clear the whole replica,
    //    and force a cold cursor replay. This is the primary cause of the
    //    "constantly loading" symptom in multi-conversation sessions.
    //
    // 2. Skipping appends entirely on non-default conversations meant side
    //    chats had no durable cache at all. Every reload was a cold replay
    //    over the full parent-session history.
    //
    // The lane now holds all events tagged with `conversationId`; projection
    // hydration filters by the active conversation when applying rows to
    // `useAgentRuntime`. Checkpoints remain conversation-scoped and are still
    // only written for the active conversation.
    if (args.events.length === 0) {
      return
    }

    const durableEvents = buildThreadReplicaRuntimeEventsFromTransportEvents({
      events: args.events,
    })
    const expectedEpoch = args.entry.durableReplicaEpoch ?? undefined
    const appendArgs = (expectedEpochOverride: number | undefined = expectedEpoch) => ({
      ...buildThreadReplicaScope(args.entry.sessionId),
      lane: "runtime" as const,
      events: durableEvents,
      expectedAfter: args.expectedAfter,
      serverResume: {
        localCursor: args.nextCursor,
        historyToken: args.historyToken,
      },
      sourceKind: args.sourceKind,
      nowMs: now(),
      expectedEpoch: expectedEpochOverride,
    })

    void this.enqueueDurableReplicaWrite(args.entry, async () => {
      let result = await appendThreadReplicaDelta(appendArgs())
      if (!this.isCurrentEntry(args.entry)) {
        return
      }
      if (
        result.status === "gap" &&
        result.reason === "before_oldest_available" &&
        args.expectedAfter <= EMPTY_THREAD_REPLICA_CURSOR &&
        args.entry.resume.requestedAfter <= EMPTY_THREAD_REPLICA_CURSOR
      ) {
        const localCursor = this.getBestKnownLocalRawCursor(args.entry)
        if (
          this.hasValidatedResumeCursor(args.entry) &&
          localCursor !== null &&
          localCursor > EMPTY_THREAD_REPLICA_CURSOR
        ) {
          debugWarn("sync.new", "Preserved compacted durable replica when stale cold replay append arrived", {
            sessionId: args.entry.sessionId,
            managerInstanceId: this.instanceId,
            previousAppendResult: result,
            requestedAfter: args.entry.resume.requestedAfter,
            resumeCursor: args.entry.resume.cursor,
            localCursor,
          })
          return
        }
        const clearResult = await clearThreadReplicaSession({
          ...buildThreadReplicaScope(args.entry.sessionId),
          expectedEpoch: args.entry.durableReplicaEpoch ?? undefined,
          reason: "foreground_runtime_before_oldest_cold_replay",
          nowMs: now(),
        })
        if (!this.isCurrentEntry(args.entry)) {
          return
        }
        if (clearResult.status === "cleared") {
          args.entry.durableReplicaEpoch = clearResult.meta.replicaEpoch
          this.clearDurableReplicaPersistedSummary(args.entry.sessionId)
          debugInfo("sync.new", "Cleared compacted durable replica before cold replay append retry", {
            sessionId: args.entry.sessionId,
            managerInstanceId: this.instanceId,
            previousAppendResult: result,
            replicaEpoch: clearResult.meta.replicaEpoch,
          })
          result = await appendThreadReplicaDelta(appendArgs(clearResult.meta.replicaEpoch))
          if (!this.isCurrentEntry(args.entry)) {
            return
          }
        }
      }

      if (result.status === "appended" || result.status === "duplicate") {
        args.entry.durableReplicaEpoch = result.meta.replicaEpoch
        this.upsertDurableReplicaPersistedSummary({
          entry: args.entry,
          meta: result.meta,
          sourceKind: args.sourceKind,
        })
        if (args.saveCheckpoint !== false) {
          this.maybeSaveDurableReplicaCheckpoint({
            entry: args.entry,
            expectedEpoch: result.meta.replicaEpoch,
            sourceKind: args.sourceKind,
            meta: result.meta,
          })
        }
        return
      }

      if (result.status === "storage_unavailable" || result.status === "stale_writer") {
        return
      }

      // When the durable replica is completely empty (currentSequence === 0) but
      // we are trying to append from a non-zero offset, the replica was wiped by
      // a concurrent hydration clear or reconnect reset while the foreground
      // runtime already advanced past cursor 0. In this case there is nothing to
      // protect — the IDB lane is empty — so clear and retry the append to seed
      // a fresh tail-window replica from the current position. Without this
      // recovery, every subsequent append also fails with after_current_tail and
      // the thread never builds a durable cache, forcing a cold replay on every
      // switch.
      if (
        result.status === "gap" &&
        result.reason === "after_current_tail" &&
        result.currentSequence === EMPTY_THREAD_REPLICA_CURSOR &&
        args.expectedAfter > EMPTY_THREAD_REPLICA_CURSOR
      ) {
        const clearResult = await clearThreadReplicaSession({
          ...buildThreadReplicaScope(args.entry.sessionId),
          expectedEpoch: args.entry.durableReplicaEpoch ?? undefined,
          reason: "foreground_runtime_ahead_of_empty_replica",
          nowMs: now(),
        })
        if (!this.isCurrentEntry(args.entry)) {
          return
        }
        if (clearResult.status === "cleared") {
          args.entry.durableReplicaEpoch = clearResult.meta.replicaEpoch
          this.clearDurableReplicaPersistedSummary(args.entry.sessionId)
          debugInfo("sync.new", "Cleared empty durable replica before seeding from current foreground position", {
            sessionId: args.entry.sessionId,
            managerInstanceId: this.instanceId,
            previousAppendResult: result,
            expectedAfter: args.expectedAfter,
            replicaEpoch: clearResult.meta.replicaEpoch,
          })
          result = await appendThreadReplicaDelta(appendArgs(clearResult.meta.replicaEpoch))
          if (!this.isCurrentEntry(args.entry)) {
            return
          }
          if (result.status === "appended" || result.status === "duplicate") {
            args.entry.durableReplicaEpoch = result.meta.replicaEpoch
            this.upsertDurableReplicaPersistedSummary({
              entry: args.entry,
              meta: result.meta,
              sourceKind: args.sourceKind,
            })
            if (args.saveCheckpoint !== false) {
              this.maybeSaveDurableReplicaCheckpoint({
                entry: args.entry,
                expectedEpoch: result.meta.replicaEpoch,
                sourceKind: args.sourceKind,
                meta: result.meta,
              })
            }
            return
          }
        }
      }

      // A runtime append gap means this writer is missing a prefix event for the
      // shared durable lane. In multi-tab/multi-conversation sessions this can
      // be caused by a stale queued writer or a tab that only observed a later
      // event; it is not proof that the already persisted prefix is corrupt.
      // Clearing here invalidates healthy replicas in other tabs and forces
      // visible cold replays. Leave the current prefix intact so the next
      // reconnect can resume from the last durable cursor and fetch the missing
      // event from the server.

      if (result.status === "conflict") {
        this.invalidatePersistedReplicaState(args.entry)
        args.entry.durableReplicaEpoch = result.nextEpoch
        this.clearDurableReplicaPersistedSummary(args.entry.sessionId)
      }

      debugWarn("sync.new", "Durable thread runtime replica append failed", {
        sessionId: args.entry.sessionId,
        managerInstanceId: this.instanceId,
        result,
      })
    })
  }

  private appendBaselineEventsToDurableReplica(args: {
    entry: ThreadSyncEntry
    events: Array<{
      contractVersion?: number
      replayEventId?: string
      baselineSeq: number
      type: string
      payload: unknown
      timestamp: number
      originSessionId?: string
      originEventId?: number
    }>
    sourceKind: "foreground_ws" | "foreground_http_prefill"
  }): void {
    if (args.events.length === 0) {
      return
    }

    const firstBaselineSeq = Math.max(0, Math.floor(args.events[0]?.baselineSeq ?? 0))
    const expectedEpoch = args.entry.durableReplicaEpoch ?? undefined
    void this.enqueueDurableReplicaWrite(args.entry, () => appendThreadReplicaDelta({
      ...buildThreadReplicaScope(args.entry.sessionId),
      lane: "baseline",
      events: buildThreadReplicaBaselineEventsFromTransportEvents({
        events: args.events,
      }),
      expectedAfter: Math.max(0, firstBaselineSeq - 1),
      sourceKind: args.sourceKind,
      nowMs: now(),
      expectedEpoch,
    })).then((result) => {
      if (!this.isCurrentEntry(args.entry)) {
        return
      }
      if (result.status === "appended" || result.status === "duplicate") {
        args.entry.durableReplicaEpoch = result.meta.replicaEpoch
        this.upsertDurableReplicaPersistedSummary({
          entry: args.entry,
          meta: result.meta,
          sourceKind: args.sourceKind,
        })
        this.maybeSaveDurableReplicaCheckpoint({
          entry: args.entry,
          expectedEpoch: result.meta.replicaEpoch,
          sourceKind: args.sourceKind,
          meta: result.meta,
        })
        return
      }

      if (result.status === "storage_unavailable" || result.status === "stale_writer") {
        return
      }

      // Baseline pages are an auxiliary replay lane. A sparse or stale page
      // should not destroy the runtime transcript cache; hydration integrity
      // checks will reset the baseline lane if the persisted rows are truly
      // unusable.

      if (result.status === "conflict") {
        this.invalidatePersistedReplicaState(args.entry)
        args.entry.durableReplicaEpoch = result.nextEpoch
        this.clearDurableReplicaPersistedSummary(args.entry.sessionId)
      }

      debugWarn("sync.new", "Durable thread baseline replica append failed", {
        sessionId: args.entry.sessionId,
        managerInstanceId: this.instanceId,
        result,
      })
    })
  }

  private shouldDeferColdHandshakeForDurableHydration(args: {
    entry: ThreadSyncEntry
    reason: string
  }): boolean {
    if (
      args.entry.durableReplicaHydration.inFlight === false ||
      args.entry.durableReplicaHydration.shouldDeferColdHandshake === false
    ) {
      return false
    }

    if (args.entry.queue.pendingLocalDispatches.size > 0) {
      return false
    }

    if (args.reason === "interactive_send" || args.reason === "interactive_send_no_prefill") {
      return false
    }

    return true
  }

  private maybeReleaseHandshakeAfterDurableHydration(entry: ThreadSyncEntry): void {
    const hasValidatedHydratedResume = this.hasValidatedHydratedResume(entry)
    const hydratedResumeCoversTerminalCursor = this.hydratedResumeCoversKnownTerminalCursor(entry)
    if (entry.httpPrefill.status === "inflight" && hydratedResumeCoversTerminalCursor) {
      this.cancelCurrentHttpPrefill({
        entry,
        status: "skipped",
        reason: "durable_hydration_covered_terminal_cursor",
      })
    }

    const ws = entry.transport.ws
    if (
      !ws ||
      ws.readyState !== WebSocket.OPEN ||
      entry.transport.helloSent === true ||
      entry.transport.deferInitialHandshake === false ||
      entry.connectionRole !== "foreground_active"
    ) {
      return
    }

    if (entry.httpPrefill.status === "inflight" && !hasValidatedHydratedResume) {
      return
    }

    this.releaseDeferredHandshake({
      entry,
      ws,
      connectionVersion: entry.transport.connectionVersion,
      reason: hasValidatedHydratedResume
        ? "durable_hydration_restored"
        : "durable_hydration_settled",
      abortHttpPrefill: hasValidatedHydratedResume,
    })
  }

  private startDurableReplicaHydration(args: {
    entry: ThreadSyncEntry
    allowReplaceExistingRuntime?: boolean
  }): void {
    const { entry } = args
    // Capture the conversation we are hydrating for so late async callbacks
    // can detect a mid-flight tab switch and bail before writing a stale
    // projection into the active `useAgentRuntime` store.
    const hydrationConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    const hydrationGeneration = entry.durableReplicaHydration.generation + 1
    const hydrationConversationRevision = entry.activeConversationRevision
    entry.durableReplicaHydration = {
      generation: hydrationGeneration,
      inFlight: true,
      shouldDeferColdHandshake: hasValidatedDurableReplicaSummary(
        getThreadSyncPersistedReplicaSummary(entry.sessionId),
      ),
    }
    const hydrationPromise = hydrateThreadReplica({
      ...buildThreadReplicaScope(entry.sessionId),
      conversationId: hydrationConversationId,
    })
    void withDurableReplicaHydrationTimeout({
      promise: hydrationPromise,
      sessionId: entry.sessionId,
      managerInstanceId: this.instanceId,
      generation: hydrationGeneration,
    }).then((hydrationResult) => {
      const currentEntry = this.entries.get(entry.sessionId)
      if (
        this.destroyed ||
        currentEntry !== entry ||
        entry.durableReplicaHydration.generation !== hydrationGeneration ||
        entry.activeConversationRevision !== hydrationConversationRevision ||
        normalizeRuntimeConversationId(entry.activeConversationId) !== hydrationConversationId
      ) {
        return
      }

      const hasRuntimeAlreadyAvailable =
        entry.runtime.cachedState !== null ||
        (this.activeThreadId === entry.sessionId && useAgentRuntime.getState().snapshot !== null)
      const hasActiveRuntimeSnapshot =
        this.activeThreadId === entry.sessionId &&
        useAgentRuntime.getState().snapshot !== null
      const shouldAllowDurableReplacement = args.allowReplaceExistingRuntime === true
      const currentReplicaSummary = this.buildEntryReplicaSummary(entry)
      const incomingReplicaSummary =
        hydrationResult.status === "hydrated"
          ? buildThreadSyncReplicaSummary({
              resumeCursor:
                typeof hydrationResult.meta.historyToken === "string" &&
                hydrationResult.meta.historyToken.trim().length > 0
                  ? hydrationResult.meta.localCursor
                  : null,
              snapshot: hydrationResult.runtimeState.snapshot,
              quality: hydrationResult.meta.historyToken ? "continuity_validated" : "best_effort",
              savedAtMs: hydrationResult.meta.lastUpdatedAtMs,
              provenance: buildThreadSyncReplicaProvenance({
                sourceKind: "hydrated",
              }),
            })
          : null
      const replicaSummaryComparison =
        hydrationResult.status === "hydrated"
          ? compareThreadSyncReplicaSummaries({
              current: currentReplicaSummary,
              incoming: incomingReplicaSummary,
            })
          : null
      const isPersistedReplicaMetadataInvalidated =
        hydrationResult.status === "hydrated" &&
        (() => {
          const invalidatedAtMs = getThreadSyncPersistedReplicaInvalidation(entry.sessionId)
          return (
            invalidatedAtMs !== null &&
            invalidatedAtMs >= hydrationResult.meta.lastUpdatedAtMs
          )
        })()
      const currentReplicaCursor = currentReplicaSummary?.cursor ?? null
      const incomingReplicaCursor = incomingReplicaSummary?.cursor ?? null
      const shouldPreserveCurrentPartialReplicaState =
        shouldAllowDurableReplacement &&
        hydrationResult.status === "hydrated" &&
        entry.runtime.materialization === "authoritative_partial" &&
        typeof currentReplicaCursor === "number" &&
        typeof incomingReplicaCursor === "number" &&
        currentReplicaCursor > incomingReplicaCursor
      const shouldPreserveCurrentReplicaState =
        shouldAllowDurableReplacement &&
        hydrationResult.status === "hydrated" &&
        (
          shouldPreserveCurrentPartialReplicaState ||
          (
            entry.runtime.materialization !== "authoritative_partial" &&
            replicaSummaryComparison !== "incoming"
          )
        )

      if (
        hydrationResult.status !== "hydrated" ||
        isPersistedReplicaMetadataInvalidated ||
        (hasRuntimeAlreadyAvailable && (!shouldAllowDurableReplacement || shouldPreserveCurrentReplicaState))
      ) {
        if (hydrationResult.status === "missing") {
          entry.durableReplicaEpoch = null
        }
        if (
          hydrationResult.status === "missing" &&
          hydrationResult.reason !== "storage_unavailable"
        ) {
          this.clearDurableReplicaPersistedSummary(entry.sessionId)
        }
        if (hydrationResult.status === "reset_required" || isPersistedReplicaMetadataInvalidated) {
          const persistedInvalidatedAtMs = getThreadSyncPersistedReplicaInvalidation(
            entry.sessionId,
          )
          if (hydrationResult.status === "reset_required") {
            entry.durableReplicaEpoch = hydrationResult.meta.replicaEpoch
          }
          this.clearDurableReplica(
            entry,
            hydrationResult.status === "reset_required"
              ? hydrationResult.reason
              : "persisted_invalidation",
            {
              invalidatedAtMs: persistedInvalidatedAtMs ?? undefined,
            },
          )
        }

        if (
          hydrationResult.status === "hydrated" &&
          !isPersistedReplicaMetadataInvalidated &&
          hasRuntimeAlreadyAvailable &&
          shouldAllowDurableReplacement &&
          shouldPreserveCurrentReplicaState &&
          this.activeThreadId === entry.sessionId &&
          !hasActiveRuntimeSnapshot &&
          entry.runtime.cachedState !== null
        ) {
          // Hydration concluded that the cached replica still wins, but the
          // visible active store is empty. Re-surface the winning cached state so
          // switchback activation does not leave the transcript blank until the
          // transport path repairs it.
          this.applyActivationContract(entry, {
            renderSource: "durable_replica",
            resumeSource: canResumeFromValidatedPrefixMaterialization(
              entry.runtime.materialization,
            )
              ? resolvePersistedValidatedResumeSource({
                  cursor: entry.resume.cursor,
                  historyToken: entry.resume.historyToken,
                })
              : "none",
          })
          this.restoreRuntimeState(entry)
          this.emit()
        }

        if (
          hydrationResult.status === "missing" &&
          this.activeThreadId === entry.sessionId &&
          !hasActiveRuntimeSnapshot &&
          entry.runtime.materialization === "authoritative_partial" &&
          entry.runtime.cachedState !== null
        ) {
          this.applyActivationContract(entry, {
            renderSource: "durable_replica",
            resumeSource: "none",
          })
          this.restoreRuntimeState(entry)
          this.emit()
        }

        return
      }

      entry.durableReplicaEpoch = hydrationResult.meta.replicaEpoch

      entry.runtime.cachedState = hydrationResult.runtimeState
      entry.runtime.materialization = "authoritative"
      entry.runtime.connectionVersion = null
      entry.runtime.continuityValidatedConnectionVersion = null
      entry.runtime.replicaQuality = hydrationResult.meta.historyToken ? "continuity_validated" : "best_effort"
      entry.runtime.replicaSavedAtMs = hydrationResult.meta.lastUpdatedAtMs
      entry.runtime.replicaProvenance = buildThreadSyncReplicaProvenance({
        sourceKind: "hydrated",
      })

      if (
        typeof hydrationResult.meta.historyToken === "string" &&
        hydrationResult.meta.historyToken.trim().length > 0 &&
        hydrationResult.meta.localCursor > 0
      ) {
        entry.resume = restoreResumeFromPersistedValidatedSnapshot({
          current: entry.resume,
          cursor: hydrationResult.meta.localCursor,
          historyToken: hydrationResult.meta.historyToken,
        })
      }

      this.applyActivationContract(entry, {
        renderSource: "durable_replica",
        resumeSource: resolvePersistedValidatedResumeSource({
          cursor: hydrationResult.meta.localCursor,
          historyToken: hydrationResult.meta.historyToken,
        }),
      })
      this.upsertDurableReplicaPersistedSummary({
        entry,
        meta: hydrationResult.meta,
        agentType: hydrationResult.runtimeState.agentType,
        sourceKind: "hydrated",
      })

      if (this.activeThreadId === entry.sessionId) {
        const restored = this.restoreRuntimeState(entry)
        if (restored) {
          debugInfo("sync.new", "Restored durable thread replica to active store", {
            sessionId: entry.sessionId,
            managerInstanceId: this.instanceId,
            actualRestoreSource: "idb_thread_replica",
            targetCursor: hydrationResult.targetCursor,
            checkpointCursor: hydrationResult.checkpointCursor,
            runtimeReplayCount: hydrationResult.runtimeReplayCount,
            baselineReplayCount: hydrationResult.baselineReplayCount,
            ...buildRuntimeStateDebugSummary({ runtime: entry.runtime }),
          })
        }
      }

      this.emit()
    }).catch((error) => {
      debugWarn("sync.new", "Durable thread replica hydration failed", {
        sessionId: entry.sessionId,
        managerInstanceId: this.instanceId,
        error: error instanceof Error ? error.message : String(error),
      })
    }).finally(() => {
      const currentEntry = this.entries.get(entry.sessionId)
      if (
        this.destroyed ||
        currentEntry !== entry ||
        entry.durableReplicaHydration.generation !== hydrationGeneration ||
        entry.activeConversationRevision !== hydrationConversationRevision ||
        normalizeRuntimeConversationId(entry.activeConversationId) !== hydrationConversationId
      ) {
        return
      }

      entry.durableReplicaHydration = {
        generation: hydrationGeneration,
        inFlight: false,
        shouldDeferColdHandshake: false,
      }
      this.maybeReleaseHandshakeAfterDurableHydration(entry)
    })
  }

  private replaceDisplayOnlyRuntimeWithControlPlaneOnly(entry: ThreadSyncEntry): void {
    const replaced = replaceDisplayOnlyRuntimeWithControlPlaneOnlyFromCache({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      resume: entry.resume,
      controlPlane: entry.controlPlane,
      codeServerError: entry.effects.codeServerError,
      connectionVersion: entry.transport.connectionVersion,
      nowMs: now(),
    })
    if (replaced) {
      entry.resume = resetResumeForColdReplay()
    }
  }

  private hasAcceptedRuntimeCursor(args: {
    activeRuntimeState: ReturnType<typeof useAgentRuntime.getState> | null
    acceptedCursor: number
    resumeCursor?: number | null
  }): boolean {
    if (!args.activeRuntimeState) {
      return false
    }

    const normalizedAcceptedCursor = Math.max(0, Math.floor(args.acceptedCursor))
    const lastEventId =
      typeof args.activeRuntimeState.lastEventId === "number" &&
        Number.isFinite(args.activeRuntimeState.lastEventId) &&
        args.activeRuntimeState.lastEventId > 0
        ? Math.floor(args.activeRuntimeState.lastEventId)
        : null
    const providerEventId =
      typeof args.activeRuntimeState.snapshot?.cursorState.providerEventId === "number" &&
        Number.isFinite(args.activeRuntimeState.snapshot.cursorState.providerEventId) &&
        args.activeRuntimeState.snapshot.cursorState.providerEventId > 0
        ? Math.floor(args.activeRuntimeState.snapshot.cursorState.providerEventId)
        : null

    if (lastEventId === null && providerEventId === null) {
      if (
        typeof args.resumeCursor === "number" &&
        Number.isFinite(args.resumeCursor) &&
        Math.floor(args.resumeCursor) >= normalizedAcceptedCursor
      ) {
        return true
      }
    }

    if (lastEventId !== null && lastEventId !== normalizedAcceptedCursor) {
      return false
    }
    if (providerEventId !== null && providerEventId !== normalizedAcceptedCursor) {
      return false
    }

    if (normalizedAcceptedCursor === 0 && lastEventId === null && providerEventId === null) {
      return true
    }

    return lastEventId === normalizedAcceptedCursor || providerEventId === normalizedAcceptedCursor
  }

  private resolveVisibleAuthoritativeRuntimeCursor(entry: ThreadSyncEntry): number | null {
    if (this.activeThreadId !== entry.sessionId) {
      return null
    }

    if (entry.runtime.materialization !== "authoritative") {
      return null
    }

    const activeRuntimeState = useAgentRuntime.getState()
    if (activeRuntimeState.snapshot === null) {
      return null
    }

    const lastEventId = this.normalizeRawCursor(activeRuntimeState.lastEventId)
    const providerEventId = this.normalizeRawCursor(
      activeRuntimeState.snapshot.cursorState.providerEventId,
    )

    if (
      lastEventId !== null &&
      providerEventId !== null &&
      lastEventId !== providerEventId
    ) {
      return null
    }

    return lastEventId ?? providerEventId
  }

  private resolveHttpPrefillRequestedAfter(entry: ThreadSyncEntry): number {
    const resumeCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.floor(entry.resume.cursor),
    )
    const visibleRuntimeCursor = this.resolveVisibleAuthoritativeRuntimeCursor(entry)
    if (visibleRuntimeCursor !== null && visibleRuntimeCursor > resumeCursor) {
      return visibleRuntimeCursor
    }

    return resumeCursor
  }

  private promoteVisibleRuntimeAfterCursorRegression(args: {
    entry: ThreadSyncEntry
    readyPlan: ReturnType<typeof planSyncReadyHandling>
    syncReadyLogDetails: Record<string, unknown>
  }): boolean {
    const visibleRuntimeCursor = this.resolveVisibleAuthoritativeRuntimeCursor(args.entry)
    if (visibleRuntimeCursor === null) {
      return false
    }

    const debugSnapshot = args.readyPlan.debugSnapshot
    const nextHistoryToken = debugSnapshot.nextHistoryToken
    if (typeof nextHistoryToken !== "string" || nextHistoryToken.trim().length === 0) {
      return false
    }

    if (
      debugSnapshot.historyContinuity !== "contiguous" ||
      debugSnapshot.replicationContinuity !== "contiguous" ||
      debugSnapshot.hasHistoryContinuityBroken ||
      debugSnapshot.hasHistoryTokenMismatch ||
      !debugSnapshot.hasCursorRegression
    ) {
      return false
    }

    const acceptedCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.floor(debugSnapshot.acceptedCursor),
    )
    const tailCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.floor(debugSnapshot.tailCursor),
    )
    if (visibleRuntimeCursor <= acceptedCursor || visibleRuntimeCursor > tailCursor) {
      return false
    }

    args.entry.resume = {
      ...args.entry.resume,
      cursor: visibleRuntimeCursor,
      tailCursor: Math.max(tailCursor, visibleRuntimeCursor),
      requestedAfter: EMPTY_THREAD_REPLICA_CURSOR,
      historyToken: nextHistoryToken,
      isCatchingUp: visibleRuntimeCursor < tailCursor,
      syncReadyReceived: false,
      lastPageCount: 0,
      lastPageBytes: 0,
    }
    args.entry.runtime.continuityValidatedConnectionVersion =
      args.readyPlan.continuityValidatedConnectionVersion
    this.captureActiveRuntimeState(args.entry, {
      materialization: "authoritative",
    })

    debugWarn("sync.new", "Promoted visible runtime cursor after sync.ready cursor regression", {
      ...args.syncReadyLogDetails,
      promotedCursor: visibleRuntimeCursor,
      promotedTailCursor: args.entry.resume.tailCursor,
    })

    this.disconnect(args.entry.sessionId, {
      clearRuntime: false,
      removeEntry: false,
      shouldReconnect: true,
      closeReason: "reconnect:promote_visible_runtime_after_cursor_regression",
    })
    return true
  }

  private shouldTrustForegroundHttpPrefillResumeCursor(args: {
    acceptedCursor: number
    resumeCursor: number
    resumeHistoryToken: string | null
    nextHistoryToken: string | null
    historyContinuity: "contiguous" | "broken"
    replicationContinuity: "contiguous" | "broken"
    hasHistoryTokenMismatch: boolean
    previousReplicaProvenance: ThreadSyncEntry["runtime"]["replicaProvenance"]
  }): boolean {
    if (args.previousReplicaProvenance?.sourceKind !== "foreground_http_prefill") {
      return false
    }

    if (
      args.historyContinuity !== "contiguous" ||
      args.replicationContinuity !== "contiguous" ||
      args.hasHistoryTokenMismatch
    ) {
      return false
    }

    if (
      typeof args.resumeHistoryToken !== "string" ||
      args.resumeHistoryToken.trim().length === 0 ||
      args.nextHistoryToken !== args.resumeHistoryToken
    ) {
      return false
    }

    if (
      !Number.isFinite(args.acceptedCursor) ||
      !Number.isFinite(args.resumeCursor)
    ) {
      return false
    }

    const normalizedAcceptedCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.floor(args.acceptedCursor),
    )
    const normalizedResumeCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.floor(args.resumeCursor),
    )

    return normalizedResumeCursor >= normalizedAcceptedCursor
  }

  private clearHttpPrefillReleaseTimer(entry: ThreadSyncEntry): void {
    if (entry.httpPrefill.releaseTimer !== null) {
      clearTimeout(entry.httpPrefill.releaseTimer)
      entry.httpPrefill.releaseTimer = null
    }
  }

  private armHttpPrefillReleaseTimer(args: {
    entry: ThreadSyncEntry
    ws: WebSocket | null
    connectionVersion: number
    maxWaitMs: number
  }): void {
    const { entry, ws, connectionVersion, maxWaitMs } = args
    this.clearHttpPrefillReleaseTimer(entry)
    entry.httpPrefill.releaseTimer = setTimeout(() => {
      if (
        !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
        !this.isCurrentHttpPrefill(entry, connectionVersion)
      ) {
        return
      }
      entry.httpPrefill.releaseTimer = null
      entry.httpPrefill.status = "timed_out"
      this.releaseDeferredHandshakeIfSocketOpen({
        entry,
        connectionVersion,
        reason: entry.httpPrefill.phase === "runtime_checkpoint"
          ? "runtime_checkpoint_timeout"
          : "http_prefill_timeout",
        abortHttpPrefill: true,
      })
    }, maxWaitMs)
  }

  private resetHttpPrefillState(entry: ThreadSyncEntry, options?: {
    preserveCompletedAt?: boolean
  }): void {
    this.clearHttpPrefillReleaseTimer(entry)
    if (options?.preserveCompletedAt !== true) {
      clearSessionSurfaceRefreshCooldownTimer(entry)
    }
    entry.httpPrefill.abortController?.abort()
    entry.httpPrefill.mode = null
    entry.httpPrefill.phase = null
    entry.httpPrefill.connectionVersion = null
    entry.httpPrefill.abortController = null
    entry.httpPrefill.status = "idle"
    entry.httpPrefill.maxPages = 0
    entry.httpPrefill.maxTotalBytes = 0
    entry.httpPrefill.maxWaitMs = 0
    entry.httpPrefill.targetCursor = null
    entry.httpPrefill.pageCount = 0
    entry.httpPrefill.totalBytes = 0
    entry.httpPrefill.lastError = null
    if (options?.preserveCompletedAt !== true) {
      entry.lastHttpPrefillCompletedAt = null
    }
    entry.transport.deferInitialHandshake = false
  }

  private hasStartedTransportWork(entry: ThreadSyncEntry): boolean {
    return entry.transport.ws !== null ||
      entry.transport.connectionVersion > 0 ||
      entry.transport.helloSent === true ||
      entry.transport.deferInitialHandshake === true ||
      entry.transport.reconnectTimer !== null ||
      entry.transport.keepaliveTimer !== null ||
      entry.transport.status !== "disconnected"
  }

  private isCurrentHttpPrefill(entry: ThreadSyncEntry, connectionVersion: number): boolean {
    return entry.httpPrefill.connectionVersion === connectionVersion && entry.httpPrefill.status === "inflight"
  }

  private isCurrentHttpPrefillTransportGeneration(args: {
    entry: ThreadSyncEntry
    ws: WebSocket | null
    connectionVersion: number
  }): boolean {
    const { entry, ws, connectionVersion } = args
    if (ws) {
      return this.isCurrentConnection(entry, ws, connectionVersion)
    }

    return (
      this.activeThreadId === entry.sessionId &&
      entry.connectionRole === "foreground_active" &&
      entry.transport.connectionVersion === connectionVersion
    )
  }

  private buildHttpPrefillPlan(entry: ThreadSyncEntry): {
    mode: ThreadSyncHttpPrefillMode
    maxPages: number
    maxTotalBytes: number
    maxWaitMs: number
    checkpointMaxWaitMs: number | null
  } | null {
    const hasNonZeroCursor = entry.resume.cursor > 0
    const hasValidatedResumablePrefix =
      hasNonZeroCursor &&
      canResumeFromValidatedPrefixMaterialization(entry.runtime.materialization) &&
      typeof entry.resume.historyToken === "string" &&
      entry.resume.historyToken.trim().length > 0 &&
      isDisplayOnlyRuntimeMaterialization(entry.runtime.materialization) === false

    if (
      this.activeThreadId !== entry.sessionId ||
      entry.connectionRole !== "foreground_active" ||
      entry.transport.readOnlyTransport === true
    ) {
      return null
    }

    if (this.shouldProbeRuntimeCheckpointBootstrap({
      entry,
      hasValidatedResumablePrefix,
    })) {
      return {
        mode: entry.runtime.materialization === "empty"
          ? "empty_prefix_bootstrap"
          : "runtime_checkpoint_probe",
        maxPages: EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_PAGES,
        maxTotalBytes: EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_TOTAL_BYTES,
        maxWaitMs: EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_WAIT_MS,
        checkpointMaxWaitMs: RUNTIME_CHECKPOINT_BOOTSTRAP_MAX_WAIT_MS,
      }
    }

    if (
      entry.resume.cursor <= 0 ||
      canResumeFromValidatedPrefixMaterialization(entry.runtime.materialization) === false
    ) {
      return {
        mode: "empty_prefix_fallback",
        maxPages: ACTIVE_THREAD_HTTP_PREFILL_MAX_PAGES,
        maxTotalBytes: ACTIVE_THREAD_HTTP_PREFILL_MAX_TOTAL_BYTES,
        maxWaitMs: ACTIVE_THREAD_HTTP_PREFILL_MAX_WAIT_MS,
        checkpointMaxWaitMs: null,
      }
    }

    if (!hasValidatedResumablePrefix) {
      return null
    }

    return {
      mode: "validated_prefix",
      maxPages: ACTIVE_THREAD_HTTP_PREFILL_MAX_PAGES,
      maxTotalBytes: ACTIVE_THREAD_HTTP_PREFILL_MAX_TOTAL_BYTES,
      maxWaitMs: ACTIVE_THREAD_HTTP_PREFILL_MAX_WAIT_MS,
      checkpointMaxWaitMs: null,
    }
  }

  private shouldProbeRuntimeCheckpointBootstrap(args: {
    entry: ThreadSyncEntry
    hasValidatedResumablePrefix: boolean
  }): boolean {
    const { entry } = args
    if (args.hasValidatedResumablePrefix) return false
    if (this.activeThreadId !== entry.sessionId) return false
    if (entry.connectionRole !== "foreground_active") return false
    if (entry.transport.readOnlyTransport === true) return false
    if (entry.transport.connectionVersion !== 1) return false
    if (entry.runtime.optimisticSeed !== null) return false
    if (normalizeRuntimeConversationId(entry.activeConversationId) !== DEFAULT_RUNTIME_CONVERSATION_ID) return false
    if (entry.runtime.materialization === "empty") return true
    if (isDisplayOnlyRuntimeMaterialization(entry.runtime.materialization)) return false
    if (
      entry.runtime.materialization !== "authoritative" ||
      entry.runtime.replicaProvenance?.sourceKind !== "hydrated"
    ) {
      return false
    }

    const hasValidatedRuntimeQuality = entry.runtime.replicaQuality === "continuity_validated"
    const hasValidatedResumeToken =
      typeof entry.resume.historyToken === "string" &&
      entry.resume.historyToken.trim().length > 0

    return !hasValidatedRuntimeQuality || !hasValidatedResumeToken
  }

  private shouldAttemptHttpPrefill(entry: ThreadSyncEntry): boolean {
    return this.buildHttpPrefillPlan(entry) !== null
  }

  private hasValidatedResumeCursor(entry: ThreadSyncEntry): boolean {
    return (
      entry.resume.cursor > EMPTY_THREAD_REPLICA_CURSOR &&
      typeof entry.resume.historyToken === "string" &&
      entry.resume.historyToken.trim().length > 0
    )
  }

  private hasExternallyRestoredResumePrefix(entry: ThreadSyncEntry): boolean {
    if (!this.hasValidatedResumeCursor(entry)) {
      return false
    }

    if (entry.runtime.resumeSource === "runtime_checkpoint") {
      return true
    }

    return entry.runtime.replicaProvenance?.sourceKind === "hydrated"
  }

  private shouldIgnoreStaleColdHttpPrefillResponse(args: {
    entry: ThreadSyncEntry
    requestedAfter: number
    response: Pick<
      ThreadSyncHttpCatchupResponse,
      "acceptedCursor" | "historyContinuity" | "historyToken" | "tailId"
    >
    source: "http_prefill" | "terminal_cursor_catchup"
  }): boolean {
    if (args.requestedAfter > EMPTY_THREAD_REPLICA_CURSOR) {
      return false
    }

    if (!this.hasExternallyRestoredResumePrefix(args.entry)) {
      return false
    }

    const localCursor = this.getBestKnownLocalRawCursor(args.entry)
    if (localCursor === null || localCursor <= EMPTY_THREAD_REPLICA_CURSOR) {
      return false
    }

    // A cursor-0 HTTP request can be started before IndexedDB hydration wins.
    // If that response arrives after the durable replica restored a validated
    // prefix, applying it would regress the active runtime and can clear a
    // healthy compacted replica. Drop the stale response and resume from the
    // already validated local cursor instead.
    debugInfo("sync.new", "Ignored stale cold HTTP prefill response after local prefix restored", {
      sessionId: args.entry.sessionId,
      requestedAfter: args.requestedAfter,
      localCursor,
      resumeCursor: args.entry.resume.cursor,
      acceptedCursor: args.response.acceptedCursor,
      tailCursor: args.response.tailId,
      historyContinuity: args.response.historyContinuity,
      source: args.source,
    })
    return true
  }

  private shouldApplyHttpPrefillResponse(args: {
    entry: ThreadSyncEntry
    requestedAfter: number
    response: Pick<
      ThreadSyncHttpCatchupResponse,
      "acceptedCursor" | "historyContinuity" | "historyToken"
    >
  }): boolean {
    if (args.requestedAfter <= 0) {
      return true
    }

    if (args.response.historyContinuity !== "contiguous") {
      return false
    }

    if (args.response.acceptedCursor !== args.requestedAfter) {
      return false
    }

    if (
      typeof args.entry.resume.historyToken === "string" &&
      args.entry.resume.historyToken.trim().length > 0 &&
      args.response.historyToken !== args.entry.resume.historyToken
    ) {
      return false
    }

    return true
  }

  private normalizeRawCursor(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) && value > 0
      ? Math.floor(value)
      : null
  }

  private hasTerminalThreadActivityEvidence(entry: {
    lastActivityEventId?: number | null
    lastFinishedEventId?: number | null
    lastFinishedAt?: number | null
  } | null | undefined): boolean {
    if (!entry) return false
    const lastActivityEventId = this.normalizeRawCursor(entry.lastActivityEventId)
    const lastFinishedEventId = this.normalizeRawCursor(entry.lastFinishedEventId)
    if (lastActivityEventId !== null && lastFinishedEventId !== null) {
      return lastFinishedEventId >= lastActivityEventId
    }
    return lastFinishedEventId !== null && this.normalizeRawCursor(entry.lastFinishedAt) !== null
  }

  private getServerTranscriptCursorSnapshot(entry: ThreadSyncEntry): {
    targetTerminalRawCursor: number | null
    serverRawTailId: number | null
  } {
    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    const conversationSnapshot = useRuntimeConversationActivity.getState()
      .bySessionId[entry.sessionId]?.[activeConversationId]
    if (
      this.hasTerminalThreadActivityEvidence(conversationSnapshot) &&
      this.normalizeRawCursor(conversationSnapshot?.terminalRawCursor) !== null
    ) {
      return {
        targetTerminalRawCursor: this.normalizeRawCursor(conversationSnapshot?.terminalRawCursor),
        serverRawTailId: this.normalizeRawCursor(conversationSnapshot?.rawTailId),
      }
    }

    const threadEntry = useThreadRuntime.getState().entries[entry.sessionId]
    if (
      this.hasTerminalThreadActivityEvidence(threadEntry) &&
      this.normalizeRawCursor(threadEntry?.terminalRawCursor) !== null
    ) {
      return {
        targetTerminalRawCursor: this.normalizeRawCursor(threadEntry?.terminalRawCursor),
        serverRawTailId: this.normalizeRawCursor(threadEntry?.rawTailId),
      }
    }

    return {
      targetTerminalRawCursor: null,
      serverRawTailId: this.normalizeRawCursor(conversationSnapshot?.rawTailId) ?? this.normalizeRawCursor(threadEntry?.rawTailId),
    }
  }

  private getBestKnownLocalRawCursor(entry: ThreadSyncEntry): number | null {
    const activeRuntimeState = this.activeThreadId === entry.sessionId
      ? useAgentRuntime.getState()
      : null
    const candidates = [
      entry.resume.cursor,
      entry.runtime.cachedState?.lastEventId ?? null,
      entry.runtime.cachedState?.snapshot?.cursorState.providerEventId ?? null,
      activeRuntimeState?.lastEventId ?? null,
      activeRuntimeState?.snapshot?.cursorState.providerEventId ?? null,
    ]
      .map((value) => this.normalizeRawCursor(value))
      .filter((value): value is number => value !== null)

    return candidates.length > 0 ? Math.max(...candidates) : null
  }

  private deriveTranscriptFreshness(entry: ThreadSyncEntry): {
    transcriptFreshness: ThreadTranscriptFreshness
    localRawCursor: number | null
    targetTerminalRawCursor: number | null
    serverRawTailId: number | null
  } {
    const { targetTerminalRawCursor, serverRawTailId } = this.getServerTranscriptCursorSnapshot(entry)
    const localRawCursor = this.getBestKnownLocalRawCursor(entry)
    if (targetTerminalRawCursor === null) {
      return { transcriptFreshness: "unknown", localRawCursor, targetTerminalRawCursor, serverRawTailId }
    }
    if (localRawCursor !== null && localRawCursor >= targetTerminalRawCursor) {
      return { transcriptFreshness: "current", localRawCursor, targetTerminalRawCursor, serverRawTailId }
    }
    if (
      entry.resume.isCatchingUp ||
      (entry.httpPrefill.status === "inflight" && entry.httpPrefill.targetCursor === targetTerminalRawCursor)
    ) {
      return { transcriptFreshness: "catching_up", localRawCursor, targetTerminalRawCursor, serverRawTailId }
    }
    return { transcriptFreshness: "needs_catchup", localRawCursor, targetTerminalRawCursor, serverRawTailId }
  }

  private hasValidatedHydratedResume(entry: ThreadSyncEntry): boolean {
    return (
      entry.resume.cursor > 0 &&
      typeof entry.resume.historyToken === "string" &&
      entry.resume.historyToken.trim().length > 0 &&
      entry.runtime.materialization === "authoritative" &&
      entry.runtime.replicaProvenance?.sourceKind === "hydrated"
    )
  }

  private hydratedResumeCoversKnownTerminalCursor(entry: ThreadSyncEntry): boolean {
    if (!this.hasValidatedHydratedResume(entry)) {
      return false
    }
    const { targetTerminalRawCursor } = this.getServerTranscriptCursorSnapshot(entry)
    if (targetTerminalRawCursor === null) {
      return false
    }
    return entry.resume.cursor >= targetTerminalRawCursor
  }

  private cancelCurrentHttpPrefill(args: {
    entry: ThreadSyncEntry
    status: "skipped" | "timed_out" | "failed"
    reason: string
  }): void {
    const { entry } = args
    this.clearHttpPrefillReleaseTimer(entry)
    entry.httpPrefill.status = args.status
    entry.httpPrefill.abortController?.abort()
    entry.httpPrefill.abortController = null
    entry.httpPrefill.connectionVersion = null
    debugInfo("sync.new", "Cancelled foreground HTTP prefill", {
      sessionId: entry.sessionId,
      status: args.status,
      mode: entry.httpPrefill.mode,
      reason: args.reason,
      cursor: entry.resume.cursor,
      targetCursor: entry.httpPrefill.targetCursor,
    })
  }

  requestForegroundTerminalCatchup(sessionId: string, args: { targetCursor: number; reason: string }): boolean {
    const entry = this.entries.get(sessionId)
    const targetCursor = this.normalizeRawCursor(args.targetCursor)
    if (!entry || targetCursor === null) return false
    if (this.activeThreadId !== sessionId || entry.connectionRole !== "foreground_active") return false
    const localCursor = this.getBestKnownLocalRawCursor(entry) ?? 0
    if (localCursor >= targetCursor) return false
    if (localCursor <= EMPTY_THREAD_REPLICA_CURSOR && this.shouldAttemptHttpPrefill(entry)) {
      if (entry.httpPrefill.status === "inflight") {
        return true
      }
      if (!this.hasStartedTransportWork(entry)) {
        debugInfo("sync.new", "Deferred terminal-cursor bootstrap prefill until transport generation starts", {
          sessionId,
          targetCursor,
          localCursor,
          reason: args.reason,
          wsStatus: entry.transport.status,
          connectionVersion: entry.transport.connectionVersion,
        })
        return false
      }

      const ws = entry.transport.ws?.readyState === WebSocket.OPEN ? entry.transport.ws : null
      debugInfo("sync.new", "Starting empty-prefix bootstrap HTTP prefill before websocket readiness", {
        sessionId,
        targetCursor,
        localCursor,
        reason: args.reason,
        wsStatus: entry.transport.status,
        connectionVersion: entry.transport.connectionVersion,
      })
      this.beginHttpPrefill(entry, ws, entry.transport.connectionVersion, {
        targetCursor,
      })
      return true
    }
    if (entry.httpPrefill.status === "inflight") {
      return entry.httpPrefill.targetCursor === targetCursor
    }

    this.beginTerminalCursorHttpCatchup({
      entry,
      targetCursor,
      reason: args.reason,
    })
    return true
  }

  private sendInitialHandshake(args: {
    entry: ThreadSyncEntry
    ws: WebSocket
    connectionVersion: number
    reason: string
  }): boolean {
    const { connectionVersion, entry, ws } = args
    if (!this.isCurrentConnection(entry, ws, connectionVersion)) {
      return false
    }
    if (entry.transport.helloSent || ws.readyState !== WebSocket.OPEN) {
      return false
    }
    // A just-sent local outbox row may still be waiting for the initial socket
    // handshake when the user switches threads. Let the short handoff socket
    // complete only that handshake so the prompt can reach the SessionDO instead
    // of staying browser-local until the user returns to the thread.
    const canSendHandoffDrainHandshake =
      entry.connectionRole === "handoff_previous" &&
      entry.queue.pendingLocalDispatches.size > 0
    if (entry.connectionRole !== "foreground_active" && !canSendHandoffDrainHandshake) {
      return false
    }

    try {
      const handshakeCursor =
        canResumeHandshakeFromRuntimePrefix({
          entry,
          connectionVersion,
        })
          ? entry.resume.cursor
          : 0
      if (
        handshakeCursor <= 0 &&
        this.shouldDeferColdHandshakeForDurableHydration({
          entry,
          reason: args.reason,
        })
      ) {
        entry.transport.deferInitialHandshake = true
        debugInfo("sync.new", "Deferred cold sync handshake until durable hydration settles", {
          sessionId: entry.sessionId,
          reason: args.reason,
          connectionVersion,
        })
        return false
      }
      const handshake = buildHandshakeFrame({
        cursor: handshakeCursor,
        viewerActivityEnabled: entry.transport.viewerActivityEnabled,
      })
      ws.send(JSON.stringify(handshake.payload))
      entry.transport.helloSent = true
      entry.transport.deferInitialHandshake = false
      entry.resume.requestedAfter = handshake.requestedAfter
      this.flushDeferredPreHandshakeMessages({ entry, ws })
      debugInfo("sync.new", "Sent initial sync handshake", {
        sessionId: entry.sessionId,
        requestedAfter: handshake.requestedAfter,
        reason: args.reason,
      })
    } catch (error) {
      recordTransportError({
        transport: entry.transport,
        message: error instanceof Error ? error.message : "Failed to send initial sync handshake",
        nowMs: now(),
      })
      this.setConnectionStatus(entry, "error")
      try {
        ws.close(1011, "initial_handshake_failed")
      } catch {
        // Ignore close failures; the reconnect path will treat the socket as degraded.
      }
      this.emit()
      return false
    }

    this.restartKeepaliveTimer(entry)
    this.completePendingHandoffIfReady(entry)
    this.flushPendingLocalDispatches({ entry })
    this.emit()
    return true
  }

  private ensureInteractiveSendReady(args: {
    entry: ThreadSyncEntry
  }): boolean {
    const { entry } = args
    const ws = entry.transport.ws
    const connectionVersion = entry.transport.connectionVersion
    if (!ws || ws.readyState !== WebSocket.OPEN || entry.transport.helloSent === true) {
      return entry.transport.helloSent === true
    }
    if (entry.connectionRole !== "foreground_active") {
      return false
    }
    if (!Number.isFinite(connectionVersion)) {
      return false
    }

    if (entry.transport.deferInitialHandshake || entry.httpPrefill.status === "inflight") {
      entry.httpPrefill.status = "skipped"
      debugInfo("sync.new", "Releasing deferred handshake for interactive send", {
        sessionId: entry.sessionId,
        connectionVersion,
        mode: entry.httpPrefill.mode,
      })
      this.releaseDeferredHandshake({
        entry,
        ws,
        connectionVersion,
        reason: "interactive_send",
        abortHttpPrefill: true,
      })
      return Boolean(entry.transport.helloSent)
    }

    return this.sendInitialHandshake({
      entry,
      ws,
      connectionVersion,
      reason: "interactive_send_no_prefill",
    })
  }

  private releaseDeferredHandshake(args: {
    entry: ThreadSyncEntry
    ws: WebSocket
    connectionVersion: number
    reason: string
    abortHttpPrefill?: boolean
  }): void {
    if (!this.isCurrentConnection(args.entry, args.ws, args.connectionVersion)) {
      return
    }

    const abortHttpPrefill = args.abortHttpPrefill === true
    this.clearHttpPrefillReleaseTimer(args.entry)
    if (abortHttpPrefill) {
      if (args.entry.httpPrefill.status === "inflight") {
        args.entry.httpPrefill.status = "skipped"
      }
      args.entry.httpPrefill.abortController?.abort()
      args.entry.httpPrefill.abortController = null
      args.entry.httpPrefill.connectionVersion = null
    }
    args.entry.transport.deferInitialHandshake = false
    this.sendInitialHandshake(args)
  }

  private releaseDeferredHandshakeIfSocketOpen(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    reason: string
    abortHttpPrefill?: boolean
  }): boolean {
    const ws = args.entry.transport.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (args.abortHttpPrefill === true) {
        if (args.entry.httpPrefill.status === "inflight") {
          args.entry.httpPrefill.status = "skipped"
        }
        args.entry.httpPrefill.abortController?.abort()
        args.entry.httpPrefill.abortController = null
        args.entry.httpPrefill.connectionVersion = null
      }
      return false
    }

    this.releaseDeferredHandshake({
      entry: args.entry,
      ws,
      connectionVersion: args.connectionVersion,
      reason: args.reason,
      abortHttpPrefill: args.abortHttpPrefill,
    })
    return args.entry.transport.helloSent === true
  }

  private adoptHttpPrefillResumeDecision(entry: ThreadSyncEntry, response: ThreadSyncHttpCatchupResponse): void {
    entry.resume = {
      ...entry.resume,
      cursor: Math.max(entry.resume.cursor, Math.max(0, Math.floor(response.acceptedCursor))),
      tailCursor: Math.max(entry.resume.tailCursor, Math.max(0, Math.floor(response.tailId))),
      historyToken: typeof response.historyToken === "string" ? response.historyToken : null,
      isCatchingUp: response.acceptedCursor < response.tailId,
      syncReadyReceived: false,
    }
  }

  private applyHttpPrefillBaselinePage(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    page: ThreadSyncHttpBaselinePageResponse
  }): "applied" | "noop" | "error" {
    if (this.activeThreadId !== args.entry.sessionId) {
      return "noop"
    }

    const result = applyBaselinePageToRuntime({
      activeThreadId: this.activeThreadId,
      sessionId: args.entry.sessionId,
      runtime: args.entry.runtime,
      resume: args.entry.resume,
      connectionVersion: args.connectionVersion,
      frame: {
        agentType: "claude_code",
        events: args.page.events,
      },
      nowMs: now(),
    })
    args.entry.resume = applyBaselinePageMetrics({
      resume: args.entry.resume,
      frame: args.page,
    })
    if (result.status !== "error") {
      this.appendBaselineEventsToDurableReplica({
        entry: args.entry,
        events: args.page.events,
        sourceKind: "foreground_http_prefill",
      })
    }
    return result.status
  }

  private applyHttpPrefillCatchupPage(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    expectedAfter: number
    requireOpenSocket?: boolean
    source: "http_prefill" | "terminal_cursor_catchup"
    page: Pick<
      ThreadSyncHttpCatchupResponse,
      | "acceptedCursor"
      | "approxBytes"
      | "count"
      | "events"
      | "historyContinuity"
      | "historyToken"
      | "nextCursor"
      | "tailId"
    >
  }): Promise<"applied" | "noop" | "error"> {
    if (this.activeThreadId !== args.entry.sessionId) {
      return Promise.resolve("noop")
    }

    const shouldContinueHttpPrefillApply = (): boolean => (
      this.activeThreadId === args.entry.sessionId &&
      (args.requireOpenSocket === false || args.entry.transport.ws?.readyState === WebSocket.OPEN) &&
      args.entry.transport.connectionVersion === args.connectionVersion &&
      this.isCurrentHttpPrefill(args.entry, args.connectionVersion) &&
      !this.shouldIgnoreStaleColdHttpPrefillResponse({
        entry: args.entry,
        requestedAfter: args.expectedAfter,
        response: args.page,
        source: args.source,
      })
    )

    if (!shouldContinueHttpPrefillApply()) {
      return Promise.resolve("noop")
    }

    // Track the events that crossed the projection filter separately from the
    // full page so IDB persistence stays contiguous across all conversations
    // even when the projection only sees a subset.
    const appliedChunkEvents: Array<{
      id: number
      type: string
      payload: unknown
      timestamp: number
      conversationId?: string
      providerSessionId?: string
      childSessionId?: string
    }> = []
    this.storeRuntimeEventsByConversation(args.entry, args.page.events)
    const activeConversationId = normalizeRuntimeConversationId(args.entry.activeConversationId)
    const activeConversationRevision = args.entry.activeConversationRevision
    const activePageEvents = this.filterRuntimeEventsForActiveConversation(args.entry, args.page.events)
    // Determine the highest event id actually visible in the page so partial
    // durable appends can cap at the same boundary as the transport frame.
    const pageMaxEventId = args.page.events.reduce<number>(
      (max, event) => Math.max(max, Math.floor(event.id ?? 0)),
      args.expectedAfter,
    )

    return applySyncEventsToRuntimeInChunks({
      activeThreadId: this.activeThreadId,
      getCurrentActiveThreadId: () => this.activeThreadId,
      sessionId: args.entry.sessionId,
      runtime: args.entry.runtime,
      resume: args.entry.resume,
      resolvePersistResume: ({ resume, applyResultStatus, maxEventId }) => planCatchupPageHandling({
        resume,
        frame: {
          count: 1,
          approxBytes: 0,
          nextCursor: maxEventId ?? resume.cursor,
        },
        applyResultStatus,
      }),
      connectionVersion: args.connectionVersion,
      events: activePageEvents,
      nowMs: now(),
      // HTTP prefill runs behind a loading treatment, so there is no visible UI
      // responsiveness benefit from paying the structured-clone cost of sending
      // the full runtime state into and back out of the replay worker for every
      // chunk. Keep this hidden bootstrap path on the main thread to avoid the
      // quadratic memory blow-up that large cold-start histories can trigger.
      preferOffMainThread: false,
      shouldContinue: () =>
        shouldContinueHttpPrefillApply() &&
        this.isActiveConversationProjectionCurrent(args.entry, {
          conversationId: activeConversationId,
          revision: activeConversationRevision,
        }),
      onChunkApplied: ({ chunkEvents, applyResult }) => {
        this.updateResumeFromCatchupChunk({
          entry: args.entry,
          chunkEvents,
          applyResultStatus: applyResult.status,
        })
        if (applyResult.status !== "error") {
          appliedChunkEvents.push(...chunkEvents)
        }
      },
      })
      .then((applyResult) => {
        if (!this.isActiveConversationProjectionCurrent(args.entry, {
          conversationId: activeConversationId,
          revision: activeConversationRevision,
        })) {
          return "noop"
        }
        if (!applyResult.completedAllChunks) {
          if (appliedChunkEvents.length > 0) {
            if (!shouldContinueHttpPrefillApply()) {
              return applyResult.status === "error" ? "error" : "noop"
            }
            // Persist the *entire* page prefix covered by the applied chunks,
            // not just the filtered-active rows. Filtering here is what used
            // to leave sequence gaps in IDB and permanently poison the replica.
            const lastAppliedChunkEventId =
              appliedChunkEvents.at(-1)?.id ?? args.expectedAfter
            const persistableFrameEvents = args.page.events.filter(
              (event) => Math.floor(event.id ?? 0) <= lastAppliedChunkEventId,
            )
            this.appendRuntimeEventsToDurableReplica({
              entry: args.entry,
              expectedAfter: args.expectedAfter,
              events: persistableFrameEvents,
              nextCursor: lastAppliedChunkEventId,
              historyToken: args.entry.resume.historyToken,
              sourceKind: "foreground_http_prefill",
              saveCheckpoint: false,
            })
          }
          return applyResult.status === "error" ? "error" : "noop"
        }
        const appliedRuntimeCursor = args.page.nextCursor
        this.updateResumeFromCatchupFrame({
          entry: args.entry,
          frame: {
            count: args.page.count,
            approxBytes: args.page.approxBytes,
            nextCursor: appliedRuntimeCursor,
          },
          applyResultStatus: applyResult.status,
        })
        if (applyResult.status !== "error") {
          if (!shouldContinueHttpPrefillApply()) {
            return "noop"
          }
          // Persist the full page of raw events so the shared IDB lane stays
          // gap-free. `appliedRuntimeCursor` matches `page.nextCursor`, which
          // is the transport's next-after cursor for the entire frame.
          this.appendRuntimeEventsToDurableReplica({
            entry: args.entry,
            expectedAfter: args.expectedAfter,
            events: args.page.events,
            nextCursor: Math.max(appliedRuntimeCursor, pageMaxEventId),
            historyToken: args.entry.resume.historyToken,
            sourceKind: "foreground_http_prefill",
          })
        }
        // Silence unused-var lint when activePageEvents is only a projection
        // input; the durable lane above intentionally writes the full page.
        void activePageEvents
        return applyResult.status
      })
  }

  private finalizeHttpPrefillIfComplete(entry: ThreadSyncEntry): void {
    if (entry.resume.cursor < entry.resume.tailCursor) {
      return
    }

    entry.resume = planCatchupCompleteHandling({
      resume: entry.resume,
      frame: {
        finalCursor: entry.resume.cursor,
      },
    })
    if (shouldCaptureAuthoritativeRuntime({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtimeMaterialization: entry.runtime.materialization,
      isCatchingUp: entry.resume.isCatchingUp,
    })) {
      this.captureActiveRuntimeState(entry, {
        materialization: "authoritative",
      })
    }

    entry.lastHttpPrefillCompletedAt = now()
    if (shouldRefreshSessionSurfaceAfterPrefill({
      lastHttpPrefillCompletedAt: entry.lastHttpPrefillCompletedAt,
      lastSessionSurfaceRefreshAt: entry.lastSessionSurfaceRefreshAt,
      nowMs: now(),
    })) {
      this.emit()
      return
    }

    this.scheduleSessionSurfaceRefreshReevaluation(entry)
  }

  private updateResumeFromCatchupFrame(args: {
    entry: ThreadSyncEntry
    frame: {
      count: number
      approxBytes: number
      nextCursor: number
    }
    applyResultStatus: "applied" | "noop" | "error"
  }): void {
    args.entry.resume = planCatchupPageHandling({
      resume: args.entry.resume,
      frame: args.frame,
      applyResultStatus: args.applyResultStatus,
    })
  }

  private updateResumeFromCatchupChunk(args: {
    entry: ThreadSyncEntry
    chunkEvents: Array<{ id: number }>
    applyResultStatus: "applied" | "noop" | "error"
  }): void {
    const nextCursor = args.chunkEvents.at(-1)?.id ?? args.entry.resume.cursor
    this.updateResumeFromCatchupFrame({
      entry: args.entry,
      frame: {
        count: args.chunkEvents.length,
        approxBytes: 0,
        nextCursor,
      },
      applyResultStatus: args.applyResultStatus,
    })
  }

  private applyCursorOnlyCatchupProgress(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    frame: {
      count: number
      approxBytes: number
      nextCursor: number
    }
  }): void {
    if (args.frame.nextCursor <= args.entry.resume.cursor) {
      return
    }

    advanceActiveRuntimeCursor({
      nextCursor: args.frame.nextCursor,
    })
    if (args.entry.runtime.cachedState !== null) {
      args.entry.runtime.cachedState = advanceManagedRuntimeStateCursor({
        currentState: args.entry.runtime.cachedState,
        nextCursor: args.frame.nextCursor,
      })
    }
    this.updateResumeFromCatchupFrame({
      entry: args.entry,
      frame: args.frame,
      applyResultStatus: "applied",
    })
    syncActiveRuntimeStateToCache({
      activeThreadId: this.activeThreadId,
      sessionId: args.entry.sessionId,
      runtime: args.entry.runtime,
      connectionVersion: args.connectionVersion,
      resume: args.entry.resume,
      nowMs: now(),
    })
    this.advanceDurableReplicaCursor({
      entry: args.entry,
      nextCursor: args.frame.nextCursor,
      historyToken: args.entry.resume.historyToken,
    })
  }

  private resetHttpPrefillToColdReplay(args: {
    entry: ThreadSyncEntry
    reason: string
    details: Record<string, unknown>
  }): void {
    debugWarn("sync.new", args.reason, {
      sessionId: args.entry.sessionId,
      ...args.details,
    })
    const invalidatedAtMs = resetRuntimeStateAndInvalidatePersistedReplica({
      activeThreadId: this.activeThreadId,
      sessionId: args.entry.sessionId,
      runtime: args.entry.runtime,
    })
    this.clearDurableReplica(args.entry, "http_prefill_resume_rejected", {
      invalidatedAtMs: invalidatedAtMs ?? undefined,
    })
    args.entry.resume = resetResumeForColdReplay()
    args.entry.httpPrefill.status = "skipped"
  }

  private reconnectAfterCatchupApplyError(entry: ThreadSyncEntry): void {
    this.disconnect(entry.sessionId, {
      clearRuntime: false,
      removeEntry: false,
      shouldReconnect: true,
    })
  }

  private forceColdReplayReconnect(entry: ThreadSyncEntry, closeReason: string): void {
    const invalidatedAtMs = resetRuntimeStateAndInvalidatePersistedReplica({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
    })
    this.clearDurableReplica(entry, closeReason, {
      invalidatedAtMs: invalidatedAtMs ?? undefined,
    })
    entry.resume = resetResumeForColdReplay()
    this.disconnect(entry.sessionId, {
      clearRuntime: false,
      removeEntry: false,
      shouldReconnect: true,
      closeReason,
    })
    this.emit()
  }

  private async computeRuntimeCheckpointSha256Hex(bytes: ArrayBuffer): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", bytes)
    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
  }

  private async decodeRuntimeCheckpointBlobBytes(args: {
    checkpoint: NonNullable<ThreadSyncBootstrapArtifactResponse["checkpoint"]>
    storedBytes: ArrayBuffer
  }): Promise<ArrayBuffer> {
    const storageEncoding = args.checkpoint.storageEncoding ?? "identity"
    const storedByteLength = args.checkpoint.storedByteLength ?? args.checkpoint.byteLength
    const storedSha256 = (args.checkpoint.storedSha256 ?? args.checkpoint.sha256).toLowerCase()

    if (args.storedBytes.byteLength !== storedByteLength) {
      throw new Error("Runtime checkpoint stored byte length mismatch")
    }
    const actualStoredSha256 = await this.computeRuntimeCheckpointSha256Hex(args.storedBytes)
    if (actualStoredSha256 !== storedSha256) {
      throw new Error("Runtime checkpoint stored sha256 mismatch")
    }

    if (storageEncoding === "identity") return args.storedBytes
    if (storageEncoding !== "gzip") {
      throw new Error("Unsupported runtime checkpoint storage encoding")
    }
    if (typeof DecompressionStream !== "function") {
      throw new Error("Browser does not support gzip runtime checkpoint decompression")
    }
    return await new Response(
      new Blob([args.storedBytes]).stream().pipeThrough(new DecompressionStream("gzip")),
    ).arrayBuffer()
  }

  private normalizeRuntimeCheckpointState(args: {
    blob: ThreadSyncRuntimeCheckpointBlob
    browserCursor: number
  }): ManagedRuntimeState | null {
    const runtimeState = args.blob.runtimeState
    if (!runtimeState || typeof runtimeState !== "object" || Array.isArray(runtimeState)) {
      return null
    }
    const state = runtimeState as Partial<ManagedRuntimeState>
    if (state.agentType !== args.blob.agentType) return null
    const checkpointCursor = Math.max(0, Math.floor(args.blob.checkpointCursor))
    const browserCursor = Math.max(0, Math.floor(args.browserCursor))
    if (browserCursor <= 0) return null
    const lastEventId = typeof state.lastEventId === "number" && Number.isFinite(state.lastEventId)
      ? Math.floor(state.lastEventId)
      : checkpointCursor
    // The checkpoint blob's own runtime state is keyed by its checkpoint cursor
    // domain (currently sidecar source-seq for sandbox uploads). Validate that
    // invariant, but expose browser-domain cursors to ThreadSync after import so
    // later catchup/sync-ready checks continue to compare raw_event ids.
    if (lastEventId !== checkpointCursor) return null

    if (args.blob.agentType === "claude_code") {
      const projection = state.rawClaudeProjection
      if (!SESSION_RUNTIME_ADAPTERS.claude_code.isRawProjectionLike(projection)) return null
      const compactedEventCount = typeof projection.compactedEventCount === "number" && Number.isFinite(projection.compactedEventCount)
        ? Math.floor(projection.compactedEventCount)
        : 0
      if (
        projection.events.length > 0 ||
        compactedEventCount <= 0 ||
        !isCurrentClaudeDerivedProjection(projection.claudeDerived)
      ) return null
      const snapshot = buildClaudeSnapshot(projection, browserCursor)
      return {
        agentType: "claude_code",
        snapshot,
        lastAppliedTimestamp: typeof state.lastAppliedTimestamp === "number" && Number.isFinite(state.lastAppliedTimestamp)
          ? Math.floor(state.lastAppliedTimestamp)
          : null,
        lastEventId: browserCursor,
        rawOpenCodeProjection: null,
        rawClaudeProjection: projection,
        rawCursorProjection: null,
      }
    }

    if (args.blob.agentType === "opencode") {
      const projection = state.rawOpenCodeProjection
      if (!SESSION_RUNTIME_ADAPTERS.opencode.isRawProjectionLike(projection)) return null
      if (
        !projection.childSessionStateBySessionId ||
        !Array.isArray(projection.orderedMessageIds) ||
        !Array.isArray(projection.orderedChildSessionIds) ||
        typeof projection.derivedStateVersion !== "number"
      ) {
        return null
      }
      const projectionForHydrate = {
        ...projection,
        // Force the browser projector to rebuild derived timeline/usage state
        // from the canonical graph instead of trusting sidecar-derived caches.
        derivedStateVersion: -1,
      }
      return {
        agentType: "opencode",
        snapshot: SESSION_RUNTIME_ADAPTERS.opencode.buildSnapshot(projectionForHydrate, browserCursor),
        lastAppliedTimestamp: typeof state.lastAppliedTimestamp === "number" && Number.isFinite(state.lastAppliedTimestamp)
          ? Math.floor(state.lastAppliedTimestamp)
          : null,
        lastEventId: browserCursor,
        rawOpenCodeProjection: projectionForHydrate,
        rawClaudeProjection: null,
        rawCursorProjection: null,
      }
    }

    return null
  }

  private async tryApplyRuntimeCheckpointBootstrap(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    artifact: ThreadSyncBootstrapArtifactResponse
    signal: AbortSignal
    ws: WebSocket | null
  }): Promise<boolean> {
    const { artifact, connectionVersion, entry, signal, ws } = args
    if (
      artifact.artifactAvailable !== true ||
      artifact.artifactKind !== "r2_runtime_checkpoint_v1" ||
      artifact.canSeedLiveRuntime !== true ||
      artifact.canResumeFromBootstrapCursor !== true ||
      !artifact.checkpoint ||
      !artifact.blobFetchPath
    ) {
      return false
    }
    const checkpoint = artifact.checkpoint
    const bootstrapCursor = Number.isFinite(artifact.bootstrapCursor ?? NaN) &&
      (artifact.bootstrapCursor ?? 0) > 0
      ? Math.floor(artifact.bootstrapCursor ?? 0)
      : null
    if (bootstrapCursor === null) {
      return false
    }
    const expectedConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    const checkpointConversationId = normalizeRuntimeConversationId(checkpoint.conversationId)
    const expectedBlobPathname = `/api/sessions/${encodeURIComponent(entry.sessionId)}/bootstrap-artifact/blob`
    let blobUrl: URL
    try {
      blobUrl = new URL(artifact.blobFetchPath, window.location.origin)
    } catch {
      return false
    }
    if (
      checkpoint.checkpointVersion !== RUNTIME_CHECKPOINT_VERSION_V1 ||
      checkpoint.projectionVersion !== RUNTIME_CHECKPOINT_PROJECTION_VERSION_V1 ||
      (checkpoint.agentType === "opencode" && !checkpoint.providerSessionId) ||
      checkpointConversationId !== expectedConversationId ||
      checkpoint.byteLength > RUNTIME_CHECKPOINT_MAX_BYTE_LENGTH ||
      checkpoint.checkpointCursor <= 0 ||
      !/^[0-9a-f]{64}$/i.test(checkpoint.sha256) ||
      blobUrl.pathname !== expectedBlobPathname ||
      blobUrl.searchParams.get("agentType") !== checkpoint.agentType ||
      normalizeRuntimeConversationId(blobUrl.searchParams.get("conversationId")) !== checkpointConversationId ||
      (checkpoint.providerSessionId ? blobUrl.searchParams.get("providerSessionId") !== checkpoint.providerSessionId : false) ||
      blobUrl.searchParams.get("cursor") !== String(checkpoint.checkpointCursor) ||
      blobUrl.searchParams.get("r2Key") !== checkpoint.r2Key ||
      blobUrl.searchParams.get("byteLength") !== String(checkpoint.byteLength) ||
      blobUrl.searchParams.get("sha256")?.toLowerCase() !== checkpoint.sha256.toLowerCase() ||
      blobUrl.searchParams.get("storageEncoding") !== (checkpoint.storageEncoding ?? "identity") ||
      blobUrl.searchParams.get("storedByteLength") !== String(checkpoint.storedByteLength ?? checkpoint.byteLength) ||
      blobUrl.searchParams.get("storedSha256")?.toLowerCase() !== (checkpoint.storedSha256 ?? checkpoint.sha256).toLowerCase()
    ) {
      return false
    }
    const incomingReplicaSummary = buildThreadSyncReplicaSummary({
      resumeCursor: bootstrapCursor,
      snapshot: null,
      quality: "continuity_validated",
      savedAtMs: checkpoint.createdAtMs,
      provenance: buildThreadSyncReplicaProvenance({
        sourceKind: "foreground_http_prefill",
        sourceTabId: getForegroundOwnershipTabId(),
      }),
    })
    const currentReplicaSummary = this.buildEntryReplicaSummary(entry)
    if (!shouldRuntimeCheckpointReplaceCurrentReplica({
      current: currentReplicaSummary,
      incoming: incomingReplicaSummary,
    })) {
      debugInfo("sync.checkpoint", "Skipped R2 runtime checkpoint because current replica is stronger", {
        sessionId: entry.sessionId,
        checkpointCursor: checkpoint.checkpointCursor,
        currentCursor: currentReplicaSummary?.cursor ?? null,
        currentQuality: currentReplicaSummary?.quality ?? null,
        currentSource: currentReplicaSummary?.provenance?.sourceKind ?? null,
      })
      return false
    }
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    // NOTE: hydration fencing (bumping `durableReplicaHydration.generation`)
    // is intentionally deferred until *after* the checkpoint blob has been
    // fetched, decoded, hash-verified, parsed, and metadata-validated — see
    // just before `enqueueDurableReplicaWrite` below. If we fenced here and
    // any of those steps failed (bad blob, hash mismatch, metadata drift,
    // network error), we would have already discarded a perfectly valid
    // local IDB hydration result and pushed restore toward cold replay.
    const storageEncoding = checkpoint.storageEncoding ?? "identity"
    const needsServerIdentityFallback = storageEncoding === "gzip" && typeof DecompressionStream !== "function"
    const blobFetchPath = needsServerIdentityFallback
      ? (() => {
          const fallbackUrl = new URL(artifact.blobFetchPath ?? "", window.location.origin)
          fallbackUrl.searchParams.set("encoding", "identity")
          return `${fallbackUrl.pathname}${fallbackUrl.search}`
        })()
      : artifact.blobFetchPath
    const storedBytes = await fetchThreadSyncBootstrapArtifactBlob({
      blobFetchPath,
      signal,
    })
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    const bytes = needsServerIdentityFallback
      ? storedBytes
      : await this.decodeRuntimeCheckpointBlobBytes({
          checkpoint,
          storedBytes,
        })
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    if (bytes.byteLength !== checkpoint.byteLength) {
      throw new Error("Runtime checkpoint byte length mismatch")
    }
    const actualSha256 = await this.computeRuntimeCheckpointSha256Hex(bytes)
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    if (actualSha256 !== checkpoint.sha256.toLowerCase()) {
      throw new Error("Runtime checkpoint sha256 mismatch")
    }
    const blob = JSON.parse(new TextDecoder().decode(bytes)) as ThreadSyncRuntimeCheckpointBlob
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    if (
      blob.checkpointVersion !== checkpoint.checkpointVersion ||
      blob.projectionVersion !== checkpoint.projectionVersion ||
      blob.sessionId !== entry.sessionId ||
      blob.agentType !== checkpoint.agentType ||
      (checkpoint.agentType === "opencode" && !blob.providerSessionId) ||
      normalizeRuntimeConversationId(blob.conversationId) !== checkpointConversationId ||
      (checkpoint.providerSessionId ? blob.providerSessionId !== checkpoint.providerSessionId : false) ||
      blob.checkpointCursor !== checkpoint.checkpointCursor ||
      (blob.browserHistoryEpoch !== 0 && blob.browserHistoryEpoch !== checkpoint.browserHistoryEpoch) ||
      (blob.historyToken !== null && blob.historyToken !== checkpoint.historyToken) ||
      blob.journalEpoch !== checkpoint.journalEpoch
    ) {
      throw new Error("Runtime checkpoint metadata mismatch")
    }
    const runtimeState = this.normalizeRuntimeCheckpointState({
      blob,
      browserCursor: bootstrapCursor,
    })
    if (!runtimeState || !runtimeState.snapshot) {
      throw new Error("Runtime checkpoint state is not hydratable")
    }
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    const appliedAtMs = now()
    const checkpointProvenance = buildThreadSyncReplicaProvenance({
      sourceKind: "foreground_http_prefill",
      sourceTabId: getForegroundOwnershipTabId(),
    })
    // All blob fetch/decode/hash/metadata validation above has succeeded and
    // we are about to enqueue the durable import. Fence any in-flight IDB
    // hydration now — a late `reset_required/integrity_gap` result for the
    // pre-bootstrap generation must not clear this freshly imported
    // checkpoint. (Doing this earlier would prematurely discard valid local
    // hydration if blob fetch/validation failed above.)
    entry.durableReplicaHydration = {
      generation: entry.durableReplicaHydration.generation + 1,
      inFlight: false,
      shouldDeferColdHandshake: false,
    }
    // Capture the conversation id the checkpoint is bound to *before* the
    // durable write is enqueued. `entry.activeConversationId` is mutable and
    // can change while this write waits its turn in the queue; reading it
    // inside the queued closure would allow an old checkpoint to be imported
    // under a newer conversation tab.
    const importConversationId = checkpointConversationId
    const importedReplica = await this.enqueueDurableReplicaWrite(entry, async () => {
      // Re-validate context from inside the queued operation: abort, socket
      // identity, and the bound conversation id must all still be current.
      // Treating these as `stale_writer` lets the outer code bail without
      // mutating `entry.runtime.*` with stale checkpoint data.
      if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) {
        return { status: "stale_writer" as const }
      }
      if (normalizeRuntimeConversationId(entry.activeConversationId) !== importConversationId) {
        return { status: "stale_writer" as const }
      }
      return importThreadReplicaRuntimeCheckpoint({
        ...buildThreadReplicaScope(entry.sessionId),
        cursor: bootstrapCursor,
        historyToken: checkpoint.historyToken,
        runtimeState,
        createdAtMs: appliedAtMs,
        conversationId: importConversationId,
      })
    })
    if (signal.aborted || !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) return false
    // If the queued write bailed because context drifted (tab switch, socket
    // change, abort) while we were queued, treat the whole bootstrap as
    // unapplied rather than mutating `entry.runtime.*` with a checkpoint
    // that no longer corresponds to the active conversation/connection.
    if (
      importedReplica.status !== "imported" &&
      (signal.aborted ||
        !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
        normalizeRuntimeConversationId(entry.activeConversationId) !== importConversationId)
    ) {
      return false
    }

    entry.runtime.cachedState = runtimeState
    entry.runtime.materialization = "authoritative"
    entry.runtime.connectionVersion = connectionVersion
    entry.runtime.continuityValidatedConnectionVersion = connectionVersion
    entry.runtime.replicaQuality = "continuity_validated"
    entry.runtime.replicaSavedAtMs = appliedAtMs
    entry.runtime.replicaProvenance = checkpointProvenance
    entry.runtime.renderSource = "runtime_checkpoint"
    entry.runtime.resumeSource = "runtime_checkpoint"
    entry.resume = {
      ...entry.resume,
      cursor: bootstrapCursor,
      tailCursor: Math.max(entry.resume.tailCursor, artifact.tailId, bootstrapCursor),
      historyToken: checkpoint.historyToken,
      isCatchingUp: bootstrapCursor < artifact.tailId,
      syncReadyReceived: false,
    }
    if (importedReplica.status === "imported") {
      entry.durableReplicaEpoch = importedReplica.meta.replicaEpoch
      upsertThreadSyncPersistedReplicaSummary({
        sessionId: entry.sessionId,
        agentType: runtimeState.agentType,
        historyToken: checkpoint.historyToken,
        storageBackend: "durable_replica",
        ...buildThreadSyncReplicaSummary({
          resumeCursor: bootstrapCursor,
          snapshot: runtimeState.snapshot,
          quality: "continuity_validated",
          savedAtMs: appliedAtMs,
          provenance: checkpointProvenance,
        }),
      })
    } else {
      debugWarn("sync.checkpoint", "Failed to import R2 runtime checkpoint into durable replica", {
        sessionId: entry.sessionId,
        status: importedReplica.status,
      })
    }
    if (this.activeThreadId === entry.sessionId) {
      restoreThreadSyncRuntimeState(entry.runtime)
    }
    entry.httpPrefill.pageCount = 0
    entry.httpPrefill.totalBytes = checkpoint.byteLength
    entry.httpPrefill.status = "applied"
    debugInfo("sync.checkpoint", "Applied R2 runtime checkpoint bootstrap", {
      sessionId: entry.sessionId,
      agentType: checkpoint.agentType,
      checkpointCursor: checkpoint.checkpointCursor,
      bootstrapCursor,
      byteLength: checkpoint.byteLength,
      storageEncoding: checkpoint.storageEncoding ?? "identity",
      storedByteLength: checkpoint.storedByteLength ?? checkpoint.byteLength,
      sha256Prefix: checkpoint.sha256.slice(0, 12),
      tailId: artifact.tailId,
      resumedAfter: bootstrapCursor,
    })
    this.emit()
    return true
  }

  private async runHttpPrefill(args: {
    entry: ThreadSyncEntry
    ws: WebSocket | null
    connectionVersion: number
  }): Promise<void> {
    const { connectionVersion, entry, ws } = args
    const signal = entry.httpPrefill.abortController?.signal
    if (!signal) {
      return
    }

    try {
      const requestedAfter = this.resolveHttpPrefillRequestedAfter(entry)
      if (
        entry.httpPrefill.mode === "empty_prefix_bootstrap" ||
        entry.httpPrefill.mode === "runtime_checkpoint_probe"
      ) {
        try {
          const artifact = await fetchThreadSyncBootstrapArtifact({
            sessionId: entry.sessionId,
            conversationId: normalizeRuntimeConversationId(entry.activeConversationId),
            signal,
          })
          debugInfo("sync.checkpoint", "Checked R2 runtime checkpoint bootstrap artifact", {
            sessionId: entry.sessionId,
            eligible: artifact.eligible,
            artifactAvailable: artifact.artifactAvailable,
            artifactKind: artifact.artifactKind,
            artifactUnavailableReason: artifact.artifactUnavailableReason,
            bootstrapCursor: artifact.bootstrapCursor,
            rawEventCount: artifact.rawEventCount,
            tailId: artifact.tailId,
          })
          if (artifact.artifactAvailable === true) {
            entry.httpPrefill.phase = "runtime_checkpoint"
            this.armHttpPrefillReleaseTimer({
              entry,
              ws,
              connectionVersion,
              maxWaitMs: RUNTIME_CHECKPOINT_BOOTSTRAP_MAX_WAIT_MS,
            })
          }
          const appliedCheckpoint = await this.tryApplyRuntimeCheckpointBootstrap({
            entry,
            connectionVersion,
            artifact,
            signal,
            ws,
          })
          if (appliedCheckpoint) {
            return
          }
        } catch (error) {
          if (signal.aborted) return
          debugWarn("sync.checkpoint", "Failed to apply R2 runtime checkpoint bootstrap; falling back to HTTP prefill", {
            sessionId: entry.sessionId,
            error: error instanceof Error ? error.message : "runtime checkpoint bootstrap failed",
          })
        }
        if (
          !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
          !this.isCurrentHttpPrefill(entry, connectionVersion)
        ) {
          return
        }
        entry.httpPrefill.phase = "catchup_prefill"
        this.armHttpPrefillReleaseTimer({
          entry,
          ws,
          connectionVersion,
          maxWaitMs: entry.httpPrefill.maxWaitMs,
        })
      }
      let catchupResponse = await fetchThreadSyncCatchupPage({
        sessionId: entry.sessionId,
        after: requestedAfter,
        signal,
      })

      if (
        !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
        !this.isCurrentHttpPrefill(entry, connectionVersion)
      ) {
        return
      }

      if (!this.shouldAttemptHttpPrefill(entry)) {
        entry.httpPrefill.status = "skipped"
        return
      }

      if (this.shouldIgnoreStaleColdHttpPrefillResponse({
        entry,
        requestedAfter,
        response: catchupResponse,
        source: "http_prefill",
      })) {
        entry.httpPrefill.status = "skipped"
        return
      }

      if (!this.shouldApplyHttpPrefillResponse({
        entry,
        requestedAfter,
        response: catchupResponse,
      })) {
        entry.httpPrefill.status = "skipped"
        debugInfo("sync.new", "Skipped HTTP thread prefill because resume contract rejected the local prefix", {
          sessionId: entry.sessionId,
          requestedAfter,
          acceptedCursor: catchupResponse.acceptedCursor,
          historyContinuity: catchupResponse.historyContinuity,
          previousHistoryToken: entry.resume.historyToken,
          nextHistoryToken: catchupResponse.historyToken,
        })
        return
      }

      entry.httpPrefill.pageCount = 1
      entry.httpPrefill.totalBytes = catchupResponse.approxBytes

      // The first catchup response can legally carry an empty baseline page when
      // that page only contained ignored rows. In that case baselineHasMore /
      // baselineNextSeq still describe additional baseline work that must be
      // fetched before we resume from the runtime cursor.
      let nextBaselineSeq = catchupResponse.baselineHasMore === true
        ? Math.max(0, Math.floor(catchupResponse.baselineNextSeq ?? 0))
        : null
      if ((catchupResponse.baselineEvents?.length ?? 0) > 0) {
        const firstBaselineStatus = this.applyHttpPrefillBaselinePage({
          entry,
          connectionVersion,
          page: {
            events: catchupResponse.baselineEvents ?? [],
            nextBaselineSeq: Math.max(0, Math.floor(catchupResponse.baselineNextSeq ?? 0)),
            hasMore: catchupResponse.baselineHasMore === true,
            count: catchupResponse.baselineEvents?.length ?? 0,
            approxBytes: catchupResponse.approxBytes,
          },
        })
        if (firstBaselineStatus === "error") {
          entry.httpPrefill.status = "failed"
          entry.httpPrefill.lastError = "Failed to apply HTTP prefill baseline page"
          return
        }
      }

      while (
        nextBaselineSeq !== null &&
        !signal.aborted &&
        this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) &&
        this.isCurrentHttpPrefill(entry, connectionVersion) &&
        entry.httpPrefill.pageCount < entry.httpPrefill.maxPages &&
        entry.httpPrefill.totalBytes < entry.httpPrefill.maxTotalBytes
      ) {
        const baselinePage = await fetchThreadSyncBaselinePage({
          sessionId: entry.sessionId,
          afterBaselineSeq: nextBaselineSeq,
          signal,
        })

        if (
          !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
          !this.isCurrentHttpPrefill(entry, connectionVersion)
        ) {
          return
        }

        if (this.shouldIgnoreStaleColdHttpPrefillResponse({
          entry,
          requestedAfter,
          response: catchupResponse,
          source: "http_prefill",
        })) {
          entry.httpPrefill.status = "skipped"
          return
        }

        entry.httpPrefill.pageCount += 1
        entry.httpPrefill.totalBytes += baselinePage.approxBytes

        const baselineStatus = this.applyHttpPrefillBaselinePage({
          entry,
          connectionVersion,
          page: baselinePage,
        })
        if (baselineStatus === "error") {
          entry.httpPrefill.status = "failed"
          entry.httpPrefill.lastError = "Failed to apply HTTP prefill baseline page"
          return
        }

        nextBaselineSeq = baselinePage.hasMore ? baselinePage.nextBaselineSeq : null
      }

      const baselineCompleted = nextBaselineSeq === null

      if (!baselineCompleted) {
        entry.httpPrefill.status = signal.aborted ? "timed_out" : "skipped"
        return
      }

      if (this.shouldIgnoreStaleColdHttpPrefillResponse({
        entry,
        requestedAfter,
        response: catchupResponse,
        source: "http_prefill",
      })) {
        entry.httpPrefill.status = "skipped"
        return
      }

      this.adoptHttpPrefillResumeDecision(entry, catchupResponse)

      if (catchupResponse.events.length > 0) {
        const firstCatchupStatus = await this.applyHttpPrefillCatchupPage({
          entry,
          connectionVersion,
          expectedAfter: requestedAfter,
          requireOpenSocket: ws === null ? false : undefined,
          source: "http_prefill",
          page: catchupResponse,
        })
        if (this.shouldIgnoreStaleColdHttpPrefillResponse({
          entry,
          requestedAfter,
          response: catchupResponse,
          source: "http_prefill",
        })) {
          entry.httpPrefill.status = "skipped"
          return
        }
        if (firstCatchupStatus === "error") {
          entry.httpPrefill.status = "failed"
          entry.httpPrefill.lastError = "Failed to apply HTTP prefill catchup page"
          return
        }
      } else if (catchupResponse.nextCursor > entry.resume.cursor) {
        this.applyCursorOnlyCatchupProgress({
          entry,
          frame: {
            count: catchupResponse.count,
            approxBytes: catchupResponse.approxBytes,
            nextCursor: catchupResponse.nextCursor,
          },
          connectionVersion,
        })
      }

      while (
        catchupResponse.hasMore &&
        !signal.aborted &&
        this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) &&
        this.isCurrentHttpPrefill(entry, connectionVersion) &&
        entry.httpPrefill.pageCount < entry.httpPrefill.maxPages &&
        entry.httpPrefill.totalBytes < entry.httpPrefill.maxTotalBytes
      ) {
        const requestedAfter = this.resolveHttpPrefillRequestedAfter(entry)
        catchupResponse = await fetchThreadSyncCatchupPage({
          sessionId: entry.sessionId,
          after: requestedAfter,
          signal,
        })

        if (
          !this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion }) ||
          !this.isCurrentHttpPrefill(entry, connectionVersion)
        ) {
          return
        }

        if (this.shouldIgnoreStaleColdHttpPrefillResponse({
          entry,
          requestedAfter,
          response: catchupResponse,
          source: "http_prefill",
        })) {
          continue
        }

        if (
          !this.shouldApplyHttpPrefillResponse({
            entry,
            requestedAfter,
            response: catchupResponse,
          }) ||
          (
            requestedAfter > 0 &&
            (catchupResponse.baselineEvents?.length ?? 0) > 0
          )
        ) {
          this.resetHttpPrefillToColdReplay({
            entry,
            reason: "Rejected HTTP thread prefill continuation because resume contract drifted mid-sequence",
            details: {
              requestedAfter,
              acceptedCursor: catchupResponse.acceptedCursor,
              historyContinuity: catchupResponse.historyContinuity,
              previousHistoryToken: entry.resume.historyToken,
              nextHistoryToken: catchupResponse.historyToken,
              baselineEventCount: catchupResponse.baselineEvents?.length ?? 0,
            },
          })
          return
        }

        entry.httpPrefill.pageCount += 1
        entry.httpPrefill.totalBytes += catchupResponse.approxBytes

        const previousCursor = entry.resume.cursor
        if (catchupResponse.events.length === 0) {
          if (catchupResponse.nextCursor > previousCursor) {
            this.applyCursorOnlyCatchupProgress({
              entry,
              frame: {
                count: catchupResponse.count,
                approxBytes: catchupResponse.approxBytes,
                nextCursor: catchupResponse.nextCursor,
              },
              connectionVersion,
            })
            continue
          }

          break
        }

        const catchupStatus = await this.applyHttpPrefillCatchupPage({
          entry,
          connectionVersion,
          expectedAfter: requestedAfter,
          requireOpenSocket: ws === null ? false : undefined,
          source: "http_prefill",
          page: catchupResponse,
        })
        if (this.shouldIgnoreStaleColdHttpPrefillResponse({
          entry,
          requestedAfter,
          response: catchupResponse,
          source: "http_prefill",
        })) {
          entry.httpPrefill.status = "skipped"
          return
        }
        if (catchupStatus === "error") {
          entry.httpPrefill.status = "failed"
          entry.httpPrefill.lastError = "Failed to apply HTTP prefill catchup page"
          return
        }
      }

      if (!this.isCurrentHttpPrefill(entry, connectionVersion) || signal.aborted) {
        return
      }

      this.finalizeHttpPrefillIfComplete(entry)
      entry.httpPrefill.status = "applied"
      debugInfo("sync.new", "HTTP thread prefill completed", {
        sessionId: entry.sessionId,
        pageCount: entry.httpPrefill.pageCount,
        totalBytes: entry.httpPrefill.totalBytes,
        cursor: entry.resume.cursor,
        tailCursor: entry.resume.tailCursor,
      })
    } catch (error) {
      if (signal.aborted) {
        if (
          entry.httpPrefill.status === "inflight" &&
          entry.httpPrefill.connectionVersion === connectionVersion &&
          entry.httpPrefill.abortController?.signal === signal
        ) {
          entry.httpPrefill.status = "timed_out"
        }
        return
      }

      entry.httpPrefill.status = "failed"
      entry.httpPrefill.lastError = error instanceof Error ? error.message : "HTTP thread prefill failed"
      debugWarn("sync.new", "HTTP thread prefill failed", {
        sessionId: entry.sessionId,
        error: entry.httpPrefill.lastError,
      })
    } finally {
      if (this.isCurrentHttpPrefillTransportGeneration({ entry, ws, connectionVersion })) {
        this.releaseDeferredHandshakeIfSocketOpen({
          entry,
          connectionVersion,
          reason: entry.httpPrefill.status === "applied"
            ? "http_prefill_complete"
            : entry.httpPrefill.status === "timed_out"
              ? "http_prefill_timeout"
              : entry.httpPrefill.status === "skipped"
                ? "http_prefill_skipped"
                : "http_prefill_failed",
        })
      }
    }
  }

  private beginTerminalCursorHttpCatchup(args: {
    entry: ThreadSyncEntry
    targetCursor: number
    reason: string
  }): void {
    const { entry, targetCursor } = args
    const connectionVersion = entry.transport.connectionVersion
    this.resetHttpPrefillState(entry, { preserveCompletedAt: true })
    this.suspendBackgroundWarmForForegroundStress()
    entry.transport.deferInitialHandshake = true
    entry.runtime.replicaProvenance = buildThreadSyncReplicaProvenance({
      sourceKind: "foreground_http_prefill",
      sourceTabId: getForegroundOwnershipTabId(),
    })
    entry.httpPrefill.mode = "terminal_cursor_catchup"
    entry.httpPrefill.phase = "catchup_prefill"
    entry.httpPrefill.connectionVersion = connectionVersion
    entry.httpPrefill.status = "inflight"
    entry.httpPrefill.maxPages = EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_PAGES
    entry.httpPrefill.maxTotalBytes = EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_TOTAL_BYTES
    entry.httpPrefill.maxWaitMs = EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_WAIT_MS
    entry.httpPrefill.targetCursor = targetCursor
    const abortController = new AbortController()
    entry.httpPrefill.abortController = abortController

    debugInfo("sync.new", "Starting terminal-cursor HTTP catchup before websocket readiness", {
      sessionId: entry.sessionId,
      connectionVersion,
      targetCursor,
      localCursor: this.getBestKnownLocalRawCursor(entry),
      reason: args.reason,
      wsStatus: entry.transport.status,
    })

    const timeout = setTimeout(() => {
      if (!this.isCurrentHttpPrefill(entry, connectionVersion)) return
      entry.httpPrefill.status = "timed_out"
      abortController.abort()
      this.emit()
    }, entry.httpPrefill.maxWaitMs)

    void this.runTerminalCursorHttpCatchup({
      entry,
      connectionVersion,
      targetCursor,
    }).finally(() => {
      clearTimeout(timeout)
      if (
        entry.httpPrefill.connectionVersion === connectionVersion &&
        entry.httpPrefill.abortController === abortController
      ) {
        entry.httpPrefill.connectionVersion = null
        entry.httpPrefill.abortController = null
      }
      const ws = entry.transport.ws
      if (
        ws?.readyState === WebSocket.OPEN &&
        this.isCurrentConnection(entry, ws, connectionVersion) &&
        entry.transport.helloSent === false
      ) {
        entry.transport.deferInitialHandshake = false
        this.sendInitialHandshake({
          entry,
          ws,
          connectionVersion,
          reason: entry.httpPrefill.status === "applied"
            ? "terminal_cursor_http_catchup_complete"
            : entry.httpPrefill.status === "timed_out"
              ? "terminal_cursor_http_catchup_timeout"
              : "terminal_cursor_http_catchup_finished",
        })
      }
      this.emit()
    })
    this.emit()
  }

  private async runTerminalCursorHttpCatchup(args: {
    entry: ThreadSyncEntry
    connectionVersion: number
    targetCursor: number
  }): Promise<void> {
    const { entry, connectionVersion, targetCursor } = args
    const signal = entry.httpPrefill.abortController?.signal
    if (!signal) return

    try {
      while (
        !signal.aborted &&
        this.activeThreadId === entry.sessionId &&
        this.isCurrentHttpPrefill(entry, connectionVersion) &&
        entry.resume.cursor < targetCursor &&
        entry.httpPrefill.pageCount < entry.httpPrefill.maxPages &&
        entry.httpPrefill.totalBytes < entry.httpPrefill.maxTotalBytes
      ) {
        const requestedAfter = this.resolveHttpPrefillRequestedAfter(entry)
        const response = await fetchThreadSyncCatchupPage({
          sessionId: entry.sessionId,
          after: requestedAfter,
          signal,
        })
        if (!this.isCurrentHttpPrefill(entry, connectionVersion) || signal.aborted) return
        if (this.shouldIgnoreStaleColdHttpPrefillResponse({
          entry,
          requestedAfter,
          response,
          source: "terminal_cursor_catchup",
        })) {
          entry.httpPrefill.status = "skipped"
          return
        }
        if (!this.shouldApplyHttpPrefillResponse({ entry, requestedAfter, response })) {
          this.resetHttpPrefillToColdReplay({
            entry,
            reason: "Rejected terminal-cursor HTTP catchup because resume contract rejected the local prefix",
            details: {
              requestedAfter,
              acceptedCursor: response.acceptedCursor,
              historyContinuity: response.historyContinuity,
              previousHistoryToken: entry.resume.historyToken,
              nextHistoryToken: response.historyToken,
              targetCursor,
            },
          })
          return
        }

        entry.httpPrefill.pageCount += 1
        entry.httpPrefill.totalBytes += response.approxBytes
        this.adoptHttpPrefillResumeDecision(entry, response)

        if ((response.baselineEvents?.length ?? 0) > 0) {
          const baselineStatus = this.applyHttpPrefillBaselinePage({
            entry,
            connectionVersion,
            page: {
              events: response.baselineEvents ?? [],
              nextBaselineSeq: Math.max(0, Math.floor(response.baselineNextSeq ?? 0)),
              hasMore: response.baselineHasMore === true,
              count: response.baselineEvents?.length ?? 0,
              approxBytes: response.approxBytes,
            },
          })
          if (baselineStatus === "error") {
            entry.httpPrefill.status = "failed"
            entry.httpPrefill.lastError = "Failed to apply terminal-cursor HTTP baseline page"
            return
          }
        }

        if (response.events.length > 0) {
          const catchupStatus = await this.applyHttpPrefillCatchupPage({
            entry,
            connectionVersion,
            expectedAfter: requestedAfter,
            requireOpenSocket: false,
            source: "terminal_cursor_catchup",
            page: response,
          })
          if (this.shouldIgnoreStaleColdHttpPrefillResponse({
            entry,
            requestedAfter,
            response,
            source: "terminal_cursor_catchup",
          })) {
            entry.httpPrefill.status = "skipped"
            return
          }
          if (catchupStatus === "error") {
            entry.httpPrefill.status = "failed"
            entry.httpPrefill.lastError = "Failed to apply terminal-cursor HTTP catchup page"
            return
          }
        } else if (response.nextCursor > entry.resume.cursor) {
          this.applyCursorOnlyCatchupProgress({
            entry,
            frame: {
              count: response.count,
              approxBytes: response.approxBytes,
              nextCursor: response.nextCursor,
            },
            connectionVersion,
          })
        }

        if (!response.hasMore && entry.resume.cursor < targetCursor) {
          break
        }
      }

      if (!this.isCurrentHttpPrefill(entry, connectionVersion) || signal.aborted) return
      this.finalizeHttpPrefillIfComplete(entry)
      entry.httpPrefill.status = entry.resume.cursor >= targetCursor ? "applied" : "skipped"
      debugInfo("sync.new", "Terminal-cursor HTTP catchup finished", {
        sessionId: entry.sessionId,
        targetCursor,
        cursor: entry.resume.cursor,
        status: entry.httpPrefill.status,
        pageCount: entry.httpPrefill.pageCount,
        totalBytes: entry.httpPrefill.totalBytes,
      })
    } catch (error) {
      if (signal.aborted) return
      entry.httpPrefill.status = "failed"
      entry.httpPrefill.lastError = error instanceof Error ? error.message : "Terminal-cursor HTTP catchup failed"
      debugWarn("sync.new", "Terminal-cursor HTTP catchup failed", {
        sessionId: entry.sessionId,
        targetCursor,
        error: entry.httpPrefill.lastError,
      })
    }
  }

  private beginHttpPrefill(
    entry: ThreadSyncEntry,
    ws: WebSocket | null,
    connectionVersion: number,
    options?: { targetCursor?: number | null },
  ): void {
    const plan = this.buildHttpPrefillPlan(entry)
    if (!plan) {
      if (ws) {
        this.sendInitialHandshake({
          entry,
          ws,
          connectionVersion,
          reason: "http_prefill_no_longer_eligible",
        })
      }
      return
    }

    this.resetHttpPrefillState(entry)
    this.suspendBackgroundWarmForForegroundStress()
    entry.transport.deferInitialHandshake = true
    entry.runtime.replicaProvenance = buildThreadSyncReplicaProvenance({
      sourceKind: "foreground_http_prefill",
      sourceTabId: getForegroundOwnershipTabId(),
    })
    entry.httpPrefill.mode = plan.mode
    entry.httpPrefill.phase = "catchup_prefill"
    entry.httpPrefill.connectionVersion = connectionVersion
    entry.httpPrefill.status = "inflight"
    entry.httpPrefill.maxPages = plan.maxPages
    entry.httpPrefill.maxTotalBytes = plan.maxTotalBytes
    entry.httpPrefill.maxWaitMs = plan.maxWaitMs
    entry.httpPrefill.targetCursor = options?.targetCursor ?? null
    const prefillAbortController = new AbortController()
    entry.httpPrefill.abortController = prefillAbortController
    this.armHttpPrefillReleaseTimer({
      entry,
      ws,
      connectionVersion,
      maxWaitMs: plan.checkpointMaxWaitMs ?? entry.httpPrefill.maxWaitMs,
    })

    debugInfo("sync.new", ws
      ? "Starting HTTP thread prefill before websocket hello"
      : "Starting HTTP thread prefill before websocket open", {
      sessionId: entry.sessionId,
      connectionVersion,
      mode: entry.httpPrefill.mode,
      maxPages: entry.httpPrefill.maxPages,
      maxBytes: entry.httpPrefill.maxTotalBytes,
      maxWaitMs: entry.httpPrefill.maxWaitMs,
      phase: entry.httpPrefill.phase,
      checkpointMaxWaitMs: plan.checkpointMaxWaitMs,
    })

    void this.runHttpPrefill({
      entry,
      ws,
      connectionVersion,
    }).finally(() => {
      if (
        entry.httpPrefill.connectionVersion === connectionVersion &&
        entry.httpPrefill.abortController === prefillAbortController
      ) {
        entry.httpPrefill.connectionVersion = null
        entry.httpPrefill.abortController = null
        this.clearHttpPrefillReleaseTimer(entry)
      }
      this.emit()
    })
  }

  private clearCatchupBufferTimer(entry: ThreadSyncEntry): void {
    if (entry.catchupBuffer.flushTimer !== null) {
      clearTimeout(entry.catchupBuffer.flushTimer)
      entry.catchupBuffer.flushTimer = null
    }
  }

  private deferUntilCatchupFlushSettles(entry: ThreadSyncEntry, callback: () => void): void {
    if (entry.catchupBuffer.flushPromise === null) {
      callback()
      return
    }
    entry.catchupBuffer.deferredCallbacks.push(callback)
  }

  private trackCatchupFlushPromise(
    entry: ThreadSyncEntry,
    flushPromise: Promise<"applied" | "noop" | "error">,
  ): Promise<"applied" | "noop" | "error"> {
    entry.catchupBuffer.flushPromise = flushPromise
    void flushPromise.finally(() => {
      if (entry.catchupBuffer.flushPromise !== flushPromise) {
        return
      }
      entry.catchupBuffer.flushPromise = null
      const deferredCallbacks = entry.catchupBuffer.deferredCallbacks.splice(0)
      for (const callback of deferredCallbacks) {
        callback()
      }
    })
    return flushPromise
  }

  private resetCatchupBuffer(entry: ThreadSyncEntry): void {
    this.clearCatchupBufferTimer(entry)
    entry.catchupBuffer.events = []
    entry.catchupBuffer.effectiveAfter = null
    entry.catchupBuffer.nextCursor = null
    entry.catchupBuffer.hasMore = false
    entry.catchupBuffer.count = 0
    entry.catchupBuffer.approxBytes = 0
    entry.catchupBuffer.flushPromise = null
    entry.catchupBuffer.deferredCallbacks = []
  }

  private clearLiveEventBufferSchedule(entry: ThreadSyncEntry): void {
    if (entry.liveEventBuffer.flushAnimationFrameId !== null) {
      if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(entry.liveEventBuffer.flushAnimationFrameId)
      }
      entry.liveEventBuffer.flushAnimationFrameId = null
    }

    if (entry.liveEventBuffer.flushTimer !== null) {
      clearTimeout(entry.liveEventBuffer.flushTimer)
      entry.liveEventBuffer.flushTimer = null
    }
  }

  private resetLiveEventBuffer(entry: ThreadSyncEntry): void {
    this.clearLiveEventBufferSchedule(entry)
    entry.liveEventBuffer.events = []
  }

  private isLiveSyncEventFrameBatchEligible(entry: ThreadSyncEntry, event: SyncRuntimeEvent): boolean {
    return (
      entry.resume.isCatchingUp === false &&
      entry.catchupBuffer.nextCursor === null &&
      entry.catchupBuffer.flushPromise === null &&
      isFrameBatchableRuntimeEvent(event)
    )
  }

  private scheduleLiveEventBufferFlush(entry: ThreadSyncEntry): void {
    if (
      entry.liveEventBuffer.flushAnimationFrameId !== null ||
      entry.liveEventBuffer.flushTimer !== null
    ) {
      return
    }

    const flush = () => {
      entry.liveEventBuffer.flushAnimationFrameId = null
      entry.liveEventBuffer.flushTimer = null
      this.flushLiveEventBuffer(entry, { emitSynchronously: true })
    }

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function" &&
      typeof document !== "undefined" &&
      document.visibilityState === "visible"
    ) {
      entry.liveEventBuffer.flushAnimationFrameId = window.requestAnimationFrame(flush)
      return
    }

    entry.liveEventBuffer.flushTimer = setTimeout(
      flush,
      THREAD_SYNC_DEFERRED_EMIT_FALLBACK_DELAY_MS,
    )
  }

  private enqueueLiveSyncEventForFrameBatch(entry: ThreadSyncEntry, event: SyncRuntimeEvent): void {
    const bufferedTailEventId = entry.liveEventBuffer.events.at(-1)?.id ?? null
    if (
      bufferedTailEventId !== null &&
      Math.floor(event.id) !== Math.floor(bufferedTailEventId) + 1
    ) {
      const flushResult = this.flushLiveEventBuffer(entry)
      if (flushResult === "error") return
    }

    entry.liveEventBuffer.events.push(event)
    this.scheduleLiveEventBufferFlush(entry)
  }

  private flushLiveEventBuffer(
    entry: ThreadSyncEntry,
    options?: { emitSynchronously?: boolean },
  ): "applied" | "noop" | "error" {
    this.clearLiveEventBufferSchedule(entry)
    const events = entry.liveEventBuffer.events.splice(0)
    if (events.length === 0) return "noop"
    if (!this.isCurrentEntry(entry)) {
      return "noop"
    }
    return this.applyLiveSyncEvents(entry, events, options)
  }

  private applyLiveSyncEvents(
    entry: ThreadSyncEntry,
    events: SyncRuntimeEvent[],
    options?: { emitSynchronously?: boolean },
  ): "applied" | "noop" | "error" {
    if (events.length === 0) return "noop"
    this.storeRuntimeEventsByConversation(entry, events)
    const activeEvents = this.filterRuntimeEventsForActiveConversation(entry, events)
    const lastEventId = events.at(-1)?.id ?? entry.resume.cursor
    const applyResult = applySyncEventsToRuntime({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      resume: entry.resume,
      resolvePersistResume: ({ resume, applyResultStatus }) => planLiveSyncEventHandling({
        resume,
        eventId: lastEventId,
        applyResultStatus,
      }),
      connectionVersion: entry.transport.connectionVersion,
      events: activeEvents,
      nowMs: now(),
      onRecoverableError: () => {
        this.disconnect(entry.sessionId, {
          clearRuntime: false,
          removeEntry: false,
          shouldReconnect: true,
        })
      },
    })
    entry.resume = planLiveSyncEventHandling({
      resume: entry.resume,
      eventId: lastEventId,
      applyResultStatus: applyResult.status,
    })
    // Persist every live runtime event regardless of which conversation authored
    // it so the durable IDB lane stays contiguous. Only the `useAgentRuntime`
    // apply above is filtered to the active tab.
    const applyErroredForRuntime = applyResult.status === "error"
    const batchContainsActiveConversationEvents = activeEvents.length > 0
    if (!applyErroredForRuntime || !batchContainsActiveConversationEvents) {
      const firstEventId = events[0]?.id ?? lastEventId
      this.appendRuntimeEventsToDurableReplica({
        entry,
        expectedAfter: Math.max(0, Math.floor(firstEventId) - 1),
        events,
        nextCursor: lastEventId,
        historyToken: entry.resume.historyToken,
        sourceKind: "foreground_ws",
      })
    }
    if (options?.emitSynchronously === true) {
      this.emitSessionNow(entry.sessionId)
    } else {
      this.emitSessionDeferred(entry.sessionId)
    }
    return applyResult.status
  }

  private storeRuntimeEventsByConversation(entry: ThreadSyncEntry, events: SyncRuntimeEvent[]): void {
    for (const event of events) {
      const conversationId = normalizeRuntimeConversationId(event.conversationId)
      const existing = entry.runtimeEventsByConversationId.get(conversationId) ?? []
      if (existing.some((candidate) => candidate.id === event.id)) {
        continue
      }
      existing.push({
        ...event,
        conversationId,
      })
      entry.runtimeEventsByConversationId.set(conversationId, existing)
    }
  }

  private getStoredRuntimeEventsForConversation(
    entry: ThreadSyncEntry,
    conversationId: string,
  ): SyncRuntimeEvent[] {
    return [...(entry.runtimeEventsByConversationId.get(normalizeRuntimeConversationId(conversationId)) ?? [])]
      .sort((left, right) => left.id - right.id)
  }

  private filterRuntimeEventsForActiveConversation(entry: ThreadSyncEntry, events: SyncRuntimeEvent[]): SyncRuntimeEvent[] {
    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    return events.filter(
      (event) => normalizeRuntimeConversationId(event.conversationId) === activeConversationId,
    )
  }

  private flushCatchupBuffer(entry: ThreadSyncEntry): "applied" | "noop" | "error" | Promise<"applied" | "noop" | "error"> {
    this.suspendBackgroundWarmForForegroundStress()
    if (entry.catchupBuffer.flushPromise !== null) {
      return entry.catchupBuffer.flushPromise
    }
    if (entry.catchupBuffer.nextCursor === null) {
      this.resetCatchupBuffer(entry)
      return "noop"
    }

    if (entry.catchupBuffer.events.length === 0) {
      const frame = {
        count: entry.catchupBuffer.count,
        approxBytes: entry.catchupBuffer.approxBytes,
        nextCursor: entry.catchupBuffer.nextCursor,
      }
      const frameConnectionVersion = entry.transport.connectionVersion
      this.resetCatchupBuffer(entry)
      this.applyCursorOnlyCatchupProgress({
        entry,
        connectionVersion: frameConnectionVersion,
        frame,
      })
      this.emit()
      return "applied"
    }

    const frame: SyncCatchupPageFrame = {
      type: "sync.catchup.page",
      events: entry.catchupBuffer.events,
      effectiveAfter: entry.catchupBuffer.effectiveAfter ?? entry.resume.cursor,
      nextCursor: entry.catchupBuffer.nextCursor,
      hasMore: entry.catchupBuffer.hasMore,
      count: entry.catchupBuffer.count,
      approxBytes: entry.catchupBuffer.approxBytes,
    }
    const frameConnectionVersion = entry.transport.connectionVersion
    this.resetCatchupBuffer(entry)
    this.storeRuntimeEventsByConversation(entry, frame.events)
    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    const activeConversationRevision = entry.activeConversationRevision
    const activeFrameEvents = this.filterRuntimeEventsForActiveConversation(entry, frame.events)

    if (activeFrameEvents.length > THREAD_SYNC_BULK_APPLY_CHUNK_EVENT_COUNT) {
      const appliedChunkEvents: Array<{
        id: number
        type: string
        payload: unknown
        timestamp: number
        childSessionId?: string
      }> = []
      return this.trackCatchupFlushPromise(entry, applySyncEventsToRuntimeInChunks({
        activeThreadId: this.activeThreadId,
        getCurrentActiveThreadId: () => this.activeThreadId,
        sessionId: entry.sessionId,
        runtime: entry.runtime,
        resume: entry.resume,
        resolvePersistResume: ({ resume, applyResultStatus, maxEventId }) => planCatchupPageHandling({
          resume,
          frame: {
            count: 1,
            approxBytes: 0,
            nextCursor: maxEventId ?? resume.cursor,
          },
          applyResultStatus,
        }),
        connectionVersion: entry.transport.connectionVersion,
        events: activeFrameEvents,
        nowMs: now(),
        shouldContinue: () =>
          this.activeThreadId === entry.sessionId &&
          entry.transport.ws?.readyState === WebSocket.OPEN &&
          entry.transport.connectionVersion === frameConnectionVersion &&
          this.isActiveConversationProjectionCurrent(entry, {
            conversationId: activeConversationId,
            revision: activeConversationRevision,
          }),
        onRecoverableError: () => this.reconnectAfterCatchupApplyError(entry),
        onChunkApplied: ({ chunkEvents, applyResult }) => {
          this.updateResumeFromCatchupChunk({
            entry,
            chunkEvents,
            applyResultStatus: applyResult.status,
          })
          if (applyResult.status !== "error") {
            appliedChunkEvents.push(...chunkEvents)
          }
          this.emitSessionDeferred(entry.sessionId)
        },
      }).then((applyResult) => {
        if (!this.isActiveConversationProjectionCurrent(entry, {
          conversationId: activeConversationId,
          revision: activeConversationRevision,
        })) {
          this.emit()
          return "noop"
        }
        if (!applyResult.completedAllChunks) {
          if (appliedChunkEvents.length > 0) {
            // Persist the full page prefix that the applied chunks span, not
            // just the active-conversation filter, so the shared IDB lane
            // stays contiguous across all conversations.
            const lastAppliedChunkEventId =
              appliedChunkEvents.at(-1)?.id ?? frame.effectiveAfter
            const persistableFrameEvents = frame.events.filter(
              (event) => Math.floor(event.id ?? 0) <= lastAppliedChunkEventId,
            )
            this.appendRuntimeEventsToDurableReplica({
              entry,
              expectedAfter: frame.effectiveAfter,
              events: persistableFrameEvents,
              nextCursor: lastAppliedChunkEventId,
              historyToken: entry.resume.historyToken,
              sourceKind: "foreground_ws",
              saveCheckpoint: false,
            })
          }
          this.emit()
          return applyResult.status === "error" ? "error" : "noop"
        }
        const appliedRuntimeCursor = frame.nextCursor
        this.updateResumeFromCatchupFrame({
          entry,
          frame: {
            ...frame,
            nextCursor: appliedRuntimeCursor,
          },
          applyResultStatus: applyResult.status,
        })
        if (applyResult.status !== "error") {
          // Append the raw frame unfiltered. `activeFrameEvents` is only used
          // to drive `useAgentRuntime`; the durable lane tracks every
          // conversation.
          this.appendRuntimeEventsToDurableReplica({
            entry,
            expectedAfter: frame.effectiveAfter,
            events: frame.events,
            nextCursor: appliedRuntimeCursor,
            historyToken: entry.resume.historyToken,
            sourceKind: "foreground_ws",
          })
        }
        this.emit()
        return applyResult.status
      }))
    }

    const applyResult = applySyncEventsToRuntime({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      resume: entry.resume,
      resolvePersistResume: ({ resume, applyResultStatus }) => planCatchupPageHandling({
        resume,
        frame,
        applyResultStatus,
      }),
      connectionVersion: entry.transport.connectionVersion,
      events: activeFrameEvents,
      nowMs: now(),
      onRecoverableError: () => this.reconnectAfterCatchupApplyError(entry),
    })
    const appliedRuntimeCursor = frame.nextCursor
    this.updateResumeFromCatchupFrame({
      entry,
      frame: {
        ...frame,
        nextCursor: appliedRuntimeCursor,
      },
      applyResultStatus: applyResult.status,
    })
    if (applyResult.status !== "error") {
      // Small-frame fast path: persist the full transport frame unfiltered so
      // IDB keeps the shared cross-conversation event lane contiguous.
      this.appendRuntimeEventsToDurableReplica({
        entry,
        expectedAfter: frame.effectiveAfter,
        events: frame.events,
        nextCursor: appliedRuntimeCursor,
        historyToken: entry.resume.historyToken,
        sourceKind: "foreground_ws",
      })
    }
    this.emit()
    return applyResult.status
  }

  private scheduleCatchupBufferFlush(entry: ThreadSyncEntry): void {
    if (entry.catchupBuffer.flushTimer !== null) {
      return
    }

    entry.catchupBuffer.flushTimer = setTimeout(() => {
      entry.catchupBuffer.flushTimer = null
      void this.flushCatchupBuffer(entry)
    }, CATCHUP_BUFFER_FLUSH_DELAY_MS)
  }

  private queueCatchupFrame(entry: ThreadSyncEntry, frame: SyncCatchupPageFrame): "queued" | "error" {
    this.suspendBackgroundWarmForForegroundStress()
    if (entry.catchupBuffer.flushPromise !== null) {
      this.deferUntilCatchupFlushSettles(entry, () => {
        this.queueCatchupFrame(entry, frame)
      })
      return "queued"
    }

    if (entry.catchupBuffer.nextCursor !== null) {
      const expectedAfter = entry.catchupBuffer.nextCursor
      if (expectedAfter !== null && frame.effectiveAfter !== expectedAfter) {
        const flushResult = this.flushCatchupBuffer(entry)
        if (flushResult instanceof Promise) {
          this.deferUntilCatchupFlushSettles(entry, () => {
            this.queueCatchupFrame(entry, frame)
          })
          return "queued"
        }
        if (flushResult === "error") {
          return "error"
        }
      }
    }

    if (entry.catchupBuffer.nextCursor === null) {
      entry.catchupBuffer.effectiveAfter = frame.effectiveAfter
    }
    entry.catchupBuffer.events.push(...frame.events)
    entry.catchupBuffer.nextCursor = entry.catchupBuffer.nextCursor === null
      ? frame.nextCursor
      : Math.max(entry.catchupBuffer.nextCursor, frame.nextCursor)
    entry.catchupBuffer.hasMore = frame.hasMore
    entry.catchupBuffer.count += frame.count
    entry.catchupBuffer.approxBytes += frame.approxBytes

    if (
      entry.catchupBuffer.count >= CATCHUP_BUFFER_EVENT_ROW_SOFT_CAP ||
      entry.catchupBuffer.approxBytes >= CATCHUP_BUFFER_APPROX_BYTES_SOFT_CAP
    ) {
      const flushResult = this.flushCatchupBuffer(entry)
      if (flushResult instanceof Promise) {
        return "queued"
      }
      if (flushResult === "error") {
        return "error"
      }
      return "queued"
    }

    this.scheduleCatchupBufferFlush(entry)
    return "queued"
  }

  private buildViewState(entry: ThreadSyncEntry): ThreadSyncViewState {
    const runtimeState =
      this.activeThreadId === entry.sessionId ? useAgentRuntime.getState() : null
    const transportLoading =
      entry.resume.isCatchingUp || entry.httpPrefill.status === "inflight"
    const foregroundActivationLoading = shouldShowForegroundActivationLoading({
      connectionRole: entry.connectionRole,
      connectEnabled: entry.transport.connectEnabled,
      syncReadyReceived: entry.resume.syncReadyReceived,
      transportLoading,
    })
    const hideTransportLoadingWhenRenderable =
      entry.resume.isCatchingUp === false &&
      entry.httpPrefill.status === "inflight" &&
      entry.httpPrefill.mode === "validated_prefix" &&
      entry.runtime.materialization === "authoritative"
    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    const transcriptFreshness = this.deriveTranscriptFreshness(entry)
    const isActiveConversationQueueItem = (
      item: LocalOutboxQueueRecord | PendingQueueRecord,
    ) => normalizeRuntimeConversationId(item.conversationId) === activeConversationId

    return buildThreadSyncViewState({
      transport: {
        status: entry.transport.status,
        hasConnected: entry.transport.hasConnected,
        firstConnectionAttemptStartedAt: entry.transport.firstConnectionAttemptStartedAt,
        wsStatusChangedAt: entry.transport.wsStatusChangedAt,
        wsDegradedSinceAt: entry.transport.wsDegradedSinceAt,
        wsTransportProbeState: entry.transport.wsTransportProbeState,
        wsTransportProbeAt: entry.transport.wsTransportProbeAt,
        lastWsCloseDiagnostics: entry.transport.lastWsCloseDiagnostics,
        lastWsErrorDiagnostics: entry.transport.lastWsErrorDiagnostics,
      },
      controlPlane: entry.controlPlane,
      queue: {
        localOutboxEntries: entry.queue.localOutbox.filter(isActiveConversationQueueItem),
        serverPendingMessages: entry.queue.serverPendingMessages.filter(isActiveConversationQueueItem),
        serverPendingSessionActions: entry.queue.serverPendingSessionActions.filter(isActiveConversationQueueItem),
        lastMessageRejection:
          normalizeRuntimeConversationId(entry.queue.lastMessageRejection?.conversationId) === activeConversationId
            ? entry.queue.lastMessageRejection
            : null,
      },
      effects: entry.effects,
      resume: entry.resume,
      transportLoading,
      foregroundActivationLoading,
      hideTransportLoadingWhenRenderable,
      runtime: {
        materialization: entry.runtime.materialization,
        renderSource: entry.runtime.renderSource,
        resumeSource: entry.runtime.resumeSource,
        lastApplyError: entry.runtime.lastApplyError,
        lastSyncReadyDecision: entry.runtime.lastSyncReadyDecision,
        lastReset: entry.runtime.lastReset,
      },
      activeRuntimeState: runtimeState,
      activeRuntimeActiveEditOperation: normalizeActiveEditOperation(runtimeState?.snapshot?.activeEditOperation),
      threadActivityState:
        this.getActiveRuntimeConversationActivityState(entry) ??
        useThreadRuntime.getState().entries[entry.sessionId]?.state ??
        null,
      shouldRefreshSessionSurfaceAfterPrefill: shouldRefreshSessionSurfaceAfterPrefill({
        lastHttpPrefillCompletedAt: entry.lastHttpPrefillCompletedAt,
        lastSessionSurfaceRefreshAt: entry.lastSessionSurfaceRefreshAt,
        nowMs: now(),
      }),
      transcriptFreshness: transcriptFreshness.transcriptFreshness,
      localRawCursor: transcriptFreshness.localRawCursor,
      targetTerminalRawCursor: transcriptFreshness.targetTerminalRawCursor,
      serverRawTailId: transcriptFreshness.serverRawTailId,
    })
  }

  private getActiveRuntimeConversationActivityState(
    entry: ThreadSyncEntry,
  ): "idle" | "running" | "error" | null {
    const runtimeConversations = this.queryClient?.getQueryData(["runtime-conversations", entry.sessionId])
    if (!Array.isArray(runtimeConversations)) return null

    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    for (const item of runtimeConversations) {
      if (!item || typeof item !== "object") continue
      const row = item as Record<string, unknown>
      if (normalizeRuntimeConversationId(row.conversationId) !== activeConversationId) continue
      return row.activityState === "running" || row.activityState === "error" || row.activityState === "idle"
        ? row.activityState
        : null
    }
    return null
  }

  private getOrCreateEntry(
    sessionId: string,
    options?: ThreadSyncManagerConnectOptions,
    optimisticRuntimeSeed?: PendingForkSnapshot | null,
  ): ThreadSyncEntry {
    const existing = this.entries.get(sessionId)
    if (existing) return existing
    const created = createEntry(sessionId, options, optimisticRuntimeSeed)
    this.entries.set(sessionId, created)
    return created
  }

  private buildEntryReplicaSummary(entry: ThreadSyncEntry): ThreadSyncReplicaSummary | null {
    if (entry.runtime.cachedState === null) {
      return null
    }

    return buildThreadSyncReplicaSummary({
      resumeCursor: entry.resume.historyToken !== null ? entry.resume.cursor : null,
      snapshot: entry.runtime.cachedState.snapshot,
      quality:
        entry.runtime.replicaQuality ??
        (isSyncDerivedRuntimeMaterialization(entry.runtime.materialization)
          ? "best_effort"
          : null),
      savedAtMs: entry.runtime.replicaSavedAtMs ?? null,
      provenance: entry.runtime.replicaProvenance,
    })
  }

  private shouldInvalidateEntryFromPersistedReplicaMetadata(entry: ThreadSyncEntry): boolean {
    const invalidatedAtMs = getThreadSyncPersistedReplicaInvalidation(entry.sessionId)
    if (invalidatedAtMs === null) {
      return false
    }

    const currentSummary = this.buildEntryReplicaSummary(entry)
    const currentSavedAtMs =
      currentSummary?.savedAtMs ??
      entry.runtime.replicaSavedAtMs ??
      null

    if (typeof currentSavedAtMs === "number" && Number.isFinite(currentSavedAtMs)) {
      return invalidatedAtMs >= currentSavedAtMs
    }

    return (
      entry.runtime.cachedState !== null ||
      entry.resume.historyToken !== null ||
      entry.resume.cursor > 0
    )
  }

  listInactiveHotSessionIds(): string[] {
    return Array.from(this.entries.values())
      .filter((entry) => entry.sessionId !== this.activeThreadId)
      .filter((entry) => entry.connectionRole === "inactive")
      .filter((entry) => entry.runtime.cachedState !== null)
      .sort((left, right) => right.lastActivatedAt - left.lastActivatedAt)
      .slice(0, THREAD_SYNC_BACKGROUND_WARM_HOT_SESSION_LRU_MAX)
      .map((entry) => entry.sessionId)
  }

  getBackgroundWarmPolicySnapshot(): Record<string, ThreadSyncBackgroundWarmPolicyEntry> {
    const hotSessionIds = new Set(this.listInactiveHotSessionIds())
    const snapshot: Record<string, ThreadSyncBackgroundWarmPolicyEntry> = {}
    for (const entry of this.entries.values()) {
      snapshot[entry.sessionId] = {
        isHot: hotSessionIds.has(entry.sessionId),
        lastActivatedAt: entry.lastActivatedAt,
        connectionRole: entry.connectionRole,
        hasOpenOrConnectingTransport: hasOpenOrConnectingTransportSocket(entry.transport),
      }
    }

    return snapshot
  }

  getBackgroundWarmControlSnapshot(): ThreadSyncBackgroundWarmControlSnapshot {
    const activeSessionId = this.activeThreadId
    if (!activeSessionId) {
      return buildDefaultBackgroundWarmControlSnapshot({ activeSessionId: null })
    }

    const entry = this.entries.get(activeSessionId)
    if (!entry) {
      return buildDefaultBackgroundWarmControlSnapshot({ activeSessionId })
    }

    const snapshot = {
      suspended: false,
      activeSessionId,
      suspensionReason: null,
      isCatchingUp: entry.resume.isCatchingUp,
      httpPrefillStatus: entry.httpPrefill.status,
      httpPrefillPageCount: entry.httpPrefill.pageCount,
      httpPrefillTotalBytes: entry.httpPrefill.totalBytes,
      lastCatchupPageCount: entry.resume.lastPageCount,
      lastCatchupPageBytes: entry.resume.lastPageBytes,
      snapshotTimelineCount: 0,
      openCodeMessageCount: 0,
      claudeRawEventCount: 0,
    } satisfies ThreadSyncBackgroundWarmControlSnapshot

    if (this.backgroundWarmActiveSwitchSuspendedUntilMs > now()) {
      return {
        ...snapshot,
        suspended: true,
        suspensionReason: "recent_active_session_switch",
      }
    }

    if (this.backgroundWarmActiveSwitchSuspendedUntilMs !== 0) {
      this.backgroundWarmActiveSwitchSuspendedUntilMs = 0
    }

    if (entry.httpPrefill.status === "inflight") {
      return {
        ...snapshot,
        suspended: true,
        suspensionReason: "active_http_prefill_inflight",
      }
    }

    if (!entry.resume.isCatchingUp) {
      if (this.backgroundWarmForegroundStressSuspendedUntilMs > now()) {
        return {
          ...snapshot,
          suspended: true,
          suspensionReason: "foreground_stress",
        }
      }

      if (this.backgroundWarmForegroundStressSuspendedUntilMs !== 0) {
        this.backgroundWarmForegroundStressSuspendedUntilMs = 0
      }
      return snapshot
    }

    if (
      entry.resume.lastPageCount >= BACKGROUND_WARM_SUSPEND_ACTIVE_CATCHUP_PAGE_ROW_THRESHOLD ||
      entry.resume.lastPageBytes >= BACKGROUND_WARM_SUSPEND_ACTIVE_CATCHUP_PAGE_BYTE_THRESHOLD
    ) {
      return {
        ...snapshot,
        suspended: true,
        suspensionReason: "active_catchup_page_too_large",
      }
    }

    if (this.backgroundWarmForegroundStressSuspendedUntilMs > now()) {
      return {
        ...snapshot,
        suspended: true,
        suspensionReason: "foreground_stress",
      }
    }

    if (this.backgroundWarmForegroundStressSuspendedUntilMs !== 0) {
      this.backgroundWarmForegroundStressSuspendedUntilMs = 0
    }

    const replayWorkerEligibility = this.getCachedActiveReplayWorkerEligibility({
      sessionId: activeSessionId,
    })
    const snapshotWithRuntimeSummary = {
      ...snapshot,
      snapshotTimelineCount: replayWorkerEligibility.summary.snapshotTimelineCount,
      openCodeMessageCount: replayWorkerEligibility.summary.openCodeMessageCount,
      claudeRawEventCount: replayWorkerEligibility.summary.claudeRawEventCount,
    } satisfies ThreadSyncBackgroundWarmControlSnapshot

    if (!replayWorkerEligibility.allowed) {
      return {
        ...snapshotWithRuntimeSummary,
        suspended: true,
        suspensionReason: "active_runtime_too_large",
      }
    }

    return snapshotWithRuntimeSummary
  }

  invalidateInactiveBackgroundReplica(
    sessionId: string,
    invalidatedAtMsOverride?: number,
  ): boolean {
    const entry = this.entries.get(sessionId)
    if (!entry) {
      return false
    }

    const invalidatedAtMs = invalidatedAtMsOverride ?? now()
    const currentSummary = this.buildEntryReplicaSummary(entry)
    const currentSavedAtMs =
      currentSummary?.savedAtMs ??
      entry.runtime.replicaSavedAtMs ??
      null
    if (
      typeof currentSavedAtMs === "number" &&
      Number.isFinite(currentSavedAtMs) &&
      currentSavedAtMs > invalidatedAtMs
    ) {
      return false
    }

    if (
      this.activeThreadId === sessionId ||
      entry.connectionRole !== "inactive" ||
      hasOpenOrConnectingTransportSocket(entry.transport)
    ) {
      entry.backgroundReplicaInvalidatedAtMs = Math.max(
        entry.backgroundReplicaInvalidatedAtMs ?? 0,
        invalidatedAtMs,
      )
      return true
    }

    this.applyBackgroundReplicaInvalidation(entry)
    return true
  }

  private pruneInactiveEntries(): void {
    if (this.entries.size <= THREAD_SYNC_ENTRY_LRU_MAX) {
      return
    }

    const inactiveEntries = Array.from(this.entries.values())
      .filter((entry) => entry.sessionId !== this.activeThreadId)
      .filter((entry) => entry.connectionRole === "inactive")
      .sort((left, right) => left.lastActivatedAt - right.lastActivatedAt)

    while (this.entries.size > THREAD_SYNC_ENTRY_LRU_MAX && inactiveEntries.length > 0) {
      const entry = inactiveEntries.shift()
      if (!entry) {
        break
      }

      this.resetHttpPrefillState(entry)
      this.resetCatchupBuffer(entry)
      this.clearRenderedLocalOutboxCleanupTimer(entry)
      clearSessionSurfaceRefreshCooldownTimer(entry)
      clearTransportTimers(entry.transport)
      closeTransportSocket(entry.transport, "reconnect:entry_lru_evict")
      clearSessionSyncHealthEntry(entry.sessionId)
      useSessionPrFlow.getState().clearSnapshot(entry.sessionId)
      this.clearEntryRuntimeState(entry)
      if (this.handoffPreviousThreadId === entry.sessionId) {
        this.clearHandoffCloseTimer()
        this.handoffPreviousThreadId = null
      }
      this.entries.delete(entry.sessionId)
      this.viewStateCache.delete(entry.sessionId)
    }
  }

  private applyOptions(entry: ThreadSyncEntry, options?: ThreadSyncManagerConnectOptions): void {
    if (options && Object.prototype.hasOwnProperty.call(options, "activeConversationId")) {
      this.applyActiveConversationId(entry, options.activeConversationId, {
        replayForegroundRuntime: true,
        notify: true,
      })
    }
    applyTransportOptions(entry.transport, {
      connectEnabled: options?.connectEnabled,
      viewerActivityEnabled: options?.viewerActivityEnabled,
      readOnlyTransport: options?.readOnlyTransport,
    })
    if (options?.initialBranchSwitchInProgress === true && entry.controlPlane.branchSwitchInProgress !== true) {
      entry.controlPlane.branchSwitchInProgress = true
    }
  }

  private applyActiveConversationId(entry: ThreadSyncEntry, conversationId: unknown, options: {
    replayForegroundRuntime: boolean
    notify: boolean
  }): boolean {
    const nextConversationId = normalizeRuntimeConversationId(conversationId)
    if (entry.activeConversationId === nextConversationId) {
      return false
    }

    entry.activeConversationId = nextConversationId
    entry.activeConversationRevision += 1
    if (entry.catchupBuffer.events.length > 0) {
      this.storeRuntimeEventsByConversation(entry, entry.catchupBuffer.events)
    }
    if (entry.catchupBuffer.flushPromise === null) {
      this.resetCatchupBuffer(entry)
    }
    this.resetHttpPrefillState(entry)
    const optimisticSeed = entry.runtime.optimisticSeed
    this.clearEntryRuntimeState(entry)
    entry.runtime.optimisticSeed = optimisticSeed
    entry.resume = resetResumeForColdReplay()

    if (this.activeThreadId === entry.sessionId) {
      // Conversation switches are intra-session tab switches, so clearing only
      // the entry-local cached projection is not enough: the singleton
      // `useAgentRuntime` store may still hold the previous tab's transcript
      // until IDB hydration or transport replay finishes. Reset it immediately
      // so a newly selected side chat cannot show the main chat's queued or
      // committed messages during that handoff window.
      resetRuntimeStore()
    }

    // The durable runtime replica lane is shared across conversations, but the
    // projection we just cleared was conversation-scoped. Invalidate any
    // hydration work that was in flight for the previous conversation so the
    // projection restore we kick off next always runs under the new
    // conversation id.
    entry.durableReplicaHydration = {
      generation: entry.durableReplicaHydration.generation + 1,
      inFlight: false,
      shouldDeferColdHandshake: false,
    }

    if (options.replayForegroundRuntime && this.activeThreadId === entry.sessionId) {
      this.replayStoredRuntimeEventsForActiveConversation(entry, nextConversationId)
    }

    if (this.activeThreadId === entry.sessionId) {
      // Re-run durable hydration so the newly selected conversation can
      // restore its own projection from the shared runtime lane. Previously
      // this was default-only, which meant side-chat tab switches always had
      // to cold-replay from server catchup even when IDB already had all
      // the events locally.
      this.startDurableReplicaHydration({
        entry,
        allowReplaceExistingRuntime: true,
      })
    }

    if (this.activeThreadId === entry.sessionId && entry.transport.helloSent === true) {
      this.disconnect(entry.sessionId, {
        clearRuntime: false,
        removeEntry: false,
        shouldReconnect: true,
        closeReason: "reconnect:switch_conversation",
      })
    }

    if (!options.notify) {
      return true
    }

    this.invalidateViewStateCache({
      type: "sessions",
      sessionIds: new Set([entry.sessionId]),
      clearEmptyViewState: false,
    })
    this.emitSessionDeferred(entry.sessionId)
    return true
  }

  private replayStoredRuntimeEventsForActiveConversation(entry: ThreadSyncEntry, conversationId: string): void {
    resetRuntimeStore()
    const events = this.getStoredRuntimeEventsForConversation(entry, conversationId)
    if (events.length === 0) {
      return
    }

    applySyncEventsToRuntime({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      resume: entry.resume,
      resolvePersistResume: ({ resume }) => resume,
      connectionVersion: entry.transport.connectionVersion,
      events,
      nowMs: now(),
    })
  }

  private isActiveConversationProjectionCurrent(entry: ThreadSyncEntry, expected: {
    conversationId: string
    revision: number
  }): boolean {
    return (
      entry.activeConversationRevision === expected.revision &&
      normalizeRuntimeConversationId(entry.activeConversationId) === expected.conversationId
    )
  }

  private isCurrentEntry(entry: ThreadSyncEntry): boolean {
    return !this.destroyed && this.entries.get(entry.sessionId) === entry
  }

  private prepareEntryForHandoff(entry: ThreadSyncEntry): void {
    this.resetHttpPrefillState(entry, { preserveCompletedAt: true })
    this.resetCatchupBuffer(entry)
    this.resetLiveEventBuffer(entry)
    entry.connectionRole = "handoff_previous"
    entry.transport.helloSent = false
    clearReconnectTimer(entry.transport)
    applyTransportOptions(entry.transport, {
      connectEnabled: false,
      viewerActivityEnabled: false,
      readOnlyTransport: true,
    })
    this.syncHealth(entry)
  }

  private startPendingHandoff(sessionId: string): void {
    this.clearHandoffCloseTimer()
    this.handoffPreviousThreadId = sessionId
    this.handoffCloseTimer = setTimeout(() => {
      if (this.handoffPreviousThreadId !== sessionId) {
        return
      }
      this.closePendingHandoffThread({
        closeReason: "reconnect:handoff_timeout",
      })
    }, HANDOFF_OVERLAP_MAX_MS)
  }

  private clearHandoffCloseTimer(): void {
    if (!this.handoffCloseTimer) {
      return
    }
    clearTimeout(this.handoffCloseTimer)
    this.handoffCloseTimer = null
  }

  private clearBackgroundWarmActiveSwitchResumeTimer(): void {
    if (!this.backgroundWarmActiveSwitchResumeTimer) {
      return
    }
    clearTimeout(this.backgroundWarmActiveSwitchResumeTimer)
    this.backgroundWarmActiveSwitchResumeTimer = null
  }

  private clearBackgroundWarmForegroundStressResumeTimer(): void {
    if (!this.backgroundWarmForegroundStressResumeTimer) {
      return
    }
    clearTimeout(this.backgroundWarmForegroundStressResumeTimer)
    this.backgroundWarmForegroundStressResumeTimer = null
  }

  private suspendBackgroundWarmForForegroundStress(): void {
    this.backgroundWarmForegroundStressSuspendedUntilMs = Math.max(
      this.backgroundWarmForegroundStressSuspendedUntilMs,
      now() + BACKGROUND_WARM_FOREGROUND_STRESS_SUSPEND_MS,
    )
    this.clearBackgroundWarmForegroundStressResumeTimer()
    this.backgroundWarmForegroundStressResumeTimer = setTimeout(() => {
      this.backgroundWarmForegroundStressResumeTimer = null
      if (this.destroyed) {
        return
      }

      if (this.backgroundWarmForegroundStressSuspendedUntilMs <= now()) {
        this.backgroundWarmForegroundStressSuspendedUntilMs = 0
        this.emit()
      }
    }, BACKGROUND_WARM_FOREGROUND_STRESS_SUSPEND_MS)
  }

  private suspendBackgroundWarmAfterActiveSwitch(): void {
    this.backgroundWarmActiveSwitchSuspendedUntilMs = now() + BACKGROUND_WARM_ACTIVE_SWITCH_SUSPEND_MS
    this.clearBackgroundWarmActiveSwitchResumeTimer()
    this.backgroundWarmActiveSwitchResumeTimer = setTimeout(() => {
      this.backgroundWarmActiveSwitchResumeTimer = null
      if (this.destroyed) {
        return
      }

      if (this.backgroundWarmActiveSwitchSuspendedUntilMs <= now()) {
        this.backgroundWarmActiveSwitchSuspendedUntilMs = 0
        this.emit()
      }
    }, BACKGROUND_WARM_ACTIVE_SWITCH_SUSPEND_MS)
  }

  private hasUnattemptedPendingLocalDispatchesForCurrentConnection(entry: ThreadSyncEntry): boolean {
    if (entry.queue.pendingLocalDispatches.size === 0) {
      return false
    }

    for (const pendingDispatch of entry.queue.pendingLocalDispatches.values()) {
      if (pendingDispatch.lastAttemptedConnectionVersion !== entry.transport.connectionVersion) {
        return true
      }
    }

    return false
  }

  private closePendingHandoffThread(args: {
    retainSessionId?: string | null
    closeReason: string
  }): void {
    const handoffPreviousThreadId = this.handoffPreviousThreadId
    if (!handoffPreviousThreadId) {
      return
    }

    if (handoffPreviousThreadId === args.retainSessionId) {
      const retainedEntry = this.entries.get(handoffPreviousThreadId)
      if (retainedEntry) {
        retainedEntry.connectionRole = "foreground_active"
      }
      this.clearHandoffCloseTimer()
      this.handoffPreviousThreadId = null
      return
    }

    this.clearHandoffCloseTimer()
    const previousEntry = this.entries.get(handoffPreviousThreadId)
    if (previousEntry) {
      previousEntry.connectionRole = "inactive"
    }
    this.handoffPreviousThreadId = null
    this.disconnect(handoffPreviousThreadId, {
      clearRuntime: false,
      removeEntry: false,
      shouldReconnect: false,
      closeReason: args.closeReason,
    })
  }

  private completePendingHandoffIfReady(entry: ThreadSyncEntry): void {
    if (
      this.activeThreadId !== entry.sessionId ||
      entry.transport.status !== "connected" ||
      entry.transport.helloSent !== true
    ) {
      return
    }

    const handoffPreviousThreadId = this.handoffPreviousThreadId
    if (handoffPreviousThreadId && handoffPreviousThreadId !== entry.sessionId) {
      const handoffEntry = this.entries.get(handoffPreviousThreadId)
      if (
        handoffEntry &&
        this.hasUnattemptedPendingLocalDispatchesForCurrentConnection(handoffEntry)
      ) {
        return
      }
    }

    this.closePendingHandoffThread({
      retainSessionId: entry.sessionId,
      closeReason: "reconnect:handoff_complete",
    })
  }

  private startConnectingWatchdog(args: {
    entry: ThreadSyncEntry
    ws: WebSocket
    connectionVersion: number
  }): void {
    clearConnectingWatchdogTimer(args.entry.transport)
    const connectingWatchdogTimer = setTimeout(() => {
      if (args.entry.transport.connectingWatchdogTimer === connectingWatchdogTimer) {
        args.entry.transport.connectingWatchdogTimer = null
      }
      if (this.destroyed) return
      if (!this.isCurrentConnection(args.entry, args.ws, args.connectionVersion)) return
      if (args.ws.readyState === WebSocket.OPEN) return

      debugWarn("sync.new", "Force reconnecting stuck websocket connect attempt", {
        sessionId: args.entry.sessionId,
        managerInstanceId: this.instanceId,
        connectionVersion: args.connectionVersion,
        readyState: args.ws.readyState,
        status: args.entry.transport.status,
      })
      this.forceReconnect({
        sessionId: args.entry.sessionId,
        closeReason: "reconnect:connect_timeout",
        resetReconnectAttempts: false,
      })
    }, THREAD_SYNC_CONNECTING_WATCHDOG_TIMEOUT_MS)
    args.entry.transport.connectingWatchdogTimer = connectingWatchdogTimer
  }

  private openSocket(entry: ThreadSyncEntry): void {
    this.resetHttpPrefillState(entry, { preserveCompletedAt: true })
    const connectionVersion = beginTransportConnectAttempt(entry.transport, {
      nowMs: now(),
    })
    entry.resume = prepareResumeForConnectionAttempt(entry.resume)
    entry.runtime.lastApplyError = null
    entry.runtime.continuityValidatedConnectionVersion = null
    this.setConnectionStatus(entry, "connecting")

    const ws = new WebSocket(normalizeWsUrl(buildSessionWsUrl(entry.sessionId)))
    entry.transport.ws = ws
    this.startConnectingWatchdog({ entry, ws, connectionVersion })

    ws.onopen = () => {
      if (!this.isCurrentConnection(entry, ws, connectionVersion)) return
      clearConnectingWatchdogTimer(entry.transport)
      this.setConnectionStatus(entry, "connected")
      if (entry.queue.pendingLocalDispatches.size > 0) {
        if (entry.httpPrefill.status === "inflight") {
          this.cancelCurrentHttpPrefill({
            entry,
            status: "skipped",
            reason: "socket_open_pending_local_dispatch",
          })
          this.releaseDeferredHandshakeIfSocketOpen({
            entry,
            connectionVersion,
            reason: "socket_open_pending_local_dispatch",
          })
        } else {
          this.sendInitialHandshake({
            entry,
            ws,
            connectionVersion,
            reason: "socket_open_pending_local_dispatch",
          })
        }
        return
      }
      if (entry.httpPrefill.status === "inflight" && this.hydratedResumeCoversKnownTerminalCursor(entry)) {
        this.cancelCurrentHttpPrefill({
          entry,
          status: "skipped",
          reason: "socket_open_durable_hydration_covered_terminal_cursor",
        })
        this.releaseDeferredHandshakeIfSocketOpen({
          entry,
          connectionVersion,
          reason: "durable_hydration_restored",
        })
        this.emit()
        return
      }
      if (entry.httpPrefill.status === "inflight" && entry.httpPrefill.mode === "terminal_cursor_catchup") {
        entry.transport.deferInitialHandshake = true
        this.emit()
        return
      }
      if (entry.httpPrefill.status === "inflight") {
        entry.transport.deferInitialHandshake = true
        this.emit()
        return
      }
      if (entry.httpPrefill.mode !== null && entry.transport.helloSent === false) {
        this.releaseDeferredHandshakeIfSocketOpen({
          entry,
          connectionVersion,
          reason: entry.httpPrefill.status === "applied"
            ? "http_prefill_complete_before_socket_open"
            : entry.httpPrefill.status === "timed_out"
              ? "http_prefill_timeout_before_socket_open"
              : entry.httpPrefill.status === "skipped"
                ? "http_prefill_skipped_before_socket_open"
                : "http_prefill_finished_before_socket_open",
        })
        this.emit()
        return
      }
      if (this.shouldAttemptHttpPrefill(entry)) {
        this.beginHttpPrefill(entry, ws, connectionVersion)
        this.emit()
        return
      }

      this.sendInitialHandshake({
        entry,
        ws,
        connectionVersion,
        reason: "socket_open",
      })
    }

    ws.onmessage = (event) => {
      if (!this.isCurrentConnection(entry, ws, connectionVersion)) return
      if (typeof event.data !== "string") return
      this.handleSocketMessage(entry, event.data)
    }

    ws.onerror = () => {
      if (!this.isCurrentConnection(entry, ws, connectionVersion)) return
      recordTransportError({
        transport: entry.transport,
        message: "WebSocket transport error",
        nowMs: now(),
      })
      this.setConnectionStatus(entry, "error")
      this.emit()
    }

    ws.onclose = (event) => {
      if (!this.isCurrentConnection(entry, ws, connectionVersion)) return
      this.resetHttpPrefillState(entry, { preserveCompletedAt: true })
      this.resetCatchupBuffer(entry)
      this.resetLiveEventBuffer(entry)
      entry.queue.deferredPreHandshakeMessages = []
      recordTransportClose({
        transport: entry.transport,
        event,
        nowMs: now(),
      })
      entry.resume = markResumeConnectionClosed(entry.resume)
      this.setConnectionStatus(
        entry,
        entry.transport.status === "error" ? "error" : "disconnected",
      )
      this.scheduleReconnect(entry)
      this.emit()
    }
  }

  private handleSocketMessage(entry: ThreadSyncEntry, raw: string): void {
    if (entry.catchupBuffer.flushPromise !== null) {
      this.deferUntilCatchupFlushSettles(entry, () => {
        this.handleSocketMessage(entry, raw)
      })
      return
    }

    let parsed: { type?: unknown; [key: string]: unknown }
    try {
      parsed = JSON.parse(raw) as { type?: unknown; [key: string]: unknown }
    } catch {
      return
    }

    const type = asString(parsed.type)
    if (!type) return

    if (type === "introspect_idb_request") {
      void this.handleBrowserStorageIntrospectionMessage(
        entry,
        parsed as CordIdbRelayRequestMessage,
      )
      return
    }

    if (type === "sync.ready") {
      const historyContinuity = normalizeSyncContinuity(parsed.historyContinuity)
      this.handleSyncFrame(entry, {
        type: "sync.ready",
        acceptedCursor: Math.max(0, asNumber(parsed.acceptedCursor) ?? 0),
        tailId: Math.max(0, asNumber(parsed.tailId) ?? 0),
        historyContinuity,
        replicationContinuity: normalizeSyncContinuity(
          parsed.replicationContinuity,
          historyContinuity,
        ),
        historyToken: typeof parsed.historyToken === "string" ? parsed.historyToken : null,
      })
      return
    }

    if (type.startsWith("sync.")) {
      this.handleSyncFrame(entry, parsed as unknown as SyncServerFrame)
      return
    }

    if (entry.catchupBuffer.nextCursor !== null) {
      const flushResult = this.flushCatchupBuffer(entry)
      if (flushResult instanceof Promise) {
        this.deferUntilCatchupFlushSettles(entry, () => {
          this.handleSideChannelMessage(entry, parsed as { type: string; [key: string]: unknown })
        })
        return
      }
      if (flushResult === "error") {
        return
      }
    }

    const liveFlushResult = this.flushLiveEventBuffer(entry)
    if (liveFlushResult === "error") {
      return
    }

    this.handleSideChannelMessage(entry, parsed as { type: string; [key: string]: unknown })
  }

  private buildThreadRuntimeIntrospection(sessionId: string): unknown {
    const entry = this.entries.get(sessionId) ?? null
    return {
      activeThreadId: this.activeThreadId,
      managerInstanceId: this.instanceId,
      entry: entry
        ? {
            codeServerError: entry.effects.codeServerError,
            codeServerStatus: entry.effects.codeServerStatus,
            connectEnabled: entry.transport.connectEnabled,
            connectionVersion: entry.transport.connectionVersion,
            continuityValidatedConnectionVersion: entry.runtime.continuityValidatedConnectionVersion,
            controlPlane: entry.controlPlane,
            cursor: entry.resume.cursor,
            hasConnected: entry.transport.hasConnected,
            helloSent: entry.transport.helloSent,
            historyToken: entry.resume.historyToken,
            isCatchingUp: entry.resume.isCatchingUp,
            keepaliveFrameType: entry.transport.keepaliveFrameType,
            lastApplyError: entry.runtime.lastApplyError,
            lastMessageRejection: entry.queue.lastMessageRejection,
            lastPageBytes: entry.resume.lastPageBytes,
            lastPageCount: entry.resume.lastPageCount,
            lastWsCloseDiagnostics: entry.transport.lastWsCloseDiagnostics,
            lastWsErrorDiagnostics: entry.transport.lastWsErrorDiagnostics,
            latestControlPlaneRuntimeSnapshot: entry.controlPlane.runtimeSnapshot,
            localOutbox: entry.queue.localOutbox,
            localCloseReason: entry.transport.localCloseReason,
            optimisticRuntimeSeed: entry.runtime.optimisticSeed,
            readOnlyTransport: entry.transport.readOnlyTransport,
            reconnectAttempts: entry.transport.reconnectAttempts,
            replicaProvenance: entry.runtime.replicaProvenance,
            replicaQuality: entry.runtime.replicaQuality,
            replicaSavedAtMs: entry.runtime.replicaSavedAtMs,
            requestedAfter: entry.resume.requestedAfter,
            runtimeConnectionVersion: entry.runtime.connectionVersion,
            runtimeMaterialization: entry.runtime.materialization,
            runtimeState: entry.runtime.cachedState,
            serverPendingMessages: entry.queue.serverPendingMessages,
            sessionActionNotice: entry.effects.sessionActionNotice,
            sessionId: entry.sessionId,
            status: entry.transport.status,
            tailCursor: entry.resume.tailCursor,
            viewerActivityEnabled: entry.transport.viewerActivityEnabled,
            wsDegradedSinceAt: entry.transport.wsDegradedSinceAt,
            wsReadyState: entry.transport.ws?.readyState ?? null,
            wsStatusChangedAt: entry.transport.wsStatusChangedAt,
            wsTransportProbeAt: entry.transport.wsTransportProbeAt,
            wsTransportProbeState: entry.transport.wsTransportProbeState,
          }
        : null,
      hasEntry: entry !== null,
      sessionId,
      viewState: this.getViewState(sessionId),
    }
  }

  private sendBrowserStorageIntrospectionResponse(args: {
    entry: ThreadSyncEntry
    requestId: string
    response: CordIntrospectionEnvelope
  }): void {
    const ws = args.entry.transport.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return
    }

    try {
      ws.send(JSON.stringify({
        type: "introspect_idb_response",
        requestId: args.requestId,
        response: args.response,
      }))
    } catch {
      // Best effort only. The SessionDO-side timeout will surface the failure.
    }
  }

  private async handleBrowserStorageIntrospectionMessage(
    entry: ThreadSyncEntry,
    message: CordIdbRelayRequestMessage,
  ): Promise<void> {
    const requestId = asString(message.requestId)
    if (!requestId) {
      return
    }

    const request =
      message.request && typeof message.request === "object"
        ? (message.request as CordIntrospectionIdbRequest)
        : null

    if (!request) {
      this.sendBrowserStorageIntrospectionResponse({
        entry,
        requestId,
        response: {
          ok: false,
          code: "invalid_request",
          error: "request is required",
        },
      })
      return
    }

    const response = await executeBrowserStorageIntrospectionRequest({
      inspectThreadRuntimeEntry: ({ sessionId }) => this.buildThreadRuntimeIntrospection(sessionId),
      request,
    })

    this.sendBrowserStorageIntrospectionResponse({
      entry,
      requestId,
      response,
    })
  }

  private handleSyncFrame(entry: ThreadSyncEntry, frame: SyncServerFrame): void {
    if (entry.catchupBuffer.flushPromise !== null) {
      this.deferUntilCatchupFlushSettles(entry, () => {
        this.handleSyncFrame(entry, frame)
      })
      return
    }

    if (
      entry.connectionRole !== "foreground_active" &&
      frame.type !== "sync.heartbeat" &&
      frame.type !== "sync.error"
    ) {
      return
    }

    if (frame.type !== "sync.catchup.page" && entry.catchupBuffer.nextCursor !== null) {
      const flushResult = this.flushCatchupBuffer(entry)
      if (flushResult instanceof Promise) {
        this.deferUntilCatchupFlushSettles(entry, () => {
          this.handleSyncFrame(entry, frame)
        })
        return
      }
      if (flushResult === "error") {
        return
      }
    }

    if (frame.type !== "sync.event") {
      const liveFlushResult = this.flushLiveEventBuffer(entry)
      if (liveFlushResult === "error") {
        return
      }
    }

    switch (frame.type) {
      case "sync.heartbeat": {
        if (entry.transport.deferInitialHandshake) {
          return
        }
        const heartbeatPlan = planSyncHeartbeat({
          helloSent: entry.transport.helloSent,
          socketReady: entry.transport.ws?.readyState === WebSocket.OPEN,
          cursor: entry.resume.cursor,
          viewerActivityEnabled: entry.transport.viewerActivityEnabled,
        })

        if (heartbeatPlan.shouldSend && entry.transport.ws) {
          this.sendInitialHandshake({
            entry,
            ws: entry.transport.ws,
            connectionVersion: entry.transport.connectionVersion,
            reason: "server_heartbeat",
          })
        }
        return
      }
      case "sync.baseline.page": {
        const applyResult = applyBaselinePageToRuntime({
          activeThreadId: this.activeThreadId,
          sessionId: entry.sessionId,
          runtime: entry.runtime,
          resume: entry.resume,
          connectionVersion: entry.transport.connectionVersion,
          frame,
          nowMs: now(),
          onRecoverableError: () => {
            this.disconnect(entry.sessionId, {
              clearRuntime: false,
              removeEntry: false,
              shouldReconnect: true,
            })
          },
        })
        entry.resume = applyBaselinePageMetrics({
          resume: entry.resume,
          frame,
        })
        if (applyResult.status !== "error") {
          this.appendBaselineEventsToDurableReplica({
            entry,
            events: frame.events,
            sourceKind: "foreground_ws",
          })
        }
        this.emit()
        return
      }
      case "sync.ready": {
        const previousReplicaProvenance = entry.runtime.replicaProvenance
        entry.runtime.replicaProvenance = buildThreadSyncReplicaProvenance({
          sourceKind: "foreground_ws",
          sourceTabId: getForegroundOwnershipTabId(),
        })
        const activeRuntimeState = this.activeThreadId === entry.sessionId ? useAgentRuntime.getState() : null
        const activeRuntimeContinuityCursor =
          activeRuntimeState?.snapshot !== null &&
          typeof activeRuntimeState?.lastEventId === "number" &&
          Number.isFinite(activeRuntimeState.lastEventId) &&
          activeRuntimeState.lastEventId > 0
            ? Math.floor(activeRuntimeState.lastEventId)
            : null
        const nowMs = now()
        const previousResumeBeforeReady = entry.resume
        const readyPlan = planSyncReadyHandling({
          resume: entry.resume,
          frame,
          runtimeMaterialization: entry.runtime.materialization,
          runtimeConnectionVersion: entry.runtime.connectionVersion,
          transportConnectionVersion: entry.transport.connectionVersion,
          hasActiveRuntimeState: activeRuntimeState !== null,
          hasActiveRuntimeSnapshot: activeRuntimeState?.snapshot !== null,
          activeLastEventId: activeRuntimeContinuityCursor,
          nowMs,
        })
        entry.runtime.lastSyncReadyDecision = readyPlan.debugSnapshot
        entry.resume = readyPlan.nextResume
        const shouldPreserveColdReplayCursorRegression =
          shouldPreserveRuntimeDuringColdReplayCursorRegression({
            acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
            requestedAfter: previousResumeBeforeReady.requestedAfter,
            previousHistoryToken: readyPlan.debugSnapshot.previousHistoryToken,
            hasCursorRegression: readyPlan.debugSnapshot.hasCursorRegression,
            hasHistoryContinuityBroken: readyPlan.debugSnapshot.hasHistoryContinuityBroken,
            hasHistoryTokenMismatch: readyPlan.debugSnapshot.hasHistoryTokenMismatch,
            replicationContinuity: readyPlan.debugSnapshot.replicationContinuity,
          })
        const syncReadyLogDetails = {
          sessionId: entry.sessionId,
          acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
          tailCursor: readyPlan.debugSnapshot.tailCursor,
          historyContinuity: readyPlan.debugSnapshot.historyContinuity,
          replicationContinuity: readyPlan.debugSnapshot.replicationContinuity,
          resetTriggered: readyPlan.debugSnapshot.resetTriggered,
          reasonCodes: readyPlan.debugSnapshot.reasonCodes,
          activeLastEventId: readyPlan.debugSnapshot.activeLastEventId,
          runtimeMaterialization: readyPlan.debugSnapshot.runtimeMaterialization,
          hasActiveRuntimeSnapshot: readyPlan.debugSnapshot.hasActiveRuntimeSnapshot,
          hasCurrentConnectionAuthoritativeRuntime: readyPlan.debugSnapshot.hasCurrentConnectionAuthoritativeRuntime,
          shouldReplaceDisplayOnlyRuntime: readyPlan.shouldReplaceDisplayOnlyRuntime,
          transportConnectionVersion: entry.transport.connectionVersion,
          runtimeConnectionVersion: entry.runtime.connectionVersion,
          continuityValidatedConnectionVersion: entry.runtime.continuityValidatedConnectionVersion,
          nextContinuityValidatedConnectionVersion: readyPlan.continuityValidatedConnectionVersion,
          previousHistoryToken: readyPlan.debugSnapshot.previousHistoryToken,
          nextHistoryToken: readyPlan.debugSnapshot.nextHistoryToken,
          previousParsedHistoryToken: readyPlan.debugSnapshot.previousParsedHistoryToken,
          nextParsedHistoryToken: readyPlan.debugSnapshot.nextParsedHistoryToken,
        }
        if (readyPlan.shouldResetRuntime) {
          const hasOnlyColdHandshakeCursorRegression =
            readyPlan.debugSnapshot.hasCursorRegression &&
            !readyPlan.debugSnapshot.hasHistoryContinuityBroken &&
            !readyPlan.debugSnapshot.hasHistoryTokenMismatch &&
            readyPlan.debugSnapshot.replicationContinuity === "contiguous"
          const shouldTreatAsStaleColdHandshake =
            hasOnlyColdHandshakeCursorRegression &&
            previousResumeBeforeReady.requestedAfter <= 0 &&
            previousResumeBeforeReady.cursor > readyPlan.debugSnapshot.acceptedCursor &&
            typeof previousResumeBeforeReady.historyToken === "string" &&
            previousResumeBeforeReady.historyToken.trim().length > 0 &&
            readyPlan.debugSnapshot.nextHistoryToken === previousResumeBeforeReady.historyToken &&
            (
              previousReplicaProvenance?.sourceKind === "hydrated" ||
              previousReplicaProvenance?.sourceKind === "foreground_http_prefill"
            ) &&
            typeof activeRuntimeState?.lastEventId === "number" &&
            Number.isFinite(activeRuntimeState.lastEventId) &&
            activeRuntimeState.lastEventId >= previousResumeBeforeReady.cursor
          if (shouldTreatAsStaleColdHandshake) {
            entry.resume = previousResumeBeforeReady
            entry.runtime.replicaProvenance = previousReplicaProvenance
            debugWarn("sync.new", "sync.ready came from stale cold handshake after durable hydration", syncReadyLogDetails)
            this.disconnect(entry.sessionId, {
              clearRuntime: false,
              removeEntry: false,
              shouldReconnect: true,
              closeReason: "reconnect:stale_cold_handshake_after_hydration",
            })
            return
          }
          if (this.promoteVisibleRuntimeAfterCursorRegression({
            entry,
            readyPlan,
            syncReadyLogDetails,
          })) {
            return
          }
          entry.runtime.lastReset = readyPlan.debugSnapshot
          debugWarn("sync.new", "sync.ready triggered runtime reset", syncReadyLogDetails)
          // The runtime needs to be wiped due to a continuity break or history
          // token mismatch.  However the server already committed to
          // `acceptedCursor` and will only send catchup events *after* that
          // cursor.  If we reset the projection here and continue, events
          // before acceptedCursor are permanently lost (the "disappearing top
          // messages" bug).  Instead we reset the runtime, rewind the cursor
          // to 0, and force a full reconnect so the next `sync.hello` fetches
          // all events from the beginning.
          // Do not re-apply optimistic/display state here: this branch is
          // deliberately dropping stale local transcript state before forcing a
          // cold replay from the server.
          this.forceColdReplayReconnect(entry, "resync:continuity_break")
          return
        }
        if (shouldPreserveColdReplayCursorRegression) {
          if (this.promoteVisibleRuntimeAfterCursorRegression({
            entry,
            readyPlan,
            syncReadyLogDetails,
          })) {
            return
          }
          debugWarn("sync.new", "Preserved local runtime during cold replay cursor regression", syncReadyLogDetails)
        }
        debugInfo("sync.new", "sync.ready accepted local runtime", syncReadyLogDetails)
        const hasAcceptedRuntimeCursor = this.hasAcceptedRuntimeCursor({
          activeRuntimeState,
          acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
          resumeCursor: entry.resume.cursor,
        })
        const shouldTrustForegroundHttpPrefillResumeCursor =
          this.shouldTrustForegroundHttpPrefillResumeCursor({
            acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
            resumeCursor: previousResumeBeforeReady.cursor,
            resumeHistoryToken: previousResumeBeforeReady.historyToken,
            nextHistoryToken: readyPlan.debugSnapshot.nextHistoryToken,
            historyContinuity: readyPlan.debugSnapshot.historyContinuity,
            replicationContinuity: readyPlan.debugSnapshot.replicationContinuity,
            hasHistoryTokenMismatch: readyPlan.debugSnapshot.hasHistoryTokenMismatch,
            previousReplicaProvenance,
          })
        const activeRuntimeLastEventId =
          typeof activeRuntimeState?.lastEventId === "number" &&
            Number.isFinite(activeRuntimeState.lastEventId) &&
            activeRuntimeState.lastEventId > 0
            ? Math.floor(activeRuntimeState.lastEventId)
            : null
        const activeRuntimeProviderEventId =
          typeof activeRuntimeState?.snapshot?.cursorState.providerEventId === "number" &&
            Number.isFinite(activeRuntimeState.snapshot.cursorState.providerEventId) &&
            activeRuntimeState.snapshot.cursorState.providerEventId > 0
            ? Math.floor(activeRuntimeState.snapshot.cursorState.providerEventId)
            : null
        const hasConcreteRuntimeCursor =
          activeRuntimeLastEventId !== null ||
          activeRuntimeProviderEventId !== null
        const hasRuntimeCursorToValidate =
          isSyncDerivedRuntimeMaterialization(entry.runtime.materialization) &&
          activeRuntimeState?.snapshot !== null &&
          hasConcreteRuntimeCursor
        if (
          hasRuntimeCursorToValidate &&
          !hasAcceptedRuntimeCursor &&
          !shouldTrustForegroundHttpPrefillResumeCursor &&
          !shouldPreserveColdReplayCursorRegression
        ) {
          debugWarn("sync.new", "sync.ready accepted cursor outran active runtime", {
            ...syncReadyLogDetails,
            acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
            activeLastEventId: activeRuntimeLastEventId,
            activeProviderEventId: activeRuntimeProviderEventId,
          })
          this.forceColdReplayReconnect(entry, "resync:accepted_cursor_gap")
          return
        }
        if (
          hasRuntimeCursorToValidate &&
          !hasAcceptedRuntimeCursor &&
          shouldTrustForegroundHttpPrefillResumeCursor
        ) {
          debugInfo("sync.new", "sync.ready accepted cursor covered by foreground HTTP prefill resume", {
            ...syncReadyLogDetails,
            acceptedCursor: readyPlan.debugSnapshot.acceptedCursor,
            activeLastEventId: activeRuntimeLastEventId,
            activeProviderEventId: activeRuntimeProviderEventId,
            resumeCursor: entry.resume.cursor,
          })
        }
        entry.runtime.continuityValidatedConnectionVersion =
          readyPlan.continuityValidatedConnectionVersion
        if (
          readyPlan.shouldReplaceDisplayOnlyRuntime &&
          activeRuntimeState?.snapshot !== null &&
          hasConcreteRuntimeCursor
        ) {
          this.replaceDisplayOnlyRuntimeWithControlPlaneOnly(entry)
        }
        if (
          shouldCaptureAuthoritativeRuntime({
            activeThreadId: this.activeThreadId,
            sessionId: entry.sessionId,
            runtimeMaterialization: entry.runtime.materialization,
            isCatchingUp: entry.resume.isCatchingUp,
          }) &&
          hasAcceptedRuntimeCursor
        ) {
          this.captureActiveRuntimeState(entry, {
            materialization: "authoritative",
          })
          if (entry.durableReplicaEpoch !== null) {
            this.maybeSaveDurableReplicaCheckpoint({
              entry,
              expectedEpoch: entry.durableReplicaEpoch,
              sourceKind: "foreground_ws",
            })
          }
        }
        this.emit()
        return
      }
      case "sync.catchup.page": {
        if (this.queueCatchupFrame(entry, frame) === "error") {
          return
        }
        return
      }
      case "sync.catchup.complete": {
        entry.resume = planCatchupCompleteHandling({
          resume: entry.resume,
          frame,
        })
        entry.transport.reconnectAttempts = 0
        if (
          shouldCaptureAuthoritativeRuntime({
            activeThreadId: this.activeThreadId,
            sessionId: entry.sessionId,
            runtimeMaterialization: entry.runtime.materialization,
            isCatchingUp: entry.resume.isCatchingUp,
          })
        ) {
          this.captureActiveRuntimeState(entry, {
            materialization: "authoritative",
          })
          if (entry.durableReplicaEpoch !== null) {
            this.maybeSaveDurableReplicaCheckpoint({
              entry,
              expectedEpoch: entry.durableReplicaEpoch,
              sourceKind: "foreground_ws",
            })
          }
        }
        this.emit()
        return
      }
      case "sync.event": {
        if (this.isLiveSyncEventFrameBatchEligible(entry, frame.event)) {
          this.enqueueLiveSyncEventForFrameBatch(entry, frame.event)
          return
        }

        const liveFlushResult = this.flushLiveEventBuffer(entry)
        if (liveFlushResult === "error") {
          return
        }

        this.applyLiveSyncEvents(entry, [frame.event])
        return
      }
      case "sync.error": {
        const syncErrorPlan = planSyncErrorHandling({
          resume: entry.resume,
          frame,
        })
        entry.runtime.lastApplyError = { reason: syncErrorPlan.reason }
        entry.resume = syncErrorPlan.nextResume
        beginTransportDisconnect(entry.transport, {
          closeReason: syncErrorPlan.closeReason,
        })
        this.setConnectionStatus(entry, "error")
        if (syncErrorPlan.shouldReconnect) {
          this.scheduleReconnect(entry)
        }
        this.emit()
        return
      }
    }
  }

  private handleSideChannelMessage(entry: ThreadSyncEntry, message: { type: string; [key: string]: unknown }): void {
    switch (message.type) {
      case "status": {
        this.applyStatusPayload(entry, message)
        this.emit()
        return
      }
      case "thread_activity": {
        // The SessionDO broadcasts thread_activity envelopes via broadcastFull
        // on every markThreadRunning / markThreadFinished transition. This is
        // the fastest path for the active session to learn about state changes
        // (e.g., running → idle/waiting_input) without waiting for the
        // ActivityHub roundtrip. The ActivityHub delta is a reliable but
        // potentially slower fallback; duplicates are deduplicated by the
        // eventId monotonicity guard in applyThreadActivity.
        const normalized = normalizeThreadActivityUpdate({ raw: message })
        if (normalized) {
          useThreadRuntime.getState().applyThreadActivity(normalized)
        }
        return
      }
      case "pending_messages_replaced": {
        const observedAt = now()
        this.pruneExpiredRenderedLocalOutboxHandoffs(entry, observedAt)
        entry.queue.serverPendingMessages = normalizePendingQueueRecords(message.pendingMessages)
        this.rememberPendingMessageClientMessageIdsByMessageId({
          entry,
          messages: entry.queue.serverPendingMessages,
          nowMs: observedAt,
        })
        this.filterCancelledPendingMessages(entry, observedAt)
        this.filterRenderedPendingMessages(entry, observedAt)
        this.rememberRecentlyServerObservedClientMessageIds({
          entry,
          messages: entry.queue.serverPendingMessages,
          nowMs: observedAt,
        })
        entry.queue.localOutbox = reconcileLocalOutboxWithServerPending({
          localOutbox: entry.queue.localOutbox,
          serverPendingMessages: [...entry.queue.serverPendingMessages, ...entry.queue.serverPendingSessionActions],
          preserveClientMessageIds: this.buildPreservedClientMessageIds({
            entry,
            nowMs: observedAt,
          }),
        })
        this.scheduleRenderedLocalOutboxCleanup(entry)
        this.prunePendingLocalDispatches(entry)
        this.applyQueuePendingWorkSideEffects(entry)
        this.emit()
        return
      }
      case "pending_session_actions_replaced": {
        const observedAt = now()
        this.pruneExpiredRenderedLocalOutboxHandoffs(entry, observedAt)
        entry.queue.serverPendingSessionActions = normalizePendingQueueRecords(message.pendingSessionActions)
        this.rememberPendingMessageClientMessageIdsByMessageId({
          entry,
          messages: entry.queue.serverPendingSessionActions,
          nowMs: observedAt,
        })
        this.filterRenderedPendingMessages(entry, observedAt)
        this.rememberRecentlyServerObservedClientMessageIds({
          entry,
          messages: entry.queue.serverPendingSessionActions,
          nowMs: observedAt,
        })
        entry.queue.localOutbox = reconcileLocalOutboxWithServerPending({
          localOutbox: entry.queue.localOutbox,
          serverPendingMessages: [...entry.queue.serverPendingMessages, ...entry.queue.serverPendingSessionActions],
          preserveClientMessageIds: this.buildPreservedClientMessageIds({
            entry,
            nowMs: observedAt,
          }),
        })
        this.scheduleRenderedLocalOutboxCleanup(entry)
        this.prunePendingLocalDispatches(entry)
        this.applyQueuePendingWorkSideEffects(entry)
        this.emit()
        return
      }
      case "message_rendered": {
        const observedAt = now()
        const renderedClientMessageId = this.resolveRenderedClientMessageId({
          entry,
          messageId: message.id,
          clientMessageId: message.clientMessageId,
          nowMs: observedAt,
        })
        const renderedMessageId = asNumber(message.id)
        if (renderedMessageId !== null) {
          entry.queue.pendingMessageClientMessageIdsByMessageId.delete(renderedMessageId)
        }
        const nextQueueState = removeRenderedMessageFromQueueState({
          serverPendingMessages: entry.queue.serverPendingMessages,
          localOutbox: entry.queue.localOutbox,
          messageId: message.id,
          clientMessageId: renderedClientMessageId ?? message.clientMessageId,
          localHandoffExpiresAt: observedAt + RENDERED_LOCAL_OUTBOX_HANDOFF_TTL_MS,
        })
        entry.queue.serverPendingMessages = nextQueueState.serverPendingMessages
        entry.queue.localOutbox = nextQueueState.localOutbox
        if (nextQueueState.removedClientMessageId) {
          entry.queue.pendingLocalDispatches.delete(nextQueueState.removedClientMessageId)
          entry.queue.recentlyServerObservedClientMessageIds.set(
            nextQueueState.removedClientMessageId,
            observedAt + RENDERED_LOCAL_OUTBOX_HANDOFF_TTL_MS,
          )
          entry.queue.recentlyRenderedClientMessageIds.set(
            nextQueueState.removedClientMessageId,
            observedAt + RENDERED_LOCAL_OUTBOX_HANDOFF_TTL_MS,
          )
        }
        this.scheduleRenderedLocalOutboxCleanup(entry)
        this.prunePendingLocalDispatches(entry)
        this.applyQueuePendingWorkSideEffects(entry)
        this.emit()
        return
      }
      case "message_rejected": {
        const rejectedClientMessageId = asString(message.clientMessageId)
        const rejectionUpdate = applyRejectedMessageToQueueState({
          serverPendingMessages: entry.queue.serverPendingMessages,
          localOutbox: entry.queue.localOutbox,
          message,
          nowMs: now(),
        })
        entry.queue.serverPendingMessages = rejectionUpdate.serverPendingMessages
        entry.queue.localOutbox = rejectionUpdate.localOutbox
        if (rejectedClientMessageId) {
          entry.queue.recentlyServerObservedClientMessageIds.delete(rejectedClientMessageId)
        }
        this.prunePendingLocalDispatches(entry)
        entry.queue.lastMessageRejection = rejectionUpdate.rejection
        this.applyQueuePendingWorkSideEffects(entry)
        this.emit()
        return
      }
      case "session_action_outcome": {
        const notice = normalizeSessionActionNotice(message, now())
        entry.effects.sessionActionNotice = notice
        if (this.activeThreadId === entry.sessionId) {
          patchActiveRuntimeSnapshot({
            sessionActionOutcome: normalizeRuntimeActionOutcome(message),
          })
          this.captureActiveRuntimeState(entry)
        }
        this.emit()
        return
      }
      case "session_action": {
        if (message.action === "edit_message") {
          const activeEditOperation = normalizeActiveEditOperation(message)
          if (!activeEditOperation) return
          const terminalEditOperation =
            activeEditOperation.state === "completed" || activeEditOperation.state === "failed"
          const currentEditOperation = entry.controlPlane.activeEditMessageOperation
          const currentOperationId = currentEditOperation?.operationId ?? null
          const incomingOperationId = activeEditOperation.operationId ?? null
          if (
            terminalEditOperation &&
            currentOperationId !== null &&
            incomingOperationId !== currentOperationId
          ) {
            return
          }
          entry.controlPlane = {
            ...entry.controlPlane,
            activeEditMessageOperation: activeEditOperation,
          }
          if (this.activeThreadId === entry.sessionId) {
            patchActiveRuntimeSnapshot({
              activeEditOperation,
            })
            this.captureActiveRuntimeState(entry)
          }
          this.emit()
          return
        }
        return
      }
      case "queued_session_command_outcome": {
        const notice = normalizeQueuedSessionCommandNotice(message, now())
        entry.effects.queuedSessionCommandNotice = notice
        if (notice?.outcome === "rejected") {
          entry.queue.serverPendingSessionActions = entry.queue.serverPendingSessionActions.filter(
            (item) => item.clientActionId !== notice.clientActionId,
          )
          this.applyQueuePendingWorkSideEffects(entry)
        }
        if (notice?.outcome === "submit_uncertain") {
          entry.queue.serverPendingSessionActions = entry.queue.serverPendingSessionActions.map((item) =>
            item.clientActionId === notice.clientActionId
              ? { ...item, status: "submit_uncertain" }
              : item,
          )
        }
        this.emit()
        return
      }
      case "code_server_status": {
        const nextCodeServer = normalizeCodeServerStatusSideChannel({
          status: message.status,
          error: message.error,
          vscodeUrl: message.vscodeUrl,
        })
        entry.effects.codeServerStatus = nextCodeServer.codeServerStatus
        entry.effects.codeServerError = nextCodeServer.codeServerError
        entry.controlPlane.vscodeUrl = nextCodeServer.vscodeUrl
        this.emit()
        return
      }
      case "sandbox_progress": {
        const progress = normalizeSandboxProgressMessage({
          step: message.step,
          message: message.message,
          timestamp: message.timestamp,
          nowMs: now(),
        })
        if (!progress) return
        entry.controlPlane.sandboxProgress = appendSandboxProgressEntries({
          current: entry.controlPlane.sandboxProgress,
          next: progress,
        })
        if (progress.step === "creating") {
          entry.controlPlane.bootSource = null
          const normalizedMessage = progress.message.toLowerCase()
          entry.controlPlane.sandboxStartupKind = normalizedMessage.includes("restor")
            ? "restore"
            : normalizedMessage.includes("resum")
            ? "resume"
            : normalizedMessage.includes("recover") ||
                normalizedMessage.includes("reconnect") ||
                normalizedMessage.includes("reattach") ||
                normalizedMessage.includes("existing sandbox") ||
                normalizedMessage.includes("retry")
            ? "recover"
            : "start"
        } else if (progress.step === "ready") {
          entry.controlPlane.sandboxStartupKind = null
        }
        this.emit()
        return
      }
      case "sandbox_ready": {
        // The sandbox_ready message carries the boot source that tells the
        // frontend whether this sandbox started from a prebuilt image or a
        // bare base image. Capture it so the UI can show contextual banners.
        const bootSource = asString(message.bootSource)
        if (bootSource) {
          entry.controlPlane.bootSource = bootSource
        }
        entry.controlPlane.sandboxStartupKind = null
        this.emit()
        return
      }
      case "pr_flow_snapshot": {
        applyPrFlowSnapshotSideEffects({
          sessionId: entry.sessionId,
          rawSnapshot: message.snapshot,
        })
        return
      }
      case "pr_flow_event": {
        const appliedSnapshot = applyPrFlowSnapshotSideEffects({
          sessionId: entry.sessionId,
          rawSnapshot: message.snapshot,
        })
        if (!appliedSnapshot) return
        applyQuickPrCompletionSideEffects({
          sessionId: entry.sessionId,
          eventName: message.event,
          operationKind: asRecord(message.operation)?.kind,
        })
        return
      }
      case "yolo_flow_progress": {
        const nextYoloFlow = computeYoloFlowUpdate({
          currentUpdatedAt: entry.controlPlane.yoloFlowUpdatedAt,
          currentStep: entry.controlPlane.yoloFlowStep,
          currentError: entry.controlPlane.yoloFlowError,
          step: message.step,
          error: message.error,
          timestamp: message.timestamp,
          mergeFailureKind: message.mergeFailureKind,
        })
        if (!nextYoloFlow.applied) return
        entry.controlPlane.yoloFlowStep = nextYoloFlow.nextStep
        entry.controlPlane.yoloFlowError = nextYoloFlow.nextError
        entry.controlPlane.yoloFlowUpdatedAt = nextYoloFlow.nextUpdatedAt
        applyYoloFlowSideEffects({
          sessionId: entry.sessionId,
          step: nextYoloFlow.nextStep,
          error: nextYoloFlow.nextError,
          timestamp: nextYoloFlow.nextUpdatedAt,
          mergeFailureKind: nextYoloFlow.mergeFailureKind,
        })
        this.emit()
        return
      }
      case "session_suspended": {
        this.applySessionLifecycleStateUpdate({
          sessionId: entry.sessionId,
          lifecycleStatus: "suspended",
          status: "suspended",
          timestamp: asNumber(message.timestamp),
          suspended: true,
        })
        this.emit()
        return
      }
      case "session_resumed": {
        this.applySessionLifecycleStateUpdate({
          sessionId: entry.sessionId,
          lifecycleStatus: "active",
          status: "idle",
          timestamp: asNumber(message.timestamp),
          suspended: false,
        })
        this.emit()
        return
      }
      case "session_archived": {
        const archiveUpdate = this.applySessionArchiveStateUpdate({
          sessionId: entry.sessionId,
          archivedAt: asString(message.archivedAt),
          timestamp: asNumber(message.timestamp),
        })
        if (archiveUpdate.accepted && archiveUpdate.archivedAt !== null) {
          this.completeYoloFlowFromArchiveEvent({
            entry,
            timestamp: archiveUpdate.timestamp,
          })
        }
        this.emit()
        return
      }
      case "session_unarchived": {
        this.applySessionArchiveStateUpdate({
          sessionId: entry.sessionId,
          archivedAt: null,
          timestamp: asNumber(message.timestamp),
        })
        this.emit()
        return
      }
      case "pr_status": {
        applyPrStatusSideEffects({
          sessionId: entry.sessionId,
          messageData: message.data,
        })
        return
      }
      case "merge_pr_result": {
        applyMergePrResultSideEffects({
          sessionId: entry.sessionId,
          message,
          nowMs: now(),
        })
        return
      }
      case "resolve_conflict_started": {
        applyResolveConflictSideChannelMessage({
          sessionId: entry.sessionId,
          state: "started",
          message,
          nowMs: now(),
        })
        return
      }
      case "resolve_conflict_dispatched": {
        applyResolveConflictSideChannelMessage({
          sessionId: entry.sessionId,
          state: "dispatched",
          message,
          nowMs: now(),
        })
        return
      }
      case "resolve_conflict_failed": {
        const failedRequestId = asString(message.requestId)
        const failedClientMessageId = failedRequestId
          ? buildResolveMergeConflictClientMessageId({ requestId: failedRequestId })
          : null
        const removedLocalOptimisticMessage = failedClientMessageId
          ? this.removeLocalOutboxEntryForClientMessageId(entry, failedClientMessageId)
          : false
        applyResolveConflictSideChannelMessage({
          sessionId: entry.sessionId,
          state: "failed",
          message,
          nowMs: now(),
        })
        if (removedLocalOptimisticMessage) {
          this.applyQueuePendingWorkSideEffects(entry)
          this.emit()
        }
        return
      }
      case "file_tree": {
        applyFileTreeSideEffects({
          sessionId: entry.sessionId,
          data: message.data,
        })
        return
      }
      case "file_changes": {
        applyFileChangesSideEffects({
          sessionId: entry.sessionId,
          data: message.data,
        })
        return
      }
      case "git_result": {
        applyGitResultSideEffects({
          sessionId: entry.sessionId,
          message,
        })
        return
      }
      case "session_branch_updated": {
        const branch = asString(message.branch)
        const baseBranch = asString(message.baseBranch)
        if (branch) {
          entry.sessionBranch = branch
        }
        if (baseBranch !== null) {
          entry.baseBranch = baseBranch
        }
        entry.controlPlane.branchSwitchInProgress = false
        applySessionBranchUpdatedSideEffects({
          sessionId: entry.sessionId,
          message,
        })
        this.emit()
        return
      }
      case "start_new_work_git_completed": {
        const ok = message.ok === true
        const branch = asString(message.newBranch)
        const baseBranch = asString(message.baseBranch)

        entry.controlPlane.startNewWorkGitPending = false
        entry.controlPlane.branchSwitchInProgress = false

        if (ok && branch) {
          entry.sessionBranch = branch
        }
        if (ok && baseBranch !== null) {
          entry.baseBranch = baseBranch
        }

        applyStartNewWorkGitCompletedSideEffects({
          sessionId: entry.sessionId,
          message,
        })

        if (ok && branch) {
          applySessionBranchUpdatedSideEffects({
            sessionId: entry.sessionId,
            message: {
              type: "session_branch_updated",
              branch,
              baseBranch,
              clearBranchSwitchInProgress: true,
            },
          })
        }

        this.emit()
        return
      }
      case "session_title_updated": {
        const normalizedTitleUpdate = normalizeSessionTitleUpdate({
          raw: {
            sessionId: entry.sessionId,
            title: message.title,
            timestamp: message.timestamp,
          },
        })
        if (!normalizedTitleUpdate) {
          return
        }
        this.applySessionTitleUpdate(normalizedTitleUpdate)
        this.emit()
        return
      }
      case "runtime_conversations_replaced": {
        if (!this.queryClient || !Array.isArray(message.runtimeConversations)) {
          return
        }
        const runtimeConversations = message.runtimeConversations.filter((item): item is Record<string, unknown> => {
          return Boolean(
            item &&
            typeof item === "object" &&
            typeof (item as Record<string, unknown>).conversationId === "string",
          )
        })
        const runtimeConversationsIncludingArchived = Array.isArray(message.runtimeConversationsIncludingArchived)
          ? message.runtimeConversationsIncludingArchived.filter((item): item is Record<string, unknown> => {
            return Boolean(
              item &&
              typeof item === "object" &&
              typeof (item as Record<string, unknown>).conversationId === "string",
            )
          })
          : null
        this.queryClient.setQueryData(["runtime-conversations", entry.sessionId], runtimeConversations)
        if (runtimeConversationsIncludingArchived) {
          this.queryClient.setQueryData(
            ["runtime-conversations", entry.sessionId, "include-archived"],
            runtimeConversationsIncludingArchived,
          )
        } else {
          void this.queryClient.invalidateQueries({
            queryKey: ["runtime-conversations", entry.sessionId, "include-archived"],
          })
        }
        this.emit()
        return
      }
      case "error": {
        const messageConversationId = asString(message.conversationId)
        if (
          messageConversationId !== null &&
          normalizeRuntimeConversationId(messageConversationId) !== normalizeRuntimeConversationId(entry.activeConversationId)
        ) {
          return
        }
        recordTransportError({
          transport: entry.transport,
          message: asString(message.message) ?? "Session websocket error",
          nowMs: now(),
        })
        this.emit()
        return
      }
      default:
        return
    }
  }

  private applyStatusPayload(entry: ThreadSyncEntry, payload: unknown): void {
    const statusConversationId = getStatusPayloadConversationId(payload)
    const activeConversationId = normalizeRuntimeConversationId(entry.activeConversationId)
    if (statusConversationId && statusConversationId !== activeConversationId) {
      debugInfo("sync.new", "Ignored status snapshot for inactive conversation", {
        sessionId: entry.sessionId,
        statusConversationId,
        activeConversationId,
      })
      return
    }

    const currentSnapshot = this.activeThreadId === entry.sessionId ? useAgentRuntime.getState().snapshot : null
    const observedAt = now()
    this.pruneExpiredRenderedLocalOutboxHandoffs(entry, observedAt)
    const preservedClientMessageIds = this.buildPreservedClientMessageIds({
      entry,
      nowMs: observedAt,
    })
    const statusApplication = applyStatusPayloadToThreadState({
      sessionId: entry.sessionId,
      payload,
      controlPlane: entry.controlPlane,
      effects: entry.effects,
      queue: entry.queue,
      preserveClientMessageIds: preservedClientMessageIds,
      existingInteractions: currentSnapshot?.pendingInteractions,
      nowMs: observedAt,
    })

    if (statusApplication.threadStatusSnapshotAccepted) {
      this.rememberPendingMessageClientMessageIdsByMessageId({
        entry,
        messages: [
          ...entry.queue.serverPendingMessages,
          ...entry.queue.serverPendingSessionActions,
        ],
        nowMs: observedAt,
      })
      this.filterRenderedPendingMessages(entry, observedAt)
      this.rememberRecentlyServerObservedClientMessageIds({
        entry,
        messages: [
          ...entry.queue.serverPendingMessages,
          ...entry.queue.serverPendingSessionActions,
        ],
        nowMs: observedAt,
      })
    }

    this.filterCancelledPendingMessages(entry, observedAt)
    this.scheduleRenderedLocalOutboxCleanup(entry)
    this.prunePendingLocalDispatches(entry)
    this.applyQueuePendingWorkSideEffects(entry)

    entry.controlPlane = statusApplication.controlPlane

    const statusPayload = asRecord(payload)
    const statusTitle = asString(statusPayload?.title)
    const statusTitleUpdatedAt =
      typeof statusPayload?.titleUpdatedAt === "number" && Number.isFinite(statusPayload.titleUpdatedAt)
        ? Math.max(0, Math.floor(statusPayload.titleUpdatedAt))
        : null
    if (statusTitle && statusTitleUpdatedAt !== null) {
      this.applySessionTitleUpdate({
        sessionId: entry.sessionId,
        title: statusTitle,
        timestamp: statusTitleUpdatedAt,
      })
    }
    if (statusApplication.controlPlaneSnapshotAccepted) {
      const currentBranch = asString(statusPayload?.branch)
      const currentBaseBranch = asString(statusPayload?.baseBranch)
      const branchChanged = currentBranch !== null && currentBranch !== entry.sessionBranch
      const baseBranchChanged = currentBaseBranch !== null && currentBaseBranch !== entry.baseBranch
      if (currentBranch && (branchChanged || baseBranchChanged)) {
        entry.sessionBranch = currentBranch
        if (currentBaseBranch !== null) {
          entry.baseBranch = currentBaseBranch
        }
        applySessionBranchUpdatedSideEffects({
          sessionId: entry.sessionId,
          message: {
            type: "session_branch_updated",
            branch: currentBranch,
            baseBranch: currentBaseBranch,
            clearBranchSwitchInProgress: entry.controlPlane.branchSwitchInProgress !== true,
          },
        })
      }
    }

    if (this.activeThreadId === entry.sessionId && statusApplication.knownAgentType) {
      const runtimeSnapshot = entry.controlPlane.runtimeSnapshot
      const hasExistingActiveRuntimeSnapshot = currentSnapshot !== null
      const runtimeIsSyncDerived =
        entry.runtime.materialization === "authoritative" ||
        entry.runtime.materialization === "authoritative_partial"
      const canPatchExistingRuntimeBeforeSyncReady =
        runtimeSnapshot !== null &&
        !entry.resume.syncReadyReceived &&
        hasExistingActiveRuntimeSnapshot
      const canApplyControlPlaneSnapshot =
        canPatchExistingRuntimeBeforeSyncReady ||
        (
          runtimeSnapshot !== null &&
          entry.resume.syncReadyReceived &&
          (
            (entry.resume.isCatchingUp === false) ||
            runtimeIsSyncDerived
          )
        )
      if (!canApplyControlPlaneSnapshot || !runtimeSnapshot) {
        this.syncHealth(entry)
        return
      }
      applyControlPlaneSnapshotToActiveRuntime({
        agentType: statusApplication.knownAgentType,
        pendingInteractions: runtimeSnapshot.pendingInteractions,
        status: runtimeSnapshot.status,
        conversationEpoch: entry.controlPlane.conversationEpoch,
        activeEditOperation: entry.controlPlane.activeEditMessageOperation,
      })
      this.captureActiveRuntimeState(entry)
      // A `thread_activity` update often arrives right after the final
      // `sync.event` of a turn closes (finish events + status transitions). The
      // sync.event-driven checkpoint save path deliberately skips when
      // `cachedState.lastEventId` disagrees with `entry.resume.cursor` (see
      // `maybeSaveDurableReplicaCheckpoint`), so without an explicit save here
      // the checkpoint would stay at whatever cursor the last milestone
      // captured. Capturing (above) then saving piggybacks on the fresh
      // cachedState that `captureActiveRuntimeState` just produced.
      if (entry.durableReplicaEpoch !== null) {
        this.maybeSaveDurableReplicaCheckpoint({
          entry,
          expectedEpoch: entry.durableReplicaEpoch,
          sourceKind: "foreground_ws",
        })
      }
    }

    this.syncHealth(entry)
  }

  private applySessionTitleUpdate(update: {
    sessionId: string
    title: string
    timestamp: number
  }): void {
    if (!this.queryClient) {
      return
    }

    applyAuthoritativeSessionTitleUpdate({
      queryClient: this.queryClient,
      update,
    })
  }

  private applySessionArchiveStateUpdate(args: {
    sessionId: string
    archivedAt: string | null
    timestamp: number | null
  }): {
    accepted: boolean
    archivedAt: string | null
    timestamp: number
  } {
    if (!this.queryClient) {
      return {
        accepted: false,
        archivedAt: args.archivedAt,
        timestamp: now(),
      }
    }

    return applyAuthoritativeSessionArchiveStateUpdate({
      queryClient: this.queryClient,
      sessionId: args.sessionId,
      archivedAt: args.archivedAt,
      timestamp: args.timestamp,
    })
  }

  private applySessionLifecycleStateUpdate(args: {
    sessionId: string
    lifecycleStatus: string
    status: string
    timestamp: number | null
    suspended: boolean
  }): {
    accepted: boolean
    timestamp: number
  } {
    const nextTimestamp =
      typeof args.timestamp === "number" && Number.isFinite(args.timestamp) && args.timestamp > 0
        ? Math.floor(args.timestamp)
        : now()

    useThreadRuntime.getState().applyThreadStatus({
      sessionId: args.sessionId,
      persistedStatus: args.status,
      lifecycleStatus: args.lifecycleStatus,
      observedAt: nextTimestamp,
    })
    useThreadRuntime.getState().applyThreadRecoveryStatus({
      sessionId: args.sessionId,
      recoveryStatus: null,
      observedAt: nextTimestamp,
    })

    if (!this.queryClient) {
      return {
        accepted: false,
        timestamp: nextTimestamp,
      }
    }

    const readLifecycleTimestamp = (row: unknown): number => {
      if (!row || typeof row !== "object") return 0
      const value = (row as Record<string, unknown>).sessionRuntimeStatusUpdatedAt
      return typeof value === "number" && Number.isFinite(value) && value > 0
        ? Math.floor(value)
        : 0
    }
    const readRecoveryFreshnessTimestamp = (row: unknown): number => {
      if (!row || typeof row !== "object") return 0
      const record = row as Record<string, unknown>
      const recoveryObservedAt = record.recoveryStatusObservedAt
      if (typeof recoveryObservedAt === "number" && Number.isFinite(recoveryObservedAt) && recoveryObservedAt > 0) {
        return Math.floor(recoveryObservedAt)
      }

      return deriveRuntimeLifecycleAvailabilityObservedAt({
        runtimeLifecycle: normalizeConnectionFlowRuntimeLifecycleDebugSnapshot(record.runtimeLifecycle),
      }) ?? 0
    }
    const scrubRuntimeLifecycleAvailabilityState = (value: unknown): unknown => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return value
      return {
        ...(value as Record<string, unknown>),
        degradedAttach: null,
        observation: null,
        recoveryPhase: null,
        recoveryStartedAt: null,
        recoveryBlockReason: null,
        recoveryDecision: null,
        sseReconnect: null,
      }
    }
    // Session suspension/resume side-channel events can arrive before an
    // authoritative session refetch. Keep the cached `suspendedAt` field aligned
    // with the lifecycle update so consumers that depend on recency (notably the
    // 5s speculative auto-resume dwell timer) do not get stuck with a suspended
    // lifecycle and a stale/null suspension timestamp.
    const suspendedAtPatch = args.suspended
      ? { suspendedAt: new Date(nextTimestamp).toISOString() }
      : { suspendedAt: null }
    const applyRecoveryClearToSessionRow = (old: unknown): unknown => {
      if (!old || typeof old !== "object") return old
      const row = old as Record<string, unknown>
      if (readRecoveryFreshnessTimestamp(row) > nextTimestamp) return old

      return {
        ...row,
        recoveryStatus: null,
        recoveryStatusObservedAt: nextTimestamp,
        ...(Object.prototype.hasOwnProperty.call(row, "runtimeLifecycle")
          ? { runtimeLifecycle: scrubRuntimeLifecycleAvailabilityState(row.runtimeLifecycle) }
          : {}),
      }
    }

    const currentDetailTimestamp = readLifecycleTimestamp(
      this.queryClient.getQueryData(["session", args.sessionId]),
    )
    if (nextTimestamp < currentDetailTimestamp) {
      return {
        accepted: false,
        timestamp: nextTimestamp,
      }
    }

    this.queryClient.setQueryData(["session", args.sessionId], (old: unknown) => {
      if (!old || typeof old !== "object") {
        return old
      }

      return applyRecoveryClearToSessionRow({
        ...(old as Record<string, unknown>),
        lifecycleStatus: args.lifecycleStatus,
        status: args.status,
        ...suspendedAtPatch,
        sessionRuntimeStatusUpdatedAt: nextTimestamp,
      })
    })

    this.queryClient.setQueriesData({ queryKey: ["sessions"] }, (old: unknown) => {
      if (!Array.isArray(old)) return old

      let changed = false
      const next = old.map((item) => {
        if (!item || typeof item !== "object") return item
        const row = item as Record<string, unknown>
        if (row.id !== args.sessionId) return item
        const rowTimestamp = readLifecycleTimestamp(row)
        if (nextTimestamp < rowTimestamp) return item
        changed = true
        return applyRecoveryClearToSessionRow({
          ...row,
          lifecycleStatus: args.lifecycleStatus,
          status: args.status,
          ...suspendedAtPatch,
          sessionRuntimeStatusUpdatedAt: nextTimestamp,
        })
      })

      return changed ? next : old
    })

    return {
      accepted: true,
      timestamp: nextTimestamp,
    }
  }

  private completeYoloFlowFromArchiveEvent(args: {
    entry: ThreadSyncEntry
    timestamp: number
  }): void {
    if (args.entry.controlPlane.yoloFlowStep !== "archiving") {
      return
    }

    /**
     * `session_archived` is emitted before the direct thread socket is torn down.
     * In the YOLO path that archive event is the authoritative completion of the
     * final step, so if the subsequent `yolo_flow_progress: done` frame is lost
     * during detach we must still converge both the thread page and sidebar away
     * from the spinner.
     */
    const nextYoloFlow = computeYoloFlowUpdate({
      currentUpdatedAt: args.entry.controlPlane.yoloFlowUpdatedAt,
      currentStep: args.entry.controlPlane.yoloFlowStep,
      currentError: args.entry.controlPlane.yoloFlowError,
      step: "done",
      error: null,
      timestamp: args.timestamp,
    })
    if (!nextYoloFlow.applied) {
      return
    }

    args.entry.controlPlane.yoloFlowStep = nextYoloFlow.nextStep
    args.entry.controlPlane.yoloFlowError = nextYoloFlow.nextError
    args.entry.controlPlane.yoloFlowUpdatedAt = nextYoloFlow.nextUpdatedAt
    applyYoloFlowSideEffects({
      sessionId: args.entry.sessionId,
      step: nextYoloFlow.nextStep,
      timestamp: nextYoloFlow.nextUpdatedAt,
    })
  }

  private applyOptimisticRuntimeSeed(entry: ThreadSyncEntry): void {
    applyOptimisticRuntimeSeedToActiveStore({
      activeThreadId: this.activeThreadId,
      sessionId: entry.sessionId,
      runtime: entry.runtime,
      connectionVersion: entry.transport.connectionVersion,
      resume: entry.resume,
      nowMs: now(),
    })
  }

  private restartKeepaliveTimer(entry: ThreadSyncEntry): void {
    clearKeepaliveTimer(entry.transport)
    entry.transport.keepaliveTimer = setInterval(() => {
      if (!entry.transport.ws || entry.transport.ws.readyState !== WebSocket.OPEN) return
      try {
        entry.transport.ws.send(JSON.stringify({ type: entry.transport.keepaliveFrameType }))
      } catch {
        // ignore and let onclose handle reconnect
      }
    }, KEEPALIVE_INTERVAL_MS)
  }

  private scheduleReconnect(entry: ThreadSyncEntry): void {
    if (this.activeThreadId !== entry.sessionId || entry.transport.connectEnabled !== true) {
      return
    }
    if (entry.transport.reconnectTimer) return

    const delayMs = computeReconnectDelayMs({
      reconnectAttempts: entry.transport.reconnectAttempts,
      maxDelayMs: RECONNECT_MAX_DELAY_MS,
    })
    entry.transport.reconnectTimer = setTimeout(() => {
      entry.transport.reconnectTimer = null
      incrementReconnectAttempts(entry.transport)
      this.openSocket(entry)
    }, delayMs)
  }

  private setConnectionStatus(
    entry: ThreadSyncEntry,
    status: WsStatus,
    options?: {
      suppressDegradedTracking?: boolean
    },
  ): void {
    setTransportStatus({
      transport: entry.transport,
      status,
      nowMs: now(),
      suppressDegradedTracking: options?.suppressDegradedTracking,
    })
    this.syncHealth(entry)
  }

  private syncHealth(entry: ThreadSyncEntry): void {
    upsertSessionSyncHealthEntry(entry.sessionId, {
      wsStatus: entry.transport.status,
      transportMode: "websocket",
      wsStatusChangedAt: entry.transport.wsStatusChangedAt,
      degradedSinceAt: entry.transport.wsDegradedSinceAt,
    })
  }

  private isCurrentConnection(entry: ThreadSyncEntry, ws: WebSocket, version: number): boolean {
    return isCurrentTransportConnection(entry.transport, ws, version)
  }

  private getCachedActiveReplayWorkerEligibility(args: {
    sessionId: string
  }): ReturnType<typeof evaluateThreadSyncReplayWorkerEligibility> {
    const store = useAgentRuntime.getState()
    const stateVersion = store.stateVersion
    const cached = this.activeReplayWorkerEligibilityCache

    if (
      cached !== null
      && cached.sessionId === args.sessionId
      && cached.stateVersion === stateVersion
    ) {
      return cached.eligibility
    }

    const eligibility = evaluateThreadSyncReplayWorkerEligibility({
      currentState: buildActiveRuntimeComputationState({
        store,
      }),
      chunkEventCount: 0,
    })

    this.activeReplayWorkerEligibilityCache = {
      sessionId: args.sessionId,
      stateVersion,
      eligibility,
    }

    return eligibility
  }

  private clearDeferredEmitSchedule(): void {
    if (this.deferredEmitAnimationFrameId !== null) {
      if (
        typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.deferredEmitAnimationFrameId)
      }
      this.deferredEmitAnimationFrameId = null
    }

    if (this.deferredEmitTimer !== null) {
      clearTimeout(this.deferredEmitTimer)
      this.deferredEmitTimer = null
    }
  }

  private resetDeferredEmitInvalidationScope(): void {
    this.deferredEmitInvalidationScope = null
  }

  private recordDeferredEmitInvalidationScope(scope: ThreadSyncViewStateInvalidationScope): void {
    if (scope.type === "all") {
      this.deferredEmitInvalidationScope = scope
      return
    }

    if (this.deferredEmitInvalidationScope?.type === "all") {
      return
    }

    if (
      this.deferredEmitInvalidationScope === null
      || this.deferredEmitInvalidationScope.type !== "sessions"
    ) {
      this.deferredEmitInvalidationScope = {
        type: "sessions",
        sessionIds: new Set(scope.sessionIds),
        clearEmptyViewState: scope.clearEmptyViewState,
      }
      return
    }

    for (const sessionId of scope.sessionIds) {
      this.deferredEmitInvalidationScope.sessionIds.add(sessionId)
    }
    this.deferredEmitInvalidationScope.clearEmptyViewState =
      this.deferredEmitInvalidationScope.clearEmptyViewState || scope.clearEmptyViewState
  }

  private invalidateViewStateCache(scope: ThreadSyncViewStateInvalidationScope): void {
    if (scope.type === "all") {
      this.viewStateCache.clear()
      this.emptyViewState = null
      return
    }

    for (const sessionId of scope.sessionIds) {
      this.viewStateCache.delete(sessionId)
    }

    if (scope.clearEmptyViewState) {
      this.emptyViewState = null
    }
  }

  private notifyListeners(scope: ThreadSyncViewStateInvalidationScope): void {
    this.invalidateViewStateCache(scope)
    for (const listener of this.listeners) {
      listener()
    }
  }

  private scheduleDeferredEmit(): void {
    if (
      this.deferredEmitAnimationFrameId !== null
      || this.deferredEmitTimer !== null
    ) {
      return
    }

    const flushDeferredEmit = () => {
      const scope = this.deferredEmitInvalidationScope ?? { type: "all" as const }
      this.deferredEmitAnimationFrameId = null
      this.deferredEmitTimer = null
      this.resetDeferredEmitInvalidationScope()
      this.notifyListeners(scope)
    }

    if (
      typeof window !== "undefined"
      && typeof window.requestAnimationFrame === "function"
      && typeof document !== "undefined"
      && document.visibilityState === "visible"
    ) {
      this.deferredEmitAnimationFrameId = window.requestAnimationFrame(() => {
        flushDeferredEmit()
      })
      return
    }

    this.deferredEmitTimer = setTimeout(
      flushDeferredEmit,
      THREAD_SYNC_DEFERRED_EMIT_FALLBACK_DELAY_MS,
    )
  }

  private emitDeferred(): void {
    this.recordDeferredEmitInvalidationScope({ type: "all" })
    this.scheduleDeferredEmit()
  }

  private emitSessionDeferred(sessionId: string): void {
    this.recordDeferredEmitInvalidationScope({
      type: "sessions",
      sessionIds: new Set([sessionId]),
      clearEmptyViewState: false,
    })
    this.scheduleDeferredEmit()
  }

  private emitSessionNow(sessionId: string): void {
    this.notifyListeners({
      type: "sessions",
      sessionIds: new Set([sessionId]),
      clearEmptyViewState: false,
    })
  }

  private emit(): void {
    this.clearDeferredEmitSchedule()
    this.resetDeferredEmitInvalidationScope()
    this.notifyListeners({ type: "all" })
  }
}

let threadSyncManagerSingleton: ThreadSyncManager | null = null
let threadSyncManagerInstanceCounter = 0

export function getThreadSyncManager(): ThreadSyncManager {
  if (!threadSyncManagerSingleton) {
    threadSyncManagerInstanceCounter += 1
    threadSyncManagerSingleton = new ThreadSyncManager(
      threadSyncManagerInstanceCounter,
    )
    let navigationType: string | null = null
    if (
      typeof window !== "undefined" &&
      typeof window.performance?.getEntriesByType === "function"
    ) {
      const navigationEntry = window.performance
        .getEntriesByType("navigation")
        .at(0) as { type?: unknown } | undefined
      navigationType = typeof navigationEntry?.type === "string"
        ? navigationEntry.type
        : null
    }
    const reloadBreadcrumb = readAndClearBrowserRuntimeReloadBreadcrumb()
    debugInfo("sync.new", "Thread sync manager initialized", {
      managerInstanceId: threadSyncManagerInstanceCounter,
      reloadCause: reloadBreadcrumb?.cause ?? null,
      reloadPath: reloadBreadcrumb?.path ?? null,
      reloadHref: reloadBreadcrumb?.href ?? null,
      reloadRecordedAtMs: reloadBreadcrumb?.recordedAtMs ?? null,
      navigationType,
    })
  }
  return threadSyncManagerSingleton
}

export function clearThreadSyncSessionState(sessionId: string): Promise<void> {
  return getThreadSyncManager().clearSessionState(sessionId)
}

export function destroyThreadSyncManager(): void {
  if (!threadSyncManagerSingleton) return
  threadSyncManagerSingleton.destroy()
  threadSyncManagerSingleton = null
}
